<template>
  <div class="scholar-page flex-col">
    <div class="scholar-shell flex-col">
      <ScholarHeader />

      <div class="scholar-main workspace-side-rail-content-inset" :class="{ 'is-search-idle': !hasSearchIntent }">
        <div v-if="hasSearchIntent" class="scholar-hero">
          <div>
            <p>聚合开放获取文献元数据，快速查看 DOI、作者、机构、引用、主题和 PDF 链接。</p>
          </div>
          <div class="scholar-hero-actions">
            <el-button class="scholar-rag-import-btn" data-track-text="文献检索导入RAG" aria-label="文献检索导入RAG" :loading="ragImportLoading" @click="openRagImportDialog()">
              <i class="el-icon-upload"></i>
              导入 RAG
            </el-button>
            <el-button class="scholar-saved-btn" data-track-text="打开我的文献收藏" aria-label="打开我的文献收藏" @click="openSavedDialog">
              <i class="el-icon-lishijilu"></i>
              我的收藏
            </el-button>
          </div>
        </div>

        <div v-if="!hasSearchIntent" class="scholar-search-intro">
          <i class="el-icon-search" aria-hidden="true"></i>
          <h1>聚合数据库检索</h1>
          <p>输入论文标题、关键词或 DOI，开始检索开放获取文献</p>
        </div>

        <div class="scholar-search-card" :class="{ 'is-initial': !hasSearchIntent }">
          <el-select
            v-if="hasSearchIntent"
            v-model="searchMode"
            class="scholar-search-mode"
            size="small"
            popper-class="scholar-search-mode-popper"
            :title="searchModeTitle"
            @change="handleSearchModeChange"
          >
            <el-option
              v-for="item in searchModeOptions"
              :key="item.value"
              :label="item.shortLabel || item.label"
              :value="item.value"
            >
              <div class="scholar-search-mode-option">
                <strong>{{ item.label }}</strong>
                <span>{{ item.description }}</span>
                <em>{{ item.apiParam }}</em>
              </div>
            </el-option>
          </el-select>
          <el-autocomplete
            v-model="keyword"
            class="scholar-search-input"
            clearable
            :fetch-suggestions="querySearchSuggestions"
            :trigger-on-focus="true"
            popper-class="scholar-suggest-popper"
            :placeholder="searchPlaceholder"
            @select="handleSuggestionSelect"
            @keyup.enter.native="submitSearch"
          >
            <i slot="prefix" class="el-icon-search"></i>
            <template slot-scope="{ item }">
              <div class="scholar-suggest-title">
                <span>{{ item.value }}</span>
                <em v-if="item.typeLabel">{{ item.typeLabel }}</em>
              </div>
              <div v-if="item.hint" class="scholar-suggest-hint">{{ item.hint }}</div>
            </template>
          </el-autocomplete>
          <el-button class="scholar-search-btn" type="primary" data-track-text="提交文献检索" aria-label="提交文献检索" :loading="loading" @click="submitSearch">
            检索
          </el-button>
          <button v-if="hasSearchIntent" type="button" class="scholar-filter-btn" data-track-text="展开文献筛选" aria-label="展开文献筛选" @click="filtersOpen = !filtersOpen">
            <i class="el-icon-shaixuan"></i>
            筛选
          </button>
          <button
            v-if="hasSearchIntent"
            type="button"
            class="scholar-advanced-btn"
            :class="{ active: advancedSearchVisible }"
            data-track-text="展开高级文献检索"
            aria-label="展开高级文献检索"
            @click="toggleAdvancedSearchPanel"
          >
            <i class="el-icon-search"></i>
            高级检索
          </button>
          <button v-if="hasSearchIntent" type="button" class="scholar-bulk-import-btn" data-track-text="打开文献批量导入" aria-label="打开文献批量导入" @click="openBulkImportDialog">
            <i class="el-icon-upload2"></i>
            批量导入
          </button>
          <button v-if="hasSearchIntent" type="button" class="scholar-history-btn" data-track-text="打开最近文献检索" aria-label="打开最近文献检索" @click="openSearchHistoryDialog">
            <i class="el-icon-time"></i>
            最近检索
          </button>
          <button v-if="hasSearchIntent" type="button" class="scholar-saved-search-btn" data-track-text="打开我的检索式" aria-label="打开我的检索式" @click="openSavedSearchesDialog">
            <i class="el-icon-folder-opened"></i>
            我的检索式
          </button>
        </div>

        <div v-show="hasSearchIntent && advancedSearchVisible" class="scholar-advanced-bar">
          <div class="scholar-advanced-form">
            <div class="scholar-advanced-line">
              <el-select v-model="advancedForm.searchMode" placeholder="检索方式">
                <el-option
                  v-for="item in searchModeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
              <el-input
                v-model="advancedForm.keyword"
                clearable
                placeholder="题名、摘要、关键词或研究问题"
                @keyup.enter.native="applyAdvancedSearch"
              />
            </div>
            <div class="scholar-advanced-line is-full">
              <el-input
                v-model="advancedForm.exactPhrase"
                clearable
                placeholder="完整标题、固定短语或 DOI，填写后优先精确检索"
                @keyup.enter.native="applyAdvancedSearch"
              />
            </div>
            <div class="scholar-advanced-grid">
              <el-autocomplete
                v-model="advancedForm.authorName"
                clearable
                :fetch-suggestions="queryAuthorSuggestions"
                placeholder="作者"
                @input="handleAdvancedEntityInput('author', $event)"
                @select="handleAdvancedEntitySelect('author', $event)"
              />
              <el-autocomplete
                v-model="advancedForm.institutionName"
                clearable
                :fetch-suggestions="queryInstitutionSuggestions"
                placeholder="机构"
                @input="handleAdvancedEntityInput('institution', $event)"
                @select="handleAdvancedEntitySelect('institution', $event)"
              />
              <el-autocomplete
                v-model="advancedForm.sourceName"
                clearable
                :fetch-suggestions="querySourceSuggestions"
                placeholder="期刊/来源"
                @input="handleAdvancedEntityInput('source', $event)"
                @select="handleAdvancedEntitySelect('source', $event)"
              />
              <el-autocomplete
                v-model="advancedForm.topicName"
                clearable
                :fetch-suggestions="queryTopicSuggestions"
                placeholder="主题"
                @input="handleAdvancedEntityInput('topic', $event)"
                @select="handleAdvancedEntitySelect('topic', $event)"
              />
            </div>
            <div class="scholar-advanced-grid">
              <el-input v-model="advancedForm.fromYear" type="number" clearable placeholder="起始年份" />
              <el-input v-model="advancedForm.toYear" type="number" clearable placeholder="结束年份" />
              <el-input v-model="advancedForm.minCitations" type="number" clearable placeholder="最低引用" />
              <el-input v-model="advancedForm.maxCitations" type="number" clearable placeholder="最高引用" />
            </div>
            <div class="scholar-advanced-grid">
              <el-select v-model="advancedForm.type" clearable placeholder="文献类型">
                <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
              <el-select v-model="advancedForm.oaStatus" clearable placeholder="OA 状态">
                <el-option v-for="item in oaStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
              <el-select v-model="advancedForm.impactLevel" clearable placeholder="影响力">
                <el-option v-for="item in impactLevelOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
              <el-select v-model="advancedForm.sort" placeholder="排序">
                <el-option v-for="item in sortQuickOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </div>
            <div class="scholar-advanced-checks">
              <el-checkbox v-model="advancedForm.oaOnly">仅开放获取</el-checkbox>
              <el-checkbox v-model="advancedForm.hasPdf">有 PDF</el-checkbox>
              <el-checkbox v-model="advancedForm.hasFulltext">可全文检索</el-checkbox>
              <el-checkbox v-model="advancedForm.hasDoi">有 DOI</el-checkbox>
              <el-checkbox v-model="advancedForm.hasAbstract">有摘要</el-checkbox>
              <el-checkbox v-model="advancedForm.hasReferences">有参考文献</el-checkbox>
              <el-checkbox v-model="advancedForm.hasPmid">PubMed 收录</el-checkbox>
            </div>
            <div class="scholar-advanced-footer">
              <el-button data-track-text="重置高级文献检索" aria-label="重置高级文献检索" @click="resetAdvancedSearchForm">重置</el-button>
              <el-button data-track-text="收起高级文献检索" aria-label="收起高级文献检索" @click="advancedSearchVisible = false">收起</el-button>
              <el-button type="primary" data-track-text="应用高级文献检索" aria-label="应用高级文献检索" :loading="loading" @click="applyAdvancedSearch">应用检索</el-button>
            </div>
          </div>
        </div>

        <div v-if="activeFilterTags.length" class="scholar-active-filters">
          <span class="scholar-active-filter-label">已筛选</span>
          <button
            v-for="tag in activeFilterTags"
            :key="tag.key"
            type="button"
            class="scholar-active-filter-tag"
            :class="{ 'is-entity-filter': isEntityFilterTag(tag.key) }"
            data-track-text="移除文献筛选项"
            :title="activeFilterTagTitle(tag)"
            :aria-label="activeFilterTagTitle(tag)"
            @click="removeFilterTag(tag.key)"
          >
            <span>{{ tag.label }}</span>
            <i class="el-icon-close"></i>
          </button>
          <button
            type="button"
            class="scholar-active-filter-clear"
            data-track-text="清除全部文献筛选"
            aria-label="清除全部文献筛选"
            @click="clearAllFilters"
          >
            清除筛选
          </button>
        </div>

        <div v-show="hasSearchIntent && filtersOpen" class="scholar-filter-bar">
          <div class="scholar-filter-row scholar-filter-row-main">
            <div class="scholar-filter-checks">
              <el-checkbox v-model="filters.oaOnly">仅开放获取</el-checkbox>
              <el-checkbox v-model="filters.hasPdf">有 PDF</el-checkbox>
              <el-checkbox v-model="filters.hasFulltext">可全文检索</el-checkbox>
              <el-checkbox v-model="filters.excludeRetracted">排除撤稿</el-checkbox>
              <el-checkbox v-model="filters.excludeParatext">排除非正文</el-checkbox>
            </div>
          </div>

          <div class="scholar-filter-row scholar-filter-row-controls">
            <div class="scholar-filter-controls">
              <el-select v-model="filters.sort" class="scholar-filter-select" placeholder="排序">
                <el-option label="相关性优先" value="relevance"></el-option>
                <el-option label="引用数优先" value="cited"></el-option>
                <el-option label="最新优先" value="newest"></el-option>
                <el-option label="影响力优先" value="impact"></el-option>
              </el-select>
              <el-select v-model="filters.impactLevel" class="scholar-filter-select" clearable placeholder="影响力">
                <el-option v-for="item in impactLevelOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-select
                v-model="qualityFilters"
                class="scholar-filter-select scholar-quality-select"
                multiple
                collapse-tags
                clearable
                placeholder="可引用性"
              >
                <el-option v-for="item in qualityFilterOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-select v-model="filters.type" class="scholar-filter-select" clearable placeholder="类型">
                <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-select v-model="filters.oaStatus" class="scholar-filter-select" clearable placeholder="OA 状态">
                <el-option v-for="item in oaStatusOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-select v-model="filters.language" class="scholar-filter-select" clearable placeholder="语言">
                <el-option v-for="item in languageOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-select v-model="filters.countryCode" class="scholar-filter-select" clearable filterable placeholder="国家/地区">
                <el-option v-for="item in countryOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
            </div>
          </div>

          <div class="scholar-filter-row">
            <div class="scholar-filter-range">
              <span class="scholar-filter-label">年份</span>
              <el-input v-model="filters.fromYear" class="scholar-year-input" type="number" placeholder="起始年"></el-input>
              <span class="scholar-year-line">至</span>
              <el-input v-model="filters.toYear" class="scholar-year-input" type="number" placeholder="结束年"></el-input>
            </div>
            <div class="scholar-filter-range">
              <span class="scholar-filter-label">引用</span>
              <el-input v-model="filters.minCitations" class="scholar-citation-input" type="number" placeholder="最低引用"></el-input>
              <span class="scholar-year-line">至</span>
              <el-input v-model="filters.maxCitations" class="scholar-citation-input" type="number" placeholder="最高引用"></el-input>
            </div>
          </div>

          <div class="scholar-filter-row scholar-filter-row-entities">
            <div class="scholar-filter-entity-grid">
              <el-autocomplete
                v-model="filters.authorName"
                class="scholar-filter-autocomplete"
                clearable
                :fetch-suggestions="queryAuthorSuggestions"
                :trigger-on-focus="false"
                placeholder="作者"
                @input="handleEntityFilterInput('author', $event)"
                @select="handleEntityFilterSelect('author', $event)"
              ></el-autocomplete>
              <el-autocomplete
                v-model="filters.institutionName"
                class="scholar-filter-autocomplete"
                clearable
                :fetch-suggestions="queryInstitutionSuggestions"
                :trigger-on-focus="false"
                placeholder="机构"
                @input="handleEntityFilterInput('institution', $event)"
                @select="handleEntityFilterSelect('institution', $event)"
              ></el-autocomplete>
              <el-autocomplete
                v-model="filters.sourceName"
                class="scholar-filter-autocomplete"
                clearable
                :fetch-suggestions="querySourceSuggestions"
                :trigger-on-focus="false"
                placeholder="期刊/来源"
                @input="handleEntityFilterInput('source', $event)"
                @select="handleEntityFilterSelect('source', $event)"
              ></el-autocomplete>
              <el-autocomplete
                v-model="filters.topicName"
                class="scholar-filter-autocomplete"
                clearable
                :fetch-suggestions="queryTopicSuggestions"
                :trigger-on-focus="false"
                placeholder="主题"
                @input="handleEntityFilterInput('topic', $event)"
                @select="handleEntityFilterSelect('topic', $event)"
              ></el-autocomplete>
              <el-autocomplete
                v-model="filters.keywordName"
                class="scholar-filter-autocomplete"
                clearable
                :fetch-suggestions="queryKeywordSuggestions"
                :trigger-on-focus="false"
                placeholder="关键词"
                @input="handleEntityFilterInput('keyword', $event)"
                @select="handleEntityFilterSelect('keyword', $event)"
              ></el-autocomplete>
              <el-autocomplete
                v-model="filters.funderName"
                class="scholar-filter-autocomplete"
                clearable
                :fetch-suggestions="queryFunderSuggestions"
                :trigger-on-focus="false"
                placeholder="基金机构"
                @input="handleEntityFilterInput('funder', $event)"
                @select="handleEntityFilterSelect('funder', $event)"
              ></el-autocomplete>
            </div>
            <div class="scholar-filter-actions">
              <el-button class="scholar-filter-apply" type="primary" data-track-text="应用文献筛选" aria-label="应用文献筛选" @click="submitSearch">应用</el-button>
              <el-button class="scholar-filter-reset" data-track-text="重置文献筛选" aria-label="重置文献筛选" @click="resetFilters">重置</el-button>
            </div>
          </div>
        </div>

        <div v-if="hasSearchIntent" class="scholar-content">
          <button
            type="button"
            class="scholar-mobile-facet-toggle"
            :class="{ active: mobileFacetsOpen }"
            :aria-expanded="mobileFacetsOpen ? 'true' : 'false'"
            data-track-text="展开文献统计聚类"
            :aria-label="mobileFacetsOpen ? '收起文献统计聚类' : '展开文献统计聚类'"
            @click="mobileFacetsOpen = !mobileFacetsOpen"
          >
            <span><i class="el-icon-data-analysis"></i>统计与聚类</span>
            <em>{{ mobileFacetsOpen ? '收起' : '展开' }}</em>
            <i :class="mobileFacetsOpen ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </button>
          <aside class="scholar-sidebar" :class="{ 'is-mobile-open': mobileFacetsOpen }">
            <div class="scholar-side-card">
              <div class="scholar-card-title">检索统计</div>
              <div class="scholar-stat-value">{{ formatNumber(total) }}</div>
              <div class="scholar-stat-label">总结果数</div>
              <div class="scholar-stat-split"></div>
              <div class="scholar-stat-row">
                <span>{{ stats.oaCount }}</span>
                <em>本页 OA</em>
              </div>
              <div class="scholar-stat-row">
                <span>{{ formatNumber(stats.avgCitations) }}</span>
                <em>本页平均引用数</em>
              </div>
              <div v-if="hasFacetData" class="scholar-facet-export-actions">
                <el-button size="mini" data-track-text="复制文献聚类摘要" aria-label="复制文献聚类摘要" :disabled="facetsLoading" @click="copyFacetSummary">
                  <i class="el-icon-document-copy"></i>
                  复制聚类
                </el-button>
                <el-button size="mini" data-track-text="导出文献聚类CSV" aria-label="导出文献聚类CSV" :disabled="facetsLoading" @click="downloadFacetSummary('csv')">
                  <i class="el-icon-download"></i>
                  CSV
                </el-button>
                <el-button size="mini" data-track-text="导出文献聚类Markdown" aria-label="导出文献聚类Markdown" :disabled="facetsLoading" @click="downloadFacetSummary('markdown')">
                  <i class="el-icon-document"></i>
                  Markdown
                </el-button>
              </div>
            </div>

            <div v-for="section in facetSections" :key="section.key" class="scholar-side-card scholar-facet-card">
              <button type="button" class="scholar-facet-head" data-track-text="展开收起文献聚类" aria-label="展开收起文献聚类" @click="toggleFacetSection(section.key)">
                <span>{{ section.title }}</span>
                <em>{{ facetSectionCountText(section) }}</em>
                <i :class="collapsedFacets[section.key] ? 'el-icon-arrow-right' : 'el-icon-arrow-down'"></i>
              </button>
              <div v-if="!collapsedFacets[section.key]" class="scholar-facet-body">
                <div v-if="facetsLoading && !section.items.length" class="scholar-empty-mini">统计加载中...</div>
                <div v-else-if="section.items.length" class="scholar-facet-list">
                <el-input
                  v-if="shouldShowFacetSearch(section)"
                  v-model="facetSearchKeywords[section.key]"
                  class="scholar-facet-search"
                  size="mini"
                  clearable
                  prefix-icon="el-icon-search"
                  placeholder="搜索当前聚类"
                  data-track-ignore
                  @click.native.stop
                />
                  <button
                    v-for="item in visibleFacetItems(section)"
                    :key="item.key"
                    type="button"
                    class="scholar-facet-item"
                    :class="{ active: isFacetActive(section.key, item) }"
                    data-track-text="应用文献聚类筛选"
                    aria-label="应用文献聚类筛选"
                    @click="applyFacetFilter(section.key, item)"
                  >
                    <span class="scholar-facet-name">{{ facetItemLabel(section.key, item) }}</span>
                    <span class="scholar-facet-track">
                      <span class="scholar-facet-progress" :style="{ width: facetPercent(section.items, item.count) }"></span>
                    </span>
                    <em>{{ formatNumber(item.count) }}</em>
                  </button>
                  <button
                    v-if="filteredFacetItems(section).length > facetPreviewLimit"
                    type="button"
                    class="scholar-facet-more"
                    data-track-text="展开收起文献聚类列表"
                    aria-label="展开收起文献聚类列表"
                    @click="toggleFacetExpand(section.key)"
                  >
                    {{ expandedFacets[section.key] ? '收起' : `展开全部 ${filteredFacetItems(section).length} 项` }}
                  </button>
                  <div v-if="!filteredFacetItems(section).length" class="scholar-empty-mini">未找到匹配项</div>
                </div>
                <div v-else class="scholar-empty-mini">暂无数据</div>
              </div>
            </div>
          </aside>

          <section class="scholar-results" :class="{ 'is-compact': listDensity === 'compact' }">
            <div class="scholar-results-head">
              <div class="scholar-results-meta">
                <strong>{{ resultTitle }}</strong>
                <span v-if="screeningView !== 'all' && results.length" class="scholar-screening-view-count">
                  当前视图 {{ visibleResults.length }} 篇
                </span>
                <div v-if="results.length" class="scholar-sort-chips" aria-label="排序方式">
                  <button
                    v-for="item in sortQuickOptions"
                    :key="item.value"
                    type="button"
                    :class="{ active: filters.sort === item.value }"
                    data-track-text="切换文献排序"
                    :title="item.hint"
                    aria-label="切换文献排序"
                    @click="applyQuickSort(item.value)"
                  >
                    {{ item.label }}
                  </button>
                </div>
                <div v-if="results.length" class="scholar-screening-stats" aria-label="当前页初筛统计">
                  <button
                    v-for="item in screeningStatChips"
                    :key="item.value"
                    type="button"
                    :class="{ active: screeningView === item.value }"
                    data-track-text="切换文献初筛视图"
                    aria-label="切换文献初筛视图"
                    @click="setScreeningView(item.value)"
                  >
                    <span>{{ item.label }}</span>
                    <em>{{ item.count }}</em>
                  </button>
                </div>
                <div v-if="results.length" class="scholar-batch-tools">
                  <el-checkbox
                    :value="allCurrentPageSelected"
                    :indeterminate="partiallyCurrentPageSelected"
                    @input="toggleCurrentPageSelection"
                  >
                    本页全选
                  </el-checkbox>
                  <span v-if="selectedWorks.length">已选 {{ selectedWorks.length }} 篇</span>
                  <button
                    v-if="selectedWorks.length"
                    type="button"
                    :disabled="savingSelected"
                    data-track-text="收藏已选文献"
                    aria-label="收藏已选文献"
                    @click="saveSelectedWorks"
                  >
                    {{ savingSelected ? '收藏中...' : '收藏已选' }}
                  </button>
                  <button
                    v-if="selectedWorks.length"
                    type="button"
                    class="scholar-batch-rag"
                    :disabled="ragImportLoading"
                    data-track-text="批量导入文献到RAG"
                    aria-label="批量导入文献到RAG"
                    @click="openRagImportDialog(selectedWorks)"
                  >
                    {{ ragImportLoading ? '导入中...' : '批量导入 RAG' }}
                  </button>
                  <button
                    v-if="selectedPdfWorks.length"
                    type="button"
                    :disabled="batchPdfDownloading"
                    data-track-text="批量下载文献PDF"
                    aria-label="批量下载文献PDF"
                    @click="downloadSelectedPdfs"
                  >
                    {{ batchPdfDownloading ? '下载中...' : `下载PDF ${selectedPdfWorks.length}` }}
                  </button>
                  <button v-if="selectedWorks.length > 1" type="button" data-track-text="对比已选文献" aria-label="对比已选文献" @click="openCompareDialog">对比已选</button>
                  <button
                    v-if="selectedWorks.length"
                    class="scholar-batch-more"
                    :class="{ active: batchMoreVisible }"
                    type="button"
                    data-track-text="展开文献批量操作"
                    aria-label="展开文献批量操作"
                    @click="toggleBatchPanel('more')"
                  >
                    批量操作
                    <i class="el-icon-arrow-down"></i>
                  </button>
                  <button
                    v-if="selectedWorks.length"
                    class="scholar-batch-screening"
                    :class="{ active: batchScreeningVisible }"
                    type="button"
                    data-track-text="展开文献批量初筛"
                    aria-label="展开文献批量初筛"
                    @click="toggleBatchPanel('screening')"
                  >
                    批量初筛
                    <i class="el-icon-arrow-down"></i>
                  </button>
                  <button v-if="selectedWorks.length" type="button" data-track-text="清空已选文献" aria-label="清空已选文献" @click="clearSelection">清空</button>
                </div>
                <div v-if="selectedWorks.length && batchMoreVisible" class="scholar-batch-panel">
                  <strong>批量操作</strong>
                  <div>
                    <button type="button" data-track-text="批量复制文献清单" aria-label="批量复制文献清单" @click="runBatchMoreCommand('copy-reading')">复制文献清单</button>
                    <button type="button" data-track-text="批量复制阅读优先级" aria-label="批量复制阅读优先级" @click="runBatchMoreCommand('copy-priority')">复制阅读优先级</button>
                    <button type="button" data-track-text="批量导出阅读优先级" aria-label="批量导出阅读优先级" @click="runBatchMoreCommand('download-priority')">导出阅读优先级</button>
                    <button type="button" data-track-text="批量复制文献DOI链接" aria-label="批量复制文献DOI链接" @click="runBatchMoreCommand('copy-identifiers')">复制 DOI/链接标识</button>
                    <button type="button" data-track-text="批量导出文献DOI链接CSV" aria-label="批量导出文献DOI链接CSV" @click="runBatchMoreCommand('download-identifiers')">导出 DOI/链接 CSV</button>
                  </div>
                </div>
                <div v-if="selectedWorks.length && batchScreeningVisible" class="scholar-batch-panel">
                  <strong>批量初筛</strong>
                  <div>
                    <button type="button" data-track-text="批量标记文献纳入" aria-label="批量标记文献纳入" @click="runBatchScreeningCommand('include')">标记为纳入</button>
                    <button type="button" data-track-text="批量标记文献可能相关" aria-label="批量标记文献可能相关" @click="runBatchScreeningCommand('maybe')">标记为可能相关</button>
                    <button type="button" data-track-text="批量标记文献待定" aria-label="批量标记文献待定" @click="runBatchScreeningCommand('pending')">标记为待定</button>
                    <button type="button" data-track-text="批量标记文献排除" aria-label="批量标记文献排除" @click="runBatchScreeningCommand('exclude')">标记为排除并填写原因</button>
                    <button type="button" data-track-text="批量填写文献初筛备注" aria-label="批量填写文献初筛备注" @click="runBatchScreeningCommand('note')">批量填写备注</button>
                    <button type="button" data-track-text="批量清除文献初筛标记" aria-label="批量清除文献初筛标记" @click="runBatchScreeningCommand('clear')">清除初筛标记</button>
                  </div>
                </div>
              </div>
              <div v-if="results.length" class="scholar-results-actions">
                <el-input
                  v-model="pageKeyword"
                  class="scholar-page-filter"
                  size="mini"
                  clearable
                  prefix-icon="el-icon-search"
                  placeholder="页内过滤"
                  title="只过滤当前页已返回文献"
                />
                <span v-if="pageFilterSummary" class="scholar-page-filter-summary">{{ pageFilterSummary }}</span>
                <div v-if="showTopPager" class="scholar-top-pager">
                  <button
                    type="button"
                    title="上一页"
                    data-track-text="文献结果上一页"
                    aria-label="文献结果上一页"
                    :disabled="!canGoPreviousPage || loading"
                    @click="goRelativePage(-1)"
                  >
                    <i class="el-icon-arrow-left"></i>
                  </button>
                  <span>{{ formatNumber(page) }} / {{ formatNumber(totalPages) }}</span>
                  <button
                    type="button"
                    title="下一页"
                    data-track-text="文献结果下一页"
                    aria-label="文献结果下一页"
                    :disabled="!canGoNextPage || loading"
                    @click="goRelativePage(1)"
                  >
                    <i class="el-icon-arrow-right"></i>
                  </button>
                </div>
                <el-select
                  v-model="listDensity"
                  class="scholar-density-select"
                  size="mini"
                  title="列表阅读密度"
                  @change="handleListDensityChange"
                >
                  <el-option label="标准视图" value="standard"></el-option>
                  <el-option label="紧凑视图" value="compact"></el-option>
                </el-select>
                <el-select
                  v-model="screeningView"
                  class="scholar-screening-view-select"
                  size="mini"
                  placeholder="初筛视图"
                >
                  <el-option
                    v-for="item in screeningViewOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
                <el-button class="scholar-save-search-btn" size="mini" data-track-text="保存文献检索式" aria-label="保存文献检索式" @click="openSaveSearchDialog">
                  <i class="el-icon-star-off"></i>
                  保存检索
                </el-button>
                <el-button class="scholar-link-btn" size="mini" data-track-text="复制文献检索链接" aria-label="复制文献检索链接" @click="copySearchLink">
                  <i class="el-icon-link"></i>
                  复制链接
                </el-button>
                <el-button
                  class="scholar-more-btn"
                  :class="{ active: resultsMoreVisible }"
                  size="mini"
                  data-track-text="展开文献更多操作"
                  aria-label="展开文献更多操作"
                  @click="toggleResultsMorePanel"
                >
                    更多操作
                    <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <input
                  ref="screeningImportInput"
                  class="scholar-hidden-file"
                  type="file"
                  accept="application/json,.json"
                  @change="importScreeningPackage"
                />
                <el-button
                  class="scholar-export-btn"
                  :class="{ active: exportPanelVisible }"
                  size="mini"
                  :loading="exportingTopResults"
                  data-track-text="展开文献导出面板"
                  aria-label="展开文献导出面板"
                  @click="toggleExportPanel"
                >
                  <i class="el-icon-download"></i>
                  导出
                  <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
              </div>
              <div v-if="results.length && resultsMoreVisible" class="scholar-results-more-panel">
                <section class="scholar-results-more-group">
                  <h4>检索复现</h4>
                  <div class="scholar-results-more-actions">
                    <button type="button" data-track-text="复制文献检索策略" aria-label="复制文献检索策略" @click="runResultsMoreCommand('copy-strategy')">复制检索策略</button>
                    <button type="button" data-track-text="导出文献检索复现Markdown" aria-label="导出文献检索复现Markdown" @click="runResultsMoreCommand('strategy-markdown')">导出复现 Markdown</button>
                    <button type="button" data-track-text="导出文献检索参数JSON" aria-label="导出文献检索参数JSON" @click="runResultsMoreCommand('strategy-json')">导出参数 JSON</button>
                  </div>
                </section>
                <section class="scholar-results-more-group">
                  <h4>质量报告</h4>
                  <div class="scholar-results-more-actions">
                    <button type="button" data-track-text="复制文献检索质量报告" aria-label="复制文献检索质量报告" @click="runResultsMoreCommand('copy-quality')">复制质量报告</button>
                    <button type="button" data-track-text="导出文献质量报告Markdown" aria-label="导出文献质量报告Markdown" @click="runResultsMoreCommand('quality-markdown')">导出报告 Markdown</button>
                    <button type="button" data-track-text="导出文献质量报告CSV" aria-label="导出文献质量报告CSV" @click="runResultsMoreCommand('quality-csv')">导出报告 CSV</button>
                  </div>
                </section>
                <section class="scholar-results-more-group">
                  <h4>初筛记录</h4>
                  <div class="scholar-results-more-actions">
                    <button type="button" data-track-text="复制文献初筛摘要" aria-label="复制文献初筛摘要" :disabled="!screeningMarkCount" @click="runResultsMoreCommand('screening-copy-summary')">复制初筛摘要</button>
                    <button type="button" data-track-text="导出文献初筛摘要" aria-label="导出文献初筛摘要" :disabled="!screeningMarkCount" @click="runResultsMoreCommand('screening-download-summary')">导出初筛摘要</button>
                    <button type="button" data-track-text="导出当前页文献初筛记录" aria-label="导出当前页文献初筛记录" :disabled="!screeningStats.marked" @click="runResultsMoreCommand('screening-export-current')">导出当前页记录</button>
                    <button type="button" data-track-text="导出全部文献初筛记录" aria-label="导出全部文献初筛记录" :disabled="!screeningMarkCount" @click="runResultsMoreCommand('screening-export-all')">导出全部记录</button>
                    <button type="button" data-track-text="导入文献初筛记录" aria-label="导入文献初筛记录" @click="runResultsMoreCommand('screening-import')">导入初筛记录</button>
                  </div>
                </section>
                <section v-if="bulkImportSummary" class="scholar-results-more-group">
                  <h4>导入报告</h4>
                  <div class="scholar-results-more-actions">
                    <button type="button" data-track-text="复制文献批量导入报告" aria-label="复制文献批量导入报告" @click="runResultsMoreCommand('copy-bulk-report')">复制导入报告</button>
                    <button type="button" data-track-text="导出文献批量导入报告Markdown" aria-label="导出文献批量导入报告Markdown" @click="runResultsMoreCommand('bulk-report-markdown')">导出 Markdown</button>
                    <button type="button" data-track-text="导出文献批量导入报告CSV" aria-label="导出文献批量导入报告CSV" @click="runResultsMoreCommand('bulk-report-csv')">导出 CSV</button>
                  </div>
                </section>
              </div>
              <div v-if="results.length && exportPanelVisible" class="scholar-results-more-panel scholar-export-panel">
                <section class="scholar-results-more-group">
                  <h4>当前页</h4>
                  <div class="scholar-results-more-actions">
                    <button type="button" data-track-text="导出当前页BibTeX" aria-label="导出当前页BibTeX" @click="runExportCommand('current:bibtex')">BibTeX</button>
                    <button type="button" data-track-text="导出当前页RIS" aria-label="导出当前页RIS" @click="runExportCommand('current:ris')">RIS / EndNote</button>
                    <button type="button" data-track-text="导出当前页GBT7714" aria-label="导出当前页GBT7714" @click="runExportCommand('current:gbt7714')">GB/T 7714</button>
                    <button type="button" data-track-text="导出当前页文献CSV" aria-label="导出当前页文献CSV" @click="runExportCommand('current:csv')">CSV</button>
                    <button type="button" data-track-text="导出当前页筛选表CSV" aria-label="导出当前页筛选表CSV" @click="runExportCommand('current:screening')">筛选表 CSV</button>
                    <button type="button" data-track-text="导出当前视图筛选表" aria-label="导出当前视图筛选表" @click="runExportCommand('visible:screening')">当前视图筛选表</button>
                    <button type="button" data-track-text="导出已标记筛选表" aria-label="导出已标记筛选表" :disabled="!screeningStats.marked" @click="runExportCommand('marked:screening')">已标记筛选表</button>
                    <button type="button" data-track-text="导出当前页Markdown清单" aria-label="导出当前页Markdown清单" @click="runExportCommand('current:markdown')">Markdown 清单</button>
                  </div>
                </section>
                <section class="scholar-results-more-group">
                  <h4>已选文献</h4>
                  <div class="scholar-results-more-actions">
                    <button type="button" data-track-text="导出已选文献BibTeX" aria-label="导出已选文献BibTeX" :disabled="!selectedWorks.length" @click="runExportCommand('selected:bibtex')">BibTeX</button>
                    <button type="button" data-track-text="导出已选文献RIS" aria-label="导出已选文献RIS" :disabled="!selectedWorks.length" @click="runExportCommand('selected:ris')">RIS / EndNote</button>
                    <button type="button" data-track-text="导出已选文献GBT7714" aria-label="导出已选文献GBT7714" :disabled="!selectedWorks.length" @click="runExportCommand('selected:gbt7714')">GB/T 7714</button>
                    <button type="button" data-track-text="导出已选文献CSV" aria-label="导出已选文献CSV" :disabled="!selectedWorks.length" @click="runExportCommand('selected:csv')">CSV</button>
                    <button type="button" data-track-text="导出已选筛选表CSV" aria-label="导出已选筛选表CSV" :disabled="!selectedWorks.length" @click="runExportCommand('selected:screening')">筛选表 CSV</button>
                    <button type="button" data-track-text="导出已选Markdown清单" aria-label="导出已选Markdown清单" :disabled="!selectedWorks.length" @click="runExportCommand('selected:markdown')">Markdown 清单</button>
                  </div>
                </section>
                <section class="scholar-results-more-group">
                  <h4>前 100 条</h4>
                  <div class="scholar-results-more-actions">
                    <button type="button" data-track-text="导出前100条BibTeX" aria-label="导出前100条BibTeX" @click="runExportCommand('top100:bibtex')">BibTeX</button>
                    <button type="button" data-track-text="导出前100条RIS" aria-label="导出前100条RIS" @click="runExportCommand('top100:ris')">RIS / EndNote</button>
                    <button type="button" data-track-text="导出前100条GBT7714" aria-label="导出前100条GBT7714" @click="runExportCommand('top100:gbt7714')">GB/T 7714</button>
                    <button type="button" data-track-text="导出前100条CSV" aria-label="导出前100条CSV" @click="runExportCommand('top100:csv')">CSV</button>
                    <button type="button" data-track-text="导出前100条筛选表CSV" aria-label="导出前100条筛选表CSV" @click="runExportCommand('top100:screening')">筛选表 CSV</button>
                    <button type="button" data-track-text="导出前100条Markdown清单" aria-label="导出前100条Markdown清单" @click="runExportCommand('top100:markdown')">Markdown 清单</button>
                  </div>
                </section>
              </div>
            </div>

            <el-alert
              v-if="errorMessage"
              class="scholar-error"
              type="error"
              :closable="false"
              :title="errorMessage"
              show-icon
            />

            <div v-loading="loading" class="scholar-list">
              <div v-if="!loading && !results.length" class="scholar-empty-state">
                <i class="el-icon-sousuo"></i>
                <div>{{ emptyStateTitle }}</div>
                <p>{{ emptyStateDescription }}</p>
                <div v-if="emptyStateActions.length" class="scholar-empty-actions">
                  <el-button
                    v-for="action in emptyStateActions"
                    :key="action.key"
                    size="mini"
                    :type="action.type || 'default'"
                    data-track-text="执行文献空状态操作"
                    aria-label="执行文献空状态操作"
                    @click="handleEmptyStateAction(action.key)"
                  >
                    {{ action.label }}
                  </el-button>
                </div>
              </div>
              <div v-else-if="!loading && !visibleResults.length" class="scholar-empty-state compact">
                <i class="el-icon-folder-opened"></i>
                <div>{{ filteredEmptyTitle }}</div>
                <p>{{ filteredEmptyDescription }}</p>
                <div v-if="pageKeyword" class="scholar-empty-actions">
                  <el-button size="mini" type="primary" data-track-text="清除文献页内过滤" aria-label="清除文献页内过滤" @click="clearPageKeyword">清除页内过滤</el-button>
                </div>
              </div>

              <article
                v-for="work in visibleResults"
                :key="work.workKey"
                class="scholar-work-card"
                :class="{ selected: isWorkSelected(work) }"
                data-track-text="打开文献详情"
                aria-label="打开文献详情"
                @click="openWorkDetailPage(work)"
              >
                <div class="scholar-work-top">
                  <el-checkbox
                    class="scholar-work-select"
                    :value="isWorkSelected(work)"
                    @input="value => toggleWorkSelection(work, value)"
                    @click.native.stop
                  >
                    <span></span>
                  </el-checkbox>
                  <div class="scholar-work-title">
                    <span v-if="work.isOa" class="scholar-oa-badge">OA</span>
                    <span v-if="hasFulltext(work)" class="scholar-fulltext-badge">全文</span>
                    <span v-if="isRetracted(work)" class="scholar-risk-badge danger">已撤稿</span>
                    <span v-if="isParatext(work)" class="scholar-risk-badge">非正文</span>
                    <span
                      v-for="item in indexedBadges(work)"
                      :key="item"
                      class="scholar-indexed-badge"
                    >
                      {{ item }}
                    </span>
                    <span
                      v-if="screeningMark(work)"
                      class="scholar-screening-badge"
                      :class="screeningClass(work)"
                    >
                      初筛 {{ screeningDecisionText(screeningMark(work).decision) }}
                    </span>
                    <span class="scholar-highlight-text">
                      <template v-for="(part, partIndex) in highlightTextParts(work.title || 'Untitled work')">
                        <mark v-if="part.matched" :key="`title-hit-${partIndex}`">{{ part.text }}</mark>
                        <span v-else :key="`title-text-${partIndex}`">{{ part.text }}</span>
                      </template>
                    </span>
                  </div>
                  <div class="scholar-work-actions">
                    <span v-if="impactBadge(work)" class="scholar-impact-badge">{{ impactBadge(work) }}</span>
                    <span class="scholar-cite-badge">引 {{ formatNumber(work.citedByCount || 0) }}</span>
                    <el-button class="scholar-pill-btn" size="mini" title="引用" data-track-text="打开文献引用格式" aria-label="打开文献引用格式" @click.stop="openCitationDialog(work)">
                      <i class="el-icon-notebook-2"></i>
                      引用
                    </el-button>
                    <button
                      v-if="work.pdfUrl"
                      type="button"
                      class="scholar-pill-btn scholar-pdf-download-btn primary"
                      :disabled="isPdfDownloading(work)"
                      title="下载 PDF"
                      data-track-text="下载文献PDF"
                      aria-label="下载文献PDF"
                      @click.stop.prevent="downloadWorkPdf(work, $event)"
                    >
                      <i :class="isPdfDownloading(work) ? 'el-icon-loading' : 'el-icon-download'"></i>
                      PDF
                    </button>
                  </div>
                </div>
                <div class="scholar-meta">
                  <span v-if="work.publicationYear"><i class="el-icon-date"></i>{{ work.publicationYear }}</span>
                  <span v-if="work.sourceName">
                    <i class="el-icon-reading"></i>
                    <button
                      v-if="work.sourceId"
                      type="button"
                      class="scholar-inline-link"
                      data-track-text="打开文献来源详情"
                      aria-label="打开文献来源详情"
                      @click.stop="openEntityDetail('sources', work.sourceId, work.sourceName, work)"
                    >
                      {{ work.sourceName }}
                    </button>
                    <template v-else>{{ work.sourceName }}</template>
                  </span>
                  <span v-if="biblioText(work)"><i class="el-icon-collection"></i>{{ biblioText(work) }}</span>
                  <span v-if="work.type"><i class="el-icon-document"></i>{{ formatType(work.type) }}</span>
                  <span v-if="work.authors && work.authors.length">
                    <i class="el-icon-user"></i>
                    <button
                      v-for="author in visiblePeople(work.authors, 3)"
                      :key="author.id || author.name"
                      type="button"
                      class="scholar-inline-link"
                      data-track-text="打开文献作者详情"
                      aria-label="打开文献作者详情"
                      @click.stop="openEntityDetail('authors', author.id, author.name, work)"
                    >
                      {{ author.name }}
                    </button>
                    <em v-if="work.authors.length > 3">+{{ work.authors.length - 3 }}</em>
                  </span>
                </div>
                <div v-if="work.institutions && work.institutions.length" class="scholar-institution">
                  <i class="el-icon-school"></i>
                  <button
                    v-for="institution in visiblePeople(work.institutions, 3)"
                    :key="institution.id || institution.name"
                    type="button"
                    class="scholar-inline-link"
                    data-track-text="打开文献机构详情"
                    aria-label="打开文献机构详情"
                    @click.stop="openEntityDetail('institutions', institution.id, institution.name, work)"
                  >
                    {{ institution.name }}
                  </button>
                  <em v-if="work.institutions.length > 3">+{{ work.institutions.length - 3 }}</em>
                </div>
                <div v-if="work.doi" class="scholar-doi">
                  <span class="scholar-doi-label">DOI</span>
                  <button
                    type="button"
                    class="scholar-doi-copy"
                    title="复制 DOI"
                    data-track-text="复制文献DOI"
                    aria-label="复制文献DOI"
                    @click.stop="copyWorkDoi(work)"
                  >
                    {{ work.doi }}
                  </button>
                  <span v-if="firstProviderId(work)" class="scholar-provider-id" data-track-text="打开文献外部编号" aria-label="打开文献外部编号" @click.stop="openExternal(firstProviderUrl(work))">
                    {{ firstProviderId(work) }}
                  </span>
                </div>
                <div v-if="openAccessEvidenceChips(work).length" class="scholar-oa-evidence">
                  <span
                    v-for="chip in openAccessEvidenceChips(work)"
                    :key="chip"
                  >
                    {{ chip }}
                  </span>
                </div>
                <p v-if="displayAbstract(work)" class="scholar-abstract" :class="{ expanded: isWorkExpanded(work) }">
                  <template v-for="(part, partIndex) in highlightTextParts(displayAbstract(work))">
                    <mark v-if="part.matched" :key="`abstract-hit-${partIndex}`">{{ part.text }}</mark>
                    <span v-else :key="`abstract-text-${partIndex}`">{{ part.text }}</span>
                  </template>
                </p>
                <div v-if="isWorkExpanded(work)" v-loading="isWorkDetailLoading(work)" class="scholar-inline-detail">
                  <div v-if="expandedWork(work).authors && expandedWork(work).authors.length" class="scholar-inline-row">
                    <strong>作者</strong>
                    <span>{{ formatAuthors(expandedWork(work).authors, 12) }}</span>
                  </div>
                  <div v-if="expandedWork(work).institutions && expandedWork(work).institutions.length" class="scholar-inline-row">
                    <strong>机构</strong>
                    <span>{{ formatInstitutions(expandedWork(work).institutions, 8) }}</span>
                  </div>
                  <div v-if="expandedWork(work).doi" class="scholar-inline-row">
                    <strong>DOI</strong>
                    <span>{{ expandedWork(work).doi }}</span>
                  </div>
                  <div class="scholar-inline-row">
                    <strong>开放</strong>
                    <span>{{ expandedWork(work).isOa ? oaStatusText(expandedWork(work).oaStatus) : '非开放获取' }}</span>
                  </div>
                </div>
                <div class="scholar-work-footer">
                  <div class="scholar-tags">
                    <button
                      v-for="topic in visibleTopics(work)"
                      :key="topic.id || topic.name"
                      type="button"
                      data-track-text="打开文献主题详情"
                      aria-label="打开文献主题详情"
                      @click.stop="openEntityDetail('topics', topic.id, topic.name, work)"
                    >
                      {{ topic.name }}
                    </button>
                  </div>
                  <div class="scholar-card-actions">
                    <button type="button" class="scholar-action-link" data-track-text="复制文献阅读条目" aria-label="复制文献阅读条目" @click.stop="copyWorkReadingItem(work)">
                      复制条目
                    </button>
                    <button
                      type="button"
                      class="scholar-action-link"
                      :class="{ active: isCardActionPanelOpen(work, 'screening') }"
                      data-track-text="展开文献初筛操作"
                      aria-label="展开文献初筛操作"
                      @click.stop="toggleCardActionPanel(work, 'screening')"
                    >
                      初筛
                      <template v-if="screeningMark(work)">：{{ screeningDecisionText(screeningMark(work).decision) }}</template>
                      <i class="el-icon-arrow-down"></i>
                    </button>
                    <button
                      v-if="canSearchCitingWorks(work)"
                      type="button"
                      class="scholar-action-link"
                      title="检索引用本文的文献"
                      data-track-text="检索引用本文文献"
                      aria-label="检索引用本文文献"
                      @click.stop="searchCitingWorks(work)"
                    >
                      引用本文
                    </button>
                    <button
                      v-if="workExpandSearchOptions(work).length"
                      type="button"
                      class="scholar-action-link"
                      :class="{ active: isCardActionPanelOpen(work, 'expand') }"
                      data-track-text="展开文献扩展检索"
                      aria-label="展开文献扩展检索"
                      @click.stop="toggleCardActionPanel(work, 'expand')"
                    >
                      扩展
                      <i class="el-icon-arrow-down"></i>
                    </button>
                    <button type="button" class="scholar-action-link" data-track-text="展开收起文献摘要" aria-label="展开收起文献摘要" @click.stop="toggleWorkExpanded(work)">
                      {{ isWorkExpanded(work) ? '收起' : '展开' }}
                    </button>
                    <button
                      type="button"
                      class="scholar-action-link"
                      :disabled="ragImportLoading"
                      data-track-text="导入单篇文献到RAG"
                      aria-label="导入单篇文献到RAG"
                      @click.stop="openRagImportDialog(work)"
                    >
                      导入RAG
                    </button>
                    <button type="button" class="scholar-action-link" data-track-text="收藏取消收藏文献" aria-label="收藏取消收藏文献" @click.stop="toggleSaved(work)">
                      {{ isSaved(work) ? '取消收藏' : '收藏' }}
                    </button>
                  </div>
                  <div
                    v-if="isCardActionPanelOpen(work, 'screening')"
                    class="scholar-card-action-panel"
                    data-track-ignore
                    @click.stop
                  >
                    <strong>初筛标记</strong>
                    <div class="scholar-card-action-grid">
                      <button type="button" data-track-text="标记单篇文献纳入" aria-label="标记单篇文献纳入" @click="runCardScreeningCommand(work, 'include')">纳入</button>
                      <button type="button" data-track-text="标记单篇文献可能相关" aria-label="标记单篇文献可能相关" @click="runCardScreeningCommand(work, 'maybe')">可能相关</button>
                      <button type="button" data-track-text="标记单篇文献待定" aria-label="标记单篇文献待定" @click="runCardScreeningCommand(work, 'pending')">待定</button>
                      <button type="button" data-track-text="标记单篇文献排除" aria-label="标记单篇文献排除" @click="runCardScreeningCommand(work, 'exclude')">排除并填写原因</button>
                      <button type="button" data-track-text="填写单篇文献备注" aria-label="填写单篇文献备注" @click="runCardScreeningCommand(work, 'note')">填写备注</button>
                      <button type="button" data-track-text="清除单篇文献初筛标记" aria-label="清除单篇文献初筛标记" :disabled="!screeningMark(work)" @click="runCardScreeningCommand(work, 'clear')">清除标记</button>
                    </div>
                  </div>
                  <div
                    v-if="isCardActionPanelOpen(work, 'expand')"
                    class="scholar-card-action-panel"
                    data-track-ignore
                    @click.stop
                  >
                    <strong>扩展检索</strong>
                    <div class="scholar-card-action-grid is-expand">
                      <button
                        v-for="item in workExpandSearchOptions(work)"
                        :key="item.key"
                        type="button"
                        data-track-text="执行单篇文献扩展检索"
                        aria-label="执行单篇文献扩展检索"
                        @click="runCardExpandSearch(work, item.key)"
                      >
                        <span>{{ item.label }}</span>
                        <em v-if="item.hint">{{ item.hint }}</em>
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div v-if="results.length" class="scholar-pagination">
              <el-pagination
                background
                :current-page="page"
                :page-size="size"
                :page-sizes="pageSizes"
                :total="paginationTotal"
                :disabled="loading"
                layout="total, sizes, prev, pager, next, jumper"
                @current-change="handlePageChange"
                @size-change="handleSizeChange"
              />
            </div>
          </section>
        </div>
      </div>

      <ScholarFooter />
    </div>

    <el-dialog class="scholar-detail-dialog" :visible.sync="detailVisible" width="760px" title="文献详情">
      <div v-if="selectedWork" class="scholar-detail">
        <h2>{{ selectedWork.title }}</h2>
        <div class="scholar-detail-meta">
          <span v-if="selectedWork.publicationYear">{{ selectedWork.publicationYear }}</span>
          <span v-if="selectedWork.sourceName">{{ selectedWork.sourceName }}</span>
          <span v-if="selectedWork.citedByCount !== null">引用 {{ formatNumber(selectedWork.citedByCount || 0) }}</span>
          <span v-if="selectedWork.isOa">开放获取：{{ oaStatusText(selectedWork.oaStatus) }}</span>
        </div>
        <p v-if="selectedWork.abstractText">{{ selectedWork.abstractText }}</p>
        <div v-if="selectedWork.authors && selectedWork.authors.length" class="scholar-detail-row">
          <strong>作者</strong>
          <span>{{ formatAuthors(selectedWork.authors, 12) }}</span>
        </div>
        <div v-if="selectedWork.institutions && selectedWork.institutions.length" class="scholar-detail-row">
          <strong>机构</strong>
          <span>{{ formatInstitutions(selectedWork.institutions, 8) }}</span>
        </div>
        <div v-if="selectedWork.doi" class="scholar-detail-row">
          <strong>DOI</strong>
          <span>{{ selectedWork.doi }}</span>
        </div>
        <div class="scholar-detail-actions">
          <el-button type="primary" data-track-text="打开文献详情原文" aria-label="打开文献详情原文" @click="openExternal(selectedWork.pdfUrl || selectedWork.landingPageUrl || doiUrl(selectedWork))">
            打开文献
          </el-button>
          <el-button data-track-text="收藏取消收藏详情文献" aria-label="收藏取消收藏详情文献" @click="toggleSaved(selectedWork)">
            {{ isSaved(selectedWork) ? '取消收藏' : '收藏文献' }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <el-dialog class="scholar-compare-dialog" :visible.sync="compareVisible" width="1080px" title="已选文献对比">
      <div v-if="selectedWorks.length" class="scholar-compare">
        <div class="scholar-compare-head">
          <div class="scholar-compare-summary">
            <span>已选 {{ selectedWorks.length }} 篇</span>
            <span>开放获取 {{ compareStats.oaCount }} 篇</span>
            <span>可下载 PDF {{ compareStats.pdfCount }} 篇</span>
            <span>平均引用 {{ formatNumber(compareStats.averageCitations) }}</span>
          </div>
          <div class="scholar-compare-actions">
            <el-button size="mini" data-track-text="复制文献对比表格" aria-label="复制文献对比表格" @click="copyCompareTable">
              <i class="el-icon-document-copy"></i>
              复制表格
            </el-button>
            <el-button size="mini" data-track-text="导出文献对比CSV" aria-label="导出文献对比CSV" @click="downloadCompareTable('csv')">
              <i class="el-icon-download"></i>
              CSV
            </el-button>
            <el-button size="mini" data-track-text="导出文献对比Markdown" aria-label="导出文献对比Markdown" @click="downloadCompareTable('markdown')">
              <i class="el-icon-document"></i>
              Markdown
            </el-button>
          </div>
        </div>
        <el-table
          :data="selectedWorks"
          class="scholar-compare-table"
          max-height="520"
          stripe
          border
        >
          <el-table-column label="题名" min-width="320">
            <template slot-scope="{ row }">
              <button type="button" class="scholar-compare-title" data-track-text="打开对比文献详情" aria-label="打开对比文献详情" @click="openWorkDetailPage(row)">
                {{ row.title || 'Untitled work' }}
              </button>
              <div class="scholar-compare-sub">{{ formatAuthors(row.authors, 3) || '未知作者' }}</div>
            </template>
          </el-table-column>
          <el-table-column label="年份" width="82" align="center">
            <template slot-scope="{ row }">{{ row.publicationYear || '-' }}</template>
          </el-table-column>
          <el-table-column label="来源" min-width="180">
            <template slot-scope="{ row }">{{ row.sourceName || '未知来源' }}</template>
          </el-table-column>
          <el-table-column label="引用" width="86" align="right">
            <template slot-scope="{ row }">{{ formatNumber(row.citedByCount || 0) }}</template>
          </el-table-column>
          <el-table-column label="开放获取" width="112" align="center">
            <template slot-scope="{ row }">
              <span :class="row.isOa ? 'scholar-compare-yes' : 'scholar-compare-no'">
                {{ row.isOa ? oaStatusText(row.oaStatus) : '否' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="PDF" width="72" align="center">
            <template slot-scope="{ row }">
              <span :class="row.pdfUrl ? 'scholar-compare-yes' : 'scholar-compare-no'">
                {{ row.pdfUrl ? '有' : '无' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="OA 证据" min-width="210">
            <template slot-scope="{ row }">
              <div v-if="openAccessEvidenceChips(row).length" class="scholar-compare-evidence">
                <span v-for="chip in openAccessEvidenceChips(row)" :key="chip">{{ chip }}</span>
              </div>
              <span v-else class="scholar-compare-no">暂无</span>
            </template>
          </el-table-column>
          <el-table-column label="DOI" min-width="180">
            <template slot-scope="{ row }">
              <span class="scholar-compare-doi">{{ row.doi || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="126" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" data-track-text="打开对比文献详情" aria-label="打开对比文献详情" @click="openWorkDetailPage(row)">详情</el-button>
              <el-button type="text" data-track-text="打开对比文献原文" aria-label="打开对比文献原文" @click="openExternal(row.pdfUrl || row.landingPageUrl || doiUrl(row))">原文</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <el-empty v-else description="请先选择需要对比的文献"></el-empty>
    </el-dialog>

    <el-dialog class="scholar-saved-dialog" :visible.sync="savedVisible" width="760px" title="我的文献收藏">
      <div v-if="savedWorks.length" class="scholar-saved-filter">
        <el-input
          v-model="savedKeyword"
          clearable
          prefix-icon="el-icon-search"
          placeholder="检索收藏标题、作者、来源或备注"
        ></el-input>
        <el-select v-model="savedTagFilter" clearable placeholder="全部标签">
          <el-option
            v-for="tag in savedFilterTagOptions"
            :key="tag"
            :label="tag"
            :value="tag"
          ></el-option>
        </el-select>
        <el-button
          class="scholar-export-btn"
          size="small"
          :class="{ active: savedExportVisible }"
          :disabled="!filteredSavedWorks.length"
          data-track-text="展开收藏文献导出"
          aria-label="展开收藏文献导出"
          @click="toggleSavedPanel('export')"
        >
          <i class="el-icon-download"></i>
          导出
          <i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <el-button
          class="scholar-copy-saved-btn"
          size="small"
          :disabled="!filteredSavedWorks.length"
          data-track-text="复制收藏文献清单"
          aria-label="复制收藏文献清单"
          @click="copySavedReadingList"
        >
          <i class="el-icon-document-copy"></i>
          复制清单
        </el-button>
        <el-button
          class="scholar-copy-saved-btn"
          size="small"
          :class="{ active: savedOrganizerVisible }"
          :disabled="!filteredSavedWorks.length"
          data-track-text="展开收藏文献整理"
          aria-label="展开收藏文献整理"
          @click="toggleSavedPanel('organizer')"
        >
          <i class="el-icon-collection"></i>
          整理
          <i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <div v-if="filteredSavedWorks.length && savedExportVisible" class="scholar-saved-panel">
          <strong>导出收藏文献</strong>
          <div>
            <button type="button" data-track-text="导出收藏文献BibTeX" aria-label="导出收藏文献BibTeX" @click="runSavedExportCommand('bibtex')">BibTeX</button>
            <button type="button" data-track-text="导出收藏文献RIS" aria-label="导出收藏文献RIS" @click="runSavedExportCommand('ris')">RIS / EndNote</button>
            <button type="button" data-track-text="导出收藏文献GBT7714" aria-label="导出收藏文献GBT7714" @click="runSavedExportCommand('gbt7714')">GB/T 7714</button>
            <button type="button" data-track-text="导出收藏文献CSV" aria-label="导出收藏文献CSV" @click="runSavedExportCommand('csv')">CSV</button>
            <button type="button" data-track-text="导出收藏文献Markdown" aria-label="导出收藏文献Markdown" @click="runSavedExportCommand('markdown')">Markdown 清单</button>
          </div>
        </div>
        <div v-if="filteredSavedWorks.length && savedOrganizerVisible" class="scholar-saved-panel">
          <strong>整理收藏文献</strong>
          <div>
            <button type="button" data-track-text="复制收藏文献DOI链接" aria-label="复制收藏文献DOI链接" @click="runSavedOrganizerCommand('copy-identifiers')">复制 DOI/链接</button>
            <button type="button" data-track-text="导出收藏文献DOI链接CSV" aria-label="导出收藏文献DOI链接CSV" @click="runSavedOrganizerCommand('download-identifiers')">导出 DOI/链接 CSV</button>
            <button type="button" data-track-text="复制收藏阅读优先级" aria-label="复制收藏阅读优先级" @click="runSavedOrganizerCommand('copy-priority')">复制阅读优先级</button>
            <button type="button" data-track-text="导出收藏阅读优先级" aria-label="导出收藏阅读优先级" @click="runSavedOrganizerCommand('download-priority')">导出阅读优先级</button>
          </div>
        </div>
      </div>
      <div v-if="filteredSavedWorks.length" class="scholar-saved-list">
        <div v-for="item in filteredSavedWorks" :key="item.work.workKey" class="scholar-saved-item">
          <div>
            <strong data-track-text="打开收藏文献" aria-label="打开收藏文献" @click="openSavedWork(item.work)">{{ item.work.title }}</strong>
            <p>{{ item.work.publicationYear || '-' }} · {{ item.work.sourceName || '未知来源' }}</p>
            <div v-if="item.tags && item.tags.length" class="scholar-saved-tags">
              <span v-for="tag in item.tags" :key="tag">{{ tag }}</span>
            </div>
            <p v-if="item.note" class="scholar-saved-note">{{ item.note }}</p>
          </div>
          <div class="scholar-saved-actions">
            <el-button type="text" data-track-text="打开收藏文献详情" aria-label="打开收藏文献详情" @click="openWorkDetailPage(item.work)">详情</el-button>
            <el-button type="text" data-track-text="打开收藏文献原文" aria-label="打开收藏文献原文" @click="openExternal(item.work.pdfUrl || item.work.landingPageUrl || doiUrl(item.work) || firstProviderUrl(item.work))">原文</el-button>
            <el-button type="text" data-track-text="复制收藏文献笔记" aria-label="复制收藏文献笔记" @click="copySavedWorkNote(item)">复制笔记</el-button>
            <el-button type="text" data-track-text="编辑收藏文献" aria-label="编辑收藏文献" @click="openSaveDialog(item.work, item)">编辑</el-button>
            <el-button type="text" data-track-text="移除收藏文献" aria-label="移除收藏文献" @click="toggleSaved(item.work)">移除</el-button>
          </div>
        </div>
      </div>
      <el-empty v-else :description="savedWorks.length ? '没有匹配的收藏文献' : '暂无收藏'"></el-empty>
    </el-dialog>

    <el-dialog class="scholar-saved-search-dialog" :visible.sync="savedSearchVisible" width="760px" title="我的检索式">
      <div v-loading="savedSearchLoading" class="scholar-saved-search-list">
        <div
          v-for="item in savedSearches"
          :key="item.searchKey"
          class="scholar-saved-search-item"
        >
          <div>
            <strong>{{ item.name }}</strong>
            <p>{{ savedSearchMeta(item) }}</p>
            <div v-if="savedSearchTags(item).length" class="scholar-saved-search-tags">
              <span v-for="tag in savedSearchTags(item)" :key="tag">{{ tag }}</span>
            </div>
            <p v-if="item.note" class="scholar-saved-note">{{ item.note }}</p>
          </div>
          <div class="scholar-saved-actions">
            <el-button type="text" data-track-text="应用保存的文献检索式" aria-label="应用保存的文献检索式" @click="applySavedSearch(item)">检索</el-button>
            <el-button type="text" data-track-text="复制保存的文献检索链接" aria-label="复制保存的文献检索链接" @click="copySavedSearchLink(item)">复制链接</el-button>
            <el-button type="text" data-track-text="复制保存的文献检索策略" aria-label="复制保存的文献检索策略" @click="copySavedSearchStrategy(item)">复制策略</el-button>
            <el-button type="text" data-track-text="删除保存的文献检索式" aria-label="删除保存的文献检索式" @click="removeSavedSearch(item)">删除</el-button>
          </div>
        </div>
        <el-empty v-if="!savedSearchLoading && !savedSearches.length" description="暂无保存的检索式"></el-empty>
      </div>
    </el-dialog>

    <el-dialog
      class="scholar-save-dialog"
      :visible.sync="saveSearchDialogVisible"
      width="520px"
      title="保存当前检索"
    >
      <div class="scholar-save-form">
        <label>名称</label>
        <el-input
          v-model="saveSearchForm.name"
          maxlength="80"
          show-word-limit
          placeholder="例如：CAD 近五年 OA 综述"
        ></el-input>
        <label>备注</label>
        <el-input
          v-model="saveSearchForm.note"
          type="textarea"
          :rows="4"
          maxlength="300"
          show-word-limit
          resize="none"
          placeholder="记录这个检索式的用途、纳入标准或后续阅读计划"
        ></el-input>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button data-track-text="取消保存文献检索式" aria-label="取消保存文献检索式" @click="saveSearchDialogVisible = false">取消</el-button>
        <el-button type="primary" data-track-text="确认保存文献检索式" aria-label="确认保存文献检索式" :loading="saveSearchLoading" @click="submitSaveSearchDialog">
          保存
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      class="scholar-save-dialog"
      :visible.sync="saveDialogVisible"
      width="560px"
      :title="saveDialogTitle"
    >
      <div v-if="saveDialogWork" class="scholar-save-form">
        <div class="scholar-save-work-title">{{ saveDialogWork.title || 'Untitled work' }}</div>
        <label>标签</label>
        <el-select
          v-model="saveForm.tags"
          class="scholar-save-tags-input"
          multiple
          filterable
          allow-create
          default-first-option
          placeholder="输入或选择标签"
        >
          <el-option
            v-for="tag in savedTagOptions"
            :key="tag"
            :label="tag"
            :value="tag"
          ></el-option>
        </el-select>
        <label>备注</label>
        <el-input
          v-model="saveForm.note"
          type="textarea"
          :rows="4"
          maxlength="500"
          show-word-limit
          resize="none"
          placeholder="记录阅读目的、实验结论、是否需要精读等"
        ></el-input>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button data-track-text="取消保存文献收藏备注" aria-label="取消保存文献收藏备注" @click="saveDialogVisible = false">取消</el-button>
        <el-button type="primary" data-track-text="确认保存文献收藏备注" aria-label="确认保存文献收藏备注" :loading="saveDialogLoading" @click="submitSaveDialog">
          保存
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      class="scholar-history-dialog"
      :visible.sync="searchHistoryVisible"
      width="620px"
      title="最近检索"
    >
      <div v-loading="searchHistoryLoading" class="scholar-history-list">
        <div
          v-for="item in searchHistory"
          :key="`${item.query}-${item.createdAt}`"
          class="scholar-history-item"
        >
          <button type="button" class="scholar-history-main" data-track-text="应用最近文献检索" aria-label="应用最近文献检索" @click="applySearchHistory(item)">
            <span>{{ item.query }}</span>
            <em>{{ historyMetaText(item) }}</em>
          </button>
          <el-button class="scholar-history-copy" type="text" data-track-text="复制最近文献检索策略" aria-label="复制最近文献检索策略" @click="copySearchHistoryStrategy(item)">
            复制策略
          </el-button>
          <el-button class="scholar-history-copy" type="text" data-track-text="复制最近文献检索链接" aria-label="复制最近文献检索链接" @click="copySearchHistoryLink(item)">
            复制链接
          </el-button>
        </div>
        <el-empty v-if="!searchHistoryLoading && !searchHistory.length" description="暂无最近检索"></el-empty>
      </div>
    </el-dialog>

    <el-dialog class="scholar-citation-dialog" :visible.sync="citationVisible" width="760px" title="引用格式">
      <div v-if="citationWork" class="scholar-citation">
        <div class="scholar-citation-title">{{ citationWork.title || 'Untitled work' }}</div>
        <el-radio-group v-model="citationFormat" size="small" class="scholar-citation-tabs">
          <el-radio-button
            v-for="item in citationFormats"
            :key="item.value"
            :label="item.value"
          >
            {{ item.label }}
          </el-radio-button>
        </el-radio-group>
        <pre class="scholar-citation-text">{{ citationText }}</pre>
        <p class="scholar-citation-note">引用格式基于三方元数据生成，投稿前请核对作者、期刊、卷期和页码。</p>
        <div class="scholar-citation-actions">
          <el-button type="primary" data-track-text="复制文献引用格式" aria-label="复制文献引用格式" @click="copyCitation">
            <i class="el-icon-document-copy"></i>
            复制
          </el-button>
          <el-button data-track-text="导出文献引用格式" aria-label="导出文献引用格式" @click="downloadSingleCitation">
            <i class="el-icon-download"></i>
            导出
          </el-button>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      class="scholar-rag-dialog"
      :visible.sync="ragImportVisible"
      width="720px"
      title="导入 RAG"
      :close-on-click-modal="!ragImportLoading"
      :close-on-press-escape="!ragImportLoading"
    >
      <div class="scholar-rag-body">
        <div class="scholar-rag-summary">
          <div>
            <strong>{{ ragImportWorks.length }}</strong>
            <span>篇文献</span>
          </div>
          <div>
            <strong>{{ ragImportPdfCount }}</strong>
            <span>个可下载 PDF</span>
          </div>
        </div>
        <p class="scholar-rag-note">
          系统会先写入文献元数据 Markdown；勾选 PDF 时，会通过本站文献下载代理获取可下载 PDF，并提交给 RAG 知识库解析。
        </p>
        <el-form label-width="108px" class="scholar-rag-form">
          <el-form-item label="目标知识库">
            <el-radio-group v-model="ragImportTargetMode" size="small">
              <el-radio-button label="existing" :disabled="!ragKnowledgeBases.length">已有知识库</el-radio-button>
              <el-radio-button label="new">新建知识库</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="ragImportTargetMode === 'existing'" label="选择知识库">
            <el-select
              v-model="ragImportKnowledgeBaseId"
              class="scholar-rag-select"
              filterable
              :loading="ragKnowledgeBaseLoading"
              placeholder="请选择知识库"
            >
              <el-option
                v-for="item in ragKnowledgeBases"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              >
                <span>{{ item.name }}</span>
                <em>{{ item.documentCount || item.knowledgeCount || 0 }} 个文档</em>
              </el-option>
            </el-select>
          </el-form-item>
          <template v-else>
            <el-form-item label="知识库名称">
              <el-input v-model="ragImportNewName" maxlength="60" show-word-limit />
            </el-form-item>
            <el-form-item label="描述">
              <el-input
                v-model="ragImportNewDescription"
                type="textarea"
                :rows="2"
                maxlength="180"
                show-word-limit
              />
            </el-form-item>
          </template>
          <el-form-item label="导入内容">
            <el-checkbox v-model="ragImportIncludePdf" :disabled="!ragImportPdfCount">
              同步导入可下载 PDF
            </el-checkbox>
          </el-form-item>
        </el-form>
        <div v-if="ragImportLoading" class="scholar-rag-progress">
          <el-progress :percentage="ragImportPercent" :show-text="false" />
          <span>{{ ragImportProgress.message || '正在提交到 RAG...' }}</span>
        </div>
        <div v-if="ragImportWorks.length" class="scholar-rag-preview">
          <div v-for="work in ragImportPreviewWorks" :key="workSelectionKey(work)">
            <span>{{ work.title || work.doi || firstProviderId(work) || 'Untitled work' }}</span>
            <em v-if="work.pdfUrl">PDF</em>
          </div>
          <p v-if="ragImportWorks.length > ragImportPreviewWorks.length">
            另有 {{ ragImportWorks.length - ragImportPreviewWorks.length }} 篇将在提交时一并导入
          </p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button data-track-text="取消导入文献到RAG" aria-label="取消导入文献到RAG" :disabled="ragImportLoading" @click="ragImportVisible = false">取消</el-button>
        <el-button type="primary" data-track-text="确认导入文献到RAG" aria-label="确认导入文献到RAG" :loading="ragImportLoading" @click="submitRagImport">
          开始导入
        </el-button>
      </div>
    </el-dialog>

    <el-dialog
      class="scholar-bulk-dialog"
      :visible.sync="bulkImportVisible"
      width="680px"
      title="批量导入文献"
    >
      <div class="scholar-bulk-body">
        <p class="scholar-bulk-note">支持 DOI、PMID、PMCID、OpenAlex Work ID，每行一个，最多识别前 50 条。</p>
        <el-input
          v-model="bulkImportText"
          type="textarea"
          :rows="9"
          resize="none"
          placeholder="例如：&#10;10.7717/peerj.4375&#10;PMID:29456894&#10;W2741809807"
        ></el-input>
        <div v-if="bulkImportSummary" class="scholar-bulk-summary">
          <span>输入 {{ bulkImportSummary.inputCount || 0 }} 条</span>
          <span>识别 {{ bulkImportSummary.recognizedCount || 0 }} 条</span>
          <span>返回 {{ bulkImportSummary.foundCount || 0 }} 篇</span>
          <span v-if="bulkImportSummary.unsupported && bulkImportSummary.unsupported.length">
            未识别 {{ bulkImportSummary.unsupported.length }} 条
          </span>
          <span v-if="bulkImportSummary.notFound && bulkImportSummary.notFound.length">
            未查到 {{ bulkImportSummary.notFound.length }} 条
          </span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button data-track-text="取消批量导入文献" aria-label="取消批量导入文献" @click="bulkImportVisible = false">取消</el-button>
        <el-button type="primary" data-track-text="确认批量导入文献" aria-label="确认批量导入文献" :loading="bulkImportLoading" @click="submitBulkImport">
          导入
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { Base64 } from 'js-base64'
import { Autocomplete } from 'element-ui'
import { authServiceClient, translateServiceClient } from '@/tools/api/service'
import {
  outRequest,
  userPowerRequest,
  userStatusRequest
} from '@/tools/api/auth-api'
import {
  checkRoamingRequest
} from '@/tools/api/translate-api'
import {
  bulkImportScholarWorks,
  deleteSavedScholarWork,
  downloadScholarWorkPdf,
  getScholarFacets,
  getScholarWork,
  listScholarSearchHistory,
  deleteSavedScholarSearch,
  listSavedScholarSearches,
  listSavedScholarWorks,
  resolveScholarIdentifier,
  saveScholarSearch,
  saveScholarWork,
  searchScholar,
  suggestScholar
} from '../tools/api/scholar-api'
import {
  ragServiceClient,
  RagBootstrapRequest,
  RagKnowledgeBaseCreateRequest,
  RagKnowledgeBasePageRequest,
  RagKnowledgeBaseUploadRequest
} from '../tools/api/rag-api'
import {
  formatCitation,
  formatWorks
} from '../tools/scholar-citation'
import { openExternalWithConfirm } from '../tools/external-link'
import { openScholarRouteInNewTab } from '../tools/scholar-route'
import ScholarHeader from '../components/ScholarHeader.vue'
import ScholarFooter from '../components/ScholarFooter.vue'

export default {
  name: 'ScholarSearch',
  components: {
    ElAutocomplete: Autocomplete,
    ScholarHeader,
    ScholarFooter
  },
  data() {
    return {
      keyword: '',
      searchMode: 'keyword',
      searchExecuted: false,
      advancedSearchVisible: false,
      advancedForm: {
        searchMode: 'keyword',
        keyword: '',
        exactPhrase: '',
        authorName: '',
        authorId: '',
        authorSelectedName: '',
        institutionName: '',
        institutionId: '',
        institutionSelectedName: '',
        sourceName: '',
        sourceId: '',
        sourceSelectedName: '',
        topicName: '',
        topicId: '',
        topicSelectedName: '',
        fromYear: '',
        toYear: '',
        minCitations: '',
        maxCitations: '',
        type: '',
        oaStatus: '',
        impactLevel: '',
        sort: 'relevance',
        oaOnly: false,
        hasPdf: false,
        hasFulltext: false,
        hasDoi: false,
        hasAbstract: false,
        hasReferences: false,
        hasPmid: false
      },
      loading: false,
      loadingMore: false,
      bulkImportVisible: false,
      bulkImportLoading: false,
      bulkImportText: '',
      bulkImportSummary: null,
      bulkImportActive: false,
      resultsMoreVisible: false,
      exportPanelVisible: false,
      batchMoreVisible: false,
      batchScreeningVisible: false,
      savedExportVisible: false,
      savedOrganizerVisible: false,
      activeCardActionKey: '',
      quotaExhausted: false,
      filtersOpen: false,
      mobileFacetsOpen: false,
      filters: {
        oaOnly: false,
        hasPdf: false,
        hasFulltext: false,
        hasDoi: false,
        hasAbstract: false,
        hasReferences: false,
        hasPmid: false,
        excludeRetracted: false,
        excludeParatext: false,
        fromYear: '',
        toYear: '',
        type: '',
        language: '',
        countryCode: '',
        oaStatus: '',
        openVersion: '',
        license: '',
        sourceType: '',
        indexedIn: '',
        impactLevel: '',
        minCitations: '',
        maxCitations: '',
        authorName: '',
        authorId: '',
        authorSelectedName: '',
        institutionName: '',
        institutionId: '',
        institutionSelectedName: '',
        sourceName: '',
        sourceId: '',
        sourceSelectedName: '',
        topicName: '',
        topicId: '',
        topicSelectedName: '',
        keywordName: '',
        keywordId: '',
        keywordSelectedName: '',
        funderName: '',
        funderId: '',
        funderSelectedName: '',
        sdgId: '',
        sdgName: '',
        domainId: '',
        domainName: '',
        fieldId: '',
        fieldName: '',
        subfieldId: '',
        subfieldName: '',
        publisherId: '',
        publisherName: '',
        citesWorkId: '',
        citesWorkName: '',
        sort: 'relevance'
      },
      searchModeOptions: [
        {
          label: '关键词检索',
          shortLabel: '关键词',
          value: 'keyword',
          description: '适合论文题名、作者、主题词等常规检索',
          apiParam: 'OpenAlex search',
          strategy: '会把输入作为普通关键词发送到三方检索接口，优先返回相关性较高的文献。'
        },
        {
          label: '精确匹配',
          shortLabel: '精确',
          value: 'exact',
          description: '适合完整标题、DOI、固定短语',
          apiParam: 'OpenAlex search + exact intent',
          strategy: '用于 DOI、完整题名或固定短语场景，前端会保留精确检索意图供后端参数映射。'
        },
        {
          label: '语义检索',
          shortLabel: '语义',
          value: 'semantic',
          description: '适合输入研究问题、摘要或自然语言描述',
          apiParam: 'OpenAlex search + semantic intent',
          strategy: '适合自然语言问题或研究方向描述，后端可按 provider 能力映射到语义/概念检索。'
        }
      ],
      sortQuickOptions: [
        { label: '相关性', value: 'relevance', hint: '按三方接口相关性排序' },
        { label: '高引用', value: 'cited', hint: '优先查看引用数较高的文献' },
        { label: '最新', value: 'newest', hint: '优先查看近年发表文献' },
        { label: '影响力', value: 'impact', hint: '优先查看同领域影响力较高的文献' }
      ],
      impactLevelOptions: [
        { label: '同领域 Top 1%', value: 'top1' },
        { label: '同领域 Top 10%', value: 'top10' },
        { label: 'FWCI > 1', value: 'fwci_gt_1' }
      ],
      qualityFilterOptions: [
        { label: '有 DOI', value: 'hasDoi' },
        { label: '有摘要', value: 'hasAbstract' },
        { label: '有参考文献', value: 'hasReferences' },
        { label: 'PubMed 收录', value: 'hasPmid' }
      ],
      citationFormats: [
        { label: 'GB/T 7714', value: 'gbt7714' },
        { label: 'APA', value: 'apa' },
        { label: 'MLA', value: 'mla' },
        { label: 'BibTeX', value: 'bibtex' },
        { label: 'RIS', value: 'ris' }
      ],
      typeOptions: [
        { label: '论文', value: 'article' },
        { label: '预印本', value: 'preprint' },
        { label: '图书章节', value: 'book-chapter' },
        { label: '书籍', value: 'book' },
        { label: '会议论文', value: 'proceedings-article' }
      ],
      oaStatusOptions: [
        { label: '金色 OA', value: 'gold' },
        { label: '绿色 OA', value: 'green' },
        { label: '青铜 OA', value: 'bronze' },
        { label: '混合 OA', value: 'hybrid' },
        { label: '钻石 OA', value: 'diamond' },
        { label: '非开放', value: 'closed' }
      ],
      languageOptions: [
        { label: '英语', value: 'en' },
        { label: '中文', value: 'zh' },
        { label: '西班牙语', value: 'es' },
        { label: '法语', value: 'fr' },
        { label: '德语', value: 'de' },
        { label: '日语', value: 'ja' },
        { label: '韩语', value: 'ko' },
        { label: '葡萄牙语', value: 'pt' },
        { label: '俄语', value: 'ru' }
      ],
      countryOptions: [
        { label: '中国', value: 'CN' },
        { label: '美国', value: 'US' },
        { label: '英国', value: 'GB' },
        { label: '德国', value: 'DE' },
        { label: '法国', value: 'FR' },
        { label: '日本', value: 'JP' },
        { label: '加拿大', value: 'CA' },
        { label: '澳大利亚', value: 'AU' },
        { label: '印度', value: 'IN' },
        { label: '韩国', value: 'KR' }
      ],
      total: 0,
      page: 1,
      size: 20,
      pageSizes: [10, 20, 50, 100],
      maxPageableRecords: 10000,
      nextCursor: '',
      facetsLoading: false,
      facetsRequestId: 0,
      cacheHit: false,
      costUsd: null,
      providers: [],
      facets: {
        years: [],
        types: [],
        oaStatuses: [],
        topics: [],
        institutionCountries: [],
        sources: [],
        institutions: [],
        languages: [],
        openVersions: [],
        licenses: [],
        sourceTypes: [],
        indexedIn: [],
        funders: [],
        sdgs: [],
        domains: [],
        fields: [],
        subfields: [],
        publishers: []
      },
      facetPreviewLimit: 8,
      collapsedFacets: {
        years: false,
        oaStatuses: false,
        types: false,
        topics: true,
        institutionCountries: true,
        sources: true,
        institutions: true,
        languages: true,
        openVersions: true,
        licenses: true,
        sourceTypes: true,
        indexedIn: true,
        funders: true,
        sdgs: true,
        domains: true,
        fields: true,
        subfields: true,
        publishers: true
      },
      expandedFacets: {},
      facetSearchKeywords: {
        years: '',
        oaStatuses: '',
        types: '',
        topics: '',
        institutionCountries: '',
        sources: '',
        institutions: '',
        languages: '',
        openVersions: '',
        licenses: '',
        sourceTypes: '',
        indexedIn: '',
        funders: '',
        sdgs: '',
        domains: '',
        fields: '',
        subfields: '',
        publishers: ''
      },
      results: [],
      selectedWorks: [],
      currentRagScopeId: '',
      ragKnowledgeBases: [],
      ragKnowledgeBaseLoading: false,
      ragImportVisible: false,
      ragImportLoading: false,
      ragImportTargetMode: 'existing',
      ragImportKnowledgeBaseId: '',
      ragImportNewName: '',
      ragImportNewDescription: '',
      ragImportWorks: [],
      ragImportIncludePdf: true,
      ragImportProgress: {
        total: 0,
        done: 0,
        failed: 0,
        pdfDone: 0,
        pdfFailed: 0,
        message: ''
      },
      screeningMarks: {},
      savingSelected: false,
      exportingTopResults: false,
      batchPdfDownloading: false,
      pdfDownloadingKeys: {},
      expandedWorkKeys: {},
      expandedWorkDetails: {},
      expandedWorkLoading: {},
      savedWorks: [],
      savedKeyword: '',
      savedTagFilter: '',
      savedSearches: [],
      savedSearchVisible: false,
      savedSearchLoading: false,
      saveSearchDialogVisible: false,
      saveSearchLoading: false,
      saveSearchForm: {
        name: '',
        note: ''
      },
      searchHistoryVisible: false,
      searchHistoryLoading: false,
      searchHistory: [],
      searchHistoryLoaded: false,
      listDensity: 'standard',
      pageKeyword: '',
      screeningView: 'all',
      screeningViewOptions: [
        { label: '全部结果', value: 'all' },
        { label: '未初筛', value: 'unmarked' },
        { label: '已标记', value: 'marked' },
        { label: '纳入', value: 'include' },
        { label: '可能相关', value: 'maybe' },
        { label: '待定', value: 'pending' },
        { label: '排除', value: 'exclude' }
      ],
      selectedWork: null,
      detailVisible: false,
      compareVisible: false,
      savedVisible: false,
      saveDialogVisible: false,
      saveDialogLoading: false,
      saveDialogWork: null,
      saveDialogExisting: null,
      saveForm: {
        tags: ['OA文献'],
        note: ''
      },
      citationVisible: false,
      citationWork: null,
      citationFormat: 'gbt7714',
      errorMessage: '',
      userInfo: '',
      groupUserInfo: '',
      groupUserInfos: [],
      showloginOut: false,
      showRoaming: false,
      qrcodeUrl: '',
      userId: '',
      userType: '',
      hasH5: false,
      isShowInRoaming: false,
      isRoamingTimer: undefined,
      isRoamingTimerOut: undefined,
      syncingRoute: false
    }
  },
  computed: {
    currentSearchModeOption() {
      return this.searchModeOptions.find(item => item.value === this.searchMode) || this.searchModeOptions[0] || {}
    },
    searchModeTitle() {
      const option = this.currentSearchModeOption
      return [option.label, option.description, option.apiParam].filter(Boolean).join(' - ')
    },
    searchPlaceholder() {
      if (this.searchMode === 'semantic') {
        return '粘贴研究方向、摘要或一段问题描述，系统会按语义匹配文献'
      }
      if (this.searchMode === 'exact') {
        return '输入完整标题、DOI、OpenAlex ID 或精确短语'
      }
      return '输入论文标题、关键词、DOI，例如 deep learning transformer'
    },
    highlightKeywords() {
      const raw = String(this.keyword || '').trim()
      if (!raw || this.isLikelyIdentifier(raw)) {
        return []
      }
      const values = raw
        .replace(/[“”"'()[\]{}<>]/g, ' ')
        .split(/[\s,，;；:：|/\\]+/)
        .map(item => item.trim())
        .filter(item => item.length >= 2 && item.length <= 40)
      return Array.from(new Set(values)).sort((a, b) => b.length - a.length).slice(0, 8)
    },
    stats() {
      const oaCount = this.results.filter(item => item.isOa).length
      const citedWorks = this.results.filter(item => typeof item.citedByCount === 'number')
      const citedTotal = citedWorks.reduce((sum, item) => sum + item.citedByCount, 0)
      return {
        oaCount,
        avgCitations: citedWorks.length ? Math.round(citedTotal / citedWorks.length) : 0
      }
    },
    saveDialogTitle() {
      return this.saveDialogExisting ? '编辑收藏' : '收藏文献'
    },
    savedTagOptions() {
      const tags = new Set(['OA文献', '待读', '精读', '已引用'])
      ;(this.savedWorks || []).forEach(item => {
        ;(item.tags || []).forEach(tag => {
          const value = this.cleanTag(tag)
          if (value) {
            tags.add(value)
          }
        })
      })
      ;(this.saveForm.tags || []).forEach(tag => {
        const value = this.cleanTag(tag)
        if (value) {
          tags.add(value)
        }
      })
      return Array.from(tags)
    },
    savedFilterTagOptions() {
      const tags = new Set()
      ;(this.savedWorks || []).forEach(item => {
        ;(item.tags || []).forEach(tag => {
          const value = this.cleanTag(tag)
          if (value) {
            tags.add(value)
          }
        })
      })
      return Array.from(tags)
    },
    filteredSavedWorks() {
      const keyword = this.cleanText(this.savedKeyword).toLowerCase()
      const tagFilter = this.cleanTag(this.savedTagFilter)
      return (this.savedWorks || []).filter(item => {
        const work = item.work || {}
        if (tagFilter && !(item.tags || []).some(tag => this.cleanTag(tag) === tagFilter)) {
          return false
        }
        if (!keyword) {
          return true
        }
        const authors = (work.authors || []).map(author => author && author.name).filter(Boolean).join(' ')
        const haystack = [
          work.title,
          work.sourceName,
          work.publicationYear,
          work.doi,
          authors,
          item.note,
          ...(item.tags || [])
        ].filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(keyword)
      })
    },
    allYearBuckets() {
      return (this.facets.years || [])
        .slice()
        .sort((a, b) => Number(b.key) - Number(a.key))
    },
    allTypeBuckets() {
      return this.facets.types || []
    },
    facetSections() {
      return [
        { key: 'years', title: '年份分布', items: this.allYearBuckets },
        { key: 'oaStatuses', title: '开放获取状态', items: this.facets.oaStatuses || [] },
        { key: 'types', title: '文献类型', items: this.allTypeBuckets },
        { key: 'domains', title: '研究领域', items: this.facets.domains || [] },
        { key: 'fields', title: '学科方向', items: this.facets.fields || [] },
        { key: 'subfields', title: '细分学科', items: this.facets.subfields || [] },
        { key: 'topics', title: '主题聚类', items: this.facets.topics || [] },
        { key: 'sdgs', title: '可持续发展目标', items: this.facets.sdgs || [] },
        { key: 'institutionCountries', title: '机构国家/地区', items: this.facets.institutionCountries || [] },
        { key: 'sources', title: '期刊/来源', items: this.facets.sources || [] },
        { key: 'publishers', title: '出版商/来源机构', items: this.facets.publishers || [] },
        { key: 'institutions', title: '机构', items: this.facets.institutions || [] },
        { key: 'funders', title: '基金机构', items: this.facets.funders || [] },
        { key: 'languages', title: '语言', items: this.facets.languages || [] },
        { key: 'indexedIn', title: '收录数据库', items: this.facets.indexedIn || [] },
        { key: 'openVersions', title: '开放全文版本', items: this.facets.openVersions || [] },
        { key: 'licenses', title: '开放许可证', items: this.facets.licenses || [] },
        { key: 'sourceTypes', title: '来源类型', items: this.facets.sourceTypes || [] }
      ]
    },
    hasFacetData() {
      return this.facetSections.some(section => section.items && section.items.length)
    },
    maxYearCount() {
      return this.allYearBuckets.reduce((max, item) => Math.max(max, item.count || 0), 1)
    },
    qualityFilters: {
      get() {
        return this.qualityFilterOptions
          .filter(item => this.filters[item.value])
          .map(item => item.value)
      },
      set(values) {
        const selected = new Set(values || [])
        this.qualityFilterOptions.forEach(item => {
          this.filters[item.value] = selected.has(item.value)
        })
      }
    },
    activeFilterTags() {
      const tags = []
      if (this.searchMode !== 'keyword') {
        tags.push({ key: 'searchMode', label: `检索模式：${this.searchModeText(this.searchMode)}` })
      }
      if (this.filters.oaOnly) {
        tags.push({ key: 'oaOnly', label: '仅开放获取' })
      }
      if (this.filters.hasPdf) {
        tags.push({ key: 'hasPdf', label: '有 PDF' })
      }
      if (this.filters.hasFulltext) {
        tags.push({ key: 'hasFulltext', label: '可全文检索' })
      }
      this.qualityFilterOptions.forEach(item => {
        if (this.filters[item.value]) {
          tags.push({ key: item.value, label: item.label })
        }
      })
      if (this.filters.excludeRetracted) {
        tags.push({ key: 'excludeRetracted', label: '排除撤稿' })
      }
      if (this.filters.excludeParatext) {
        tags.push({ key: 'excludeParatext', label: '排除非正文' })
      }
      if (this.filters.fromYear && this.filters.toYear && String(this.filters.fromYear) === String(this.filters.toYear)) {
        tags.push({ key: 'years', label: `年份：${this.filters.fromYear}` })
      } else if (this.filters.fromYear || this.filters.toYear) {
        tags.push({ key: 'years', label: `年份：${this.filters.fromYear || '不限'} - ${this.filters.toYear || '不限'}` })
      }
      if (this.filters.minCitations || this.filters.maxCitations) {
        tags.push({ key: 'citations', label: `引用：${this.filters.minCitations || '不限'} - ${this.filters.maxCitations || '不限'}` })
      }
      if (this.filters.oaStatus) {
        tags.push({ key: 'oaStatuses', label: `开放获取：${this.oaStatusText(this.filters.oaStatus)}` })
      }
      if (this.filters.openVersion) {
        tags.push({ key: 'openVersions', label: `开放版本：${this.openVersionText(this.filters.openVersion)}` })
      }
      if (this.filters.license) {
        tags.push({ key: 'licenses', label: `许可证：${this.licenseText(this.filters.license)}` })
      }
      if (this.filters.sourceType) {
        tags.push({ key: 'sourceTypes', label: `来源类型：${this.sourceTypeText(this.filters.sourceType)}` })
      }
      if (this.filters.indexedIn) {
        tags.push({ key: 'indexedIn', label: `收录：${this.indexedInText(this.filters.indexedIn)}` })
      }
      if (this.filters.impactLevel) {
        tags.push({ key: 'impactLevel', label: `影响力：${this.impactLevelText(this.filters.impactLevel)}` })
      }
      if (this.filters.type) {
        tags.push({ key: 'types', label: `文献类型：${this.formatType(this.filters.type)}` })
      }
      if (this.filters.authorId) {
        tags.push({ key: 'authors', label: `作者：${this.filters.authorName || this.filters.authorId}` })
      }
      if (this.filters.topicId) {
        tags.push({ key: 'topics', label: `主题：${this.filters.topicName || this.facetLabelByValue('topics', this.filters.topicId) || this.filters.topicId}` })
      }
      if (this.filters.countryCode) {
        tags.push({ key: 'institutionCountries', label: `国家/地区：${this.countryText(this.filters.countryCode, this.filters.countryCode)}` })
      }
      if (this.filters.sourceId) {
        tags.push({ key: 'sources', label: `来源：${this.filters.sourceName || this.facetLabelByValue('sources', this.filters.sourceId) || this.filters.sourceId}` })
      }
      if (this.filters.institutionId) {
        tags.push({ key: 'institutions', label: `机构：${this.filters.institutionName || this.facetLabelByValue('institutions', this.filters.institutionId) || this.filters.institutionId}` })
      }
      if (this.filters.keywordId) {
        tags.push({ key: 'keywords', label: `关键词：${this.filters.keywordName || this.filters.keywordId}` })
      }
      if (this.filters.funderId) {
        tags.push({ key: 'funders', label: `基金机构：${this.filters.funderName || this.facetLabelByValue('funders', this.filters.funderId) || this.filters.funderId}` })
      }
      if (this.filters.sdgId) {
        tags.push({ key: 'sdgs', label: `SDG：${this.filters.sdgName || this.facetLabelByValue('sdgs', this.filters.sdgId) || this.filters.sdgId}` })
      }
      if (this.filters.domainId) {
        tags.push({ key: 'domains', label: `领域：${this.filters.domainName || this.facetLabelByValue('domains', this.filters.domainId) || this.filters.domainId}` })
      }
      if (this.filters.fieldId) {
        tags.push({ key: 'fields', label: `学科：${this.filters.fieldName || this.facetLabelByValue('fields', this.filters.fieldId) || this.filters.fieldId}` })
      }
      if (this.filters.subfieldId) {
        tags.push({ key: 'subfields', label: `子学科：${this.filters.subfieldName || this.facetLabelByValue('subfields', this.filters.subfieldId) || this.filters.subfieldId}` })
      }
      if (this.filters.publisherId) {
        tags.push({ key: 'publishers', label: `出版商：${this.filters.publisherName || this.facetLabelByValue('publishers', this.filters.publisherId) || this.filters.publisherId}` })
      }
      if (this.filters.citesWorkId) {
        tags.push({ key: 'citesWork', label: `引用文献：${this.filters.citesWorkName || this.filters.citesWorkId}` })
      }
      if (this.filters.language) {
        tags.push({ key: 'languages', label: `语言：${this.languageText(this.filters.language)}` })
      }
      return tags
    },
    resultTitle() {
      if (this.bulkImportActive) {
        return `批量导入 ${this.formatNumber(this.total)} 篇文献`
      }
      if (!this.results.length && !this.total) {
        return '检索结果'
      }
      return `找到 ${this.formatNumber(this.total)} 条结果`
    },
    hasSearchIntent() {
      return this.searchExecuted || this.bulkImportActive
    },
    emptyStateTitle() {
      if (this.quotaExhausted) {
        return '今日用量完毕，明天再来吧'
      }
      if (!this.hasSearchIntent) {
        return '输入关键词后开始检索开放获取文献'
      }
      if (this.bulkImportActive) {
        return '批量导入没有返回文献'
      }
      return '没有找到匹配的文献'
    },
    emptyStateDescription() {
      if (this.quotaExhausted) {
        return '今日额度已全部用完，系统会在额度恢复后自动继续服务。'
      }
      if (!this.hasSearchIntent) {
        return '可以尝试 “transformer”、“protein structure prediction” 或 DOI。'
      }
      if (this.bulkImportActive) {
        return '请检查 DOI、PMID、PMCID 或 OpenAlex ID 是否正确，或减少一次导入数量后重试。'
      }
      if (this.activeFilterTags.length) {
        return '尝试减少筛选条件、换用更宽泛关键词，或检查 DOI/标题拼写。'
      }
      return '尝试换用更宽泛关键词，或检查 DOI、标题、作者名称是否准确。'
    },
    emptyStateActions() {
      if (this.quotaExhausted) {
        return []
      }
      if (!this.hasSearchIntent) {
        return []
      }
      if (this.bulkImportActive) {
        return [
          { key: 'retryBulkImport', label: '重新导入', type: 'primary' },
          { key: 'clearKeyword', label: '清空结果' }
        ]
      }
      const actions = []
      const hasKeyword = !!(this.keyword && this.keyword.trim())
      const hasFilters = this.hasActiveFilters()
      if (hasFilters) {
        actions.push({
          key: 'clearFilters',
          label: hasKeyword ? '仅保留关键词检索' : '清除筛选',
          type: 'primary'
        })
      }
      if (hasKeyword && this.searchMode !== 'keyword') {
        actions.push({ key: 'keywordMode', label: '改为关键词检索' })
      }
      if (hasKeyword) {
        actions.push({ key: 'clearKeyword', label: '清空关键词' })
      }
      return actions
    },
    paginationTotal() {
      return Math.min(Number(this.total) || 0, this.maxPageableRecords)
    },
    totalPages() {
      const size = Number(this.size) || 20
      return Math.max(1, Math.ceil(this.paginationTotal / size))
    },
    maxPageablePages() {
      const size = Number(this.size) || 20
      return Math.max(1, Math.floor(this.maxPageableRecords / size))
    },
    showTopPager() {
      return this.paginationTotal > (Number(this.size) || 20)
    },
    canGoPreviousPage() {
      return Number(this.page) > 1
    },
    canGoNextPage() {
      return Number(this.page) < this.totalPages
    },
    screeningStats() {
      const stats = {
        all: (this.results || []).length,
        unmarked: 0,
        marked: 0,
        include: 0,
        maybe: 0,
        pending: 0,
        exclude: 0
      }
      ;(this.results || []).forEach(work => {
        const mark = this.screeningMark(work)
        if (!mark) {
          stats.unmarked += 1
          return
        }
        stats.marked += 1
        if (stats[mark.decision] !== undefined) {
          stats[mark.decision] += 1
        } else {
          stats.pending += 1
        }
      })
      return stats
    },
    screeningStatChips() {
      return this.screeningViewOptions.map(item => ({
        ...item,
        count: this.screeningStats[item.value] || 0
      }))
    },
    screeningMarkCount() {
      return Object.keys(this.screeningMarks || {}).length
    },
    normalizedPageKeyword() {
      return String(this.pageKeyword || '').trim().toLowerCase()
    },
    screeningVisibleResults() {
      if (this.screeningView === 'all') {
        return this.results
      }
      return (this.results || []).filter(work => {
        const mark = this.screeningMark(work)
        if (this.screeningView === 'unmarked') {
          return !mark
        }
        if (this.screeningView === 'marked') {
          return !!mark
        }
        return !!mark && mark.decision === this.screeningView
      })
    },
    visibleResults() {
      const base = this.screeningVisibleResults || []
      const keyword = this.normalizedPageKeyword
      if (!keyword) {
        return base
      }
      return base.filter(work => this.currentPageFilterText(work).includes(keyword))
    },
    pageFilterSummary() {
      if (!this.normalizedPageKeyword) {
        return ''
      }
      return `${this.visibleResults.length}/${this.screeningVisibleResults.length}`
    },
    filteredEmptyTitle() {
      return this.pageKeyword ? '当前页没有匹配页内过滤的文献' : '当前页没有匹配初筛状态的文献'
    },
    filteredEmptyDescription() {
      return this.pageKeyword ? '清除页内过滤，或翻页查看其它结果。' : '切换初筛视图，或继续翻页查看其它结果。'
    },
    selectedWorkKeys() {
      return this.selectedWorks.map(work => this.workSelectionKey(work))
    },
    allCurrentPageSelected() {
      return !!this.visibleResults.length && this.visibleResults.every(work => this.selectedWorkKeys.includes(this.workSelectionKey(work)))
    },
    partiallyCurrentPageSelected() {
      const selectedCount = this.visibleResults.filter(work => this.selectedWorkKeys.includes(this.workSelectionKey(work))).length
      return selectedCount > 0 && selectedCount < this.visibleResults.length
    },
    selectedPdfWorks() {
      return (this.selectedWorks || []).filter(work => this.canImportWorkPdfToRag(work))
    },
    ragImportPdfCount() {
      return (this.ragImportWorks || []).filter(work => this.canImportWorkPdfToRag(work)).length
    },
    ragImportPreviewWorks() {
      return (this.ragImportWorks || []).slice(0, 5)
    },
    ragImportPercent() {
      const total = Number(this.ragImportProgress && this.ragImportProgress.total) || 0
      const done = Number(this.ragImportProgress && this.ragImportProgress.done) || 0
      if (!total) {
        return 0
      }
      return Math.min(100, Math.max(0, Math.round((done / total) * 100)))
    },
    compareStats() {
      const works = this.selectedWorks || []
      const cited = works
        .map(work => Number(work && work.citedByCount))
        .filter(value => Number.isFinite(value))
      const totalCitations = cited.reduce((sum, value) => sum + value, 0)
      return {
        oaCount: works.filter(work => work && work.isOa).length,
        pdfCount: works.filter(work => work && work.pdfUrl).length,
        averageCitations: cited.length ? Math.round(totalCitations / cited.length) : 0
      }
    },
    citationText() {
      return this.citationWork ? formatCitation(this.citationWork, this.citationFormat) : ''
    }
  },
  watch: {
    '$route.query'(query) {
      if (this.syncingRoute) {
        return
      }
      const nextKeyword = this.routeString(query, 'q')
      const stateChanged = this.applyRouteFilters(query)
      if (nextKeyword !== this.keyword) {
        this.keyword = nextKeyword
        if (nextKeyword.trim() || this.hasActiveFilters()) {
          this.submitSearch({ resetPage: false })
        } else {
          this.resetSearchState()
        }
      } else if (stateChanged && (this.keyword || this.hasActiveFilters())) {
        this.submitSearch({ resetPage: false })
      } else if (stateChanged) {
        this.resetSearchState()
      }
    }
  },
  created() {
    this.keyword = this.$route.query.q || ''
    this.applyRouteFilters(this.$route.query)
    this.loadListDensity()
    this.loadScreeningMarks()
    this.syncUserInfoFromStorage()
    this.syncHeaderUserStatus()
    this.loadSavedWorks()
    if ((this.keyword && this.keyword.trim()) || this.hasActiveFilters()) {
      this.submitSearch({ resetPage: false })
    }
  },
  mounted() {
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.add('scholar-search-page-active')
    }
    this.$nextTick(this.cleanupLegacyScholarExportMenus)
    window.addEventListener('message', this.handleAuthMessage)
  },
  beforeDestroy() {
    this.cleanupLegacyScholarExportMenus()
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('scholar-search-page-active')
    }
    window.removeEventListener('message', this.handleAuthMessage)
    clearInterval(this.isRoamingTimer)
    clearTimeout(this.isRoamingTimerOut)
  },
  methods: {
    cleanupLegacyScholarExportMenus() {
      if (typeof document === 'undefined') {
        return
      }
      const exportMarkers = [
        '当前页 BibTeX',
        '当前页 RIS',
        '当前页 GB/T',
        '当前页筛选表',
        '已选 BibTeX',
        '前 100 条 BibTeX',
        '导出检索复现 Markdown',
        '导出检索参数 JSON',
        '导出质量报告 Markdown'
      ]
      const menus = document.querySelectorAll('body > ul[id^="dropdown-menu-"].el-dropdown-menu')
      menus.forEach(menu => {
        const className = String(menu.className || '')
        const text = String(menu.textContent || '').replace(/\s+/g, ' ').trim()
        const isScholarLegacyMenu = className.includes('scholar-export-menu') ||
          className.includes('scholar-results-more-menu') ||
          exportMarkers.some(marker => text.includes(marker))
        if (isScholarLegacyMenu && menu.parentNode) {
          menu.parentNode.removeChild(menu)
        }
      })
    },
    listDensityStorageKey() {
      return 'scholar-list-density-v1'
    },
    normalizeListDensity(value) {
      return value === 'compact' ? 'compact' : 'standard'
    },
    loadListDensity() {
      try {
        this.listDensity = this.normalizeListDensity(window.localStorage.getItem(this.listDensityStorageKey()))
      } catch (e) {
        this.listDensity = 'standard'
      }
    },
    handleListDensityChange(value) {
      this.listDensity = this.normalizeListDensity(value)
      try {
        window.localStorage.setItem(this.listDensityStorageKey(), this.listDensity)
      } catch (e) {
        // Ignore storage failures; the current session still uses the selected density.
      }
    },
    isEntityFilterTag(key) {
      return [
        'authors',
        'institutions',
        'sources',
        'topics',
        'keywords',
        'funders',
        'publishers',
        'domains',
        'fields',
        'subfields',
        'sdgs',
        'citesWork'
      ].includes(key)
    },
    activeFilterTagTitle(tag) {
      const label = tag && tag.label ? tag.label : '筛选条件'
      return `${label}，点击移除此筛选`
    },
    resetSearchState() {
      this.searchExecuted = false
      this.loading = false
      this.loadingMore = false
      this.facetsLoading = false
      this.facetsRequestId += 1
      this.searchMode = 'keyword'
      this.advancedSearchVisible = false
      this.filtersOpen = false
      this.mobileFacetsOpen = false
      this.page = 1
      this.nextCursor = ''
      this.total = 0
      this.results = []
      this.providers = []
      this.cacheHit = false
      this.costUsd = null
      this.errorMessage = ''
      this.facets = this.emptyFacets()
      this.expandedFacets = {}
      this.expandedWorkKeys = {}
      this.expandedWorkLoading = {}
      this.bulkImportActive = false
      this.bulkImportSummary = null
      this.pageKeyword = ''
      this.clearSelection()
      this.replaceRouteQuery()
    },
    handleEmptyStateAction(key) {
      if (key === 'retryBulkImport') {
        this.openBulkImportDialog()
        return
      }
      if (key === 'clearFilters') {
        this.filters = this.defaultScholarFilters()
        this.searchMode = 'keyword'
        this.submitSearch()
        return
      }
      if (key === 'keywordMode') {
        this.searchMode = 'keyword'
        if (this.keyword || this.hasActiveFilters()) {
          this.submitSearch()
        } else {
          this.replaceRouteQuery()
        }
        return
      }
      if (key === 'clearKeyword') {
        this.keyword = ''
        this.resetSearchState()
      }
    },
    async submitSearch(options = {}) {
      const resetPage = options.resetPage !== false
      const q = (this.keyword || '').trim()
      this.bulkImportActive = false
      this.bulkImportSummary = null
      this.keyword = q
      this.pageKeyword = ''
      if (!q && !this.hasActiveFilters()) {
        this.resetSearchState()
        if (!options.silentEmpty) {
          this.$message.warning('请输入检索词')
        }
        return
      }
      this.searchExecuted = true
      if (q && this.searchMode !== 'semantic' && this.isLikelyIdentifier(q) && !this.hasActiveFilters()) {
        const resolved = await this.resolveIdentifier(q)
        if (resolved) {
          return
        }
      }
      if (resetPage) {
        this.page = 1
      }
      this.nextCursor = ''
      this.expandedFacets = {}
      await this.fetchResults(false, { refreshFacets: true })
      this.replaceRouteQuery()
    },
    openBulkImportDialog() {
      this.bulkImportSummary = null
      this.bulkImportVisible = true
    },
    async submitBulkImport() {
      const text = (this.bulkImportText || '').trim()
      if (!text) {
        this.$message.warning('请粘贴 DOI、PMID、PMCID 或 OpenAlex ID')
        return
      }
      this.bulkImportLoading = true
      this.errorMessage = ''
      try {
        const response = await bulkImportScholarWorks(text)
        const results = response.results || []
        this.bulkImportSummary = response
        this.bulkImportActive = true
        this.searchExecuted = true
        this.keyword = ''
        this.page = 1
        this.nextCursor = ''
        this.total = response.foundCount || results.length
        this.results = results
        this.clearSelection()
        this.expandedWorkKeys = {}
        this.expandedWorkLoading = {}
        this.facets = this.facetsFromResults(results)
        this.providers = results.length ? ['openalex'] : []
        this.bulkImportVisible = false
        this.replaceRouteQuery()
        if (results.length) {
          this.$message.success(`已导入 ${results.length} 篇文献`)
        } else {
          this.$message.warning('未找到可导入的文献')
        }
      } catch (e) {
        this.$message.error(this.resolveErrorMessage(e, '批量导入失败'))
      } finally {
        this.bulkImportLoading = false
      }
    },
    async resolveIdentifier(q) {
      this.loading = true
      this.errorMessage = ''
      try {
        const resolved = await resolveScholarIdentifier(q)
        if (!resolved || resolved.targetType === 'search') {
          return false
        }
        if (resolved.targetType === 'work' && resolved.id) {
          this.$router.push({
            path: '/scholar/detail',
            query: {
              provider: resolved.provider || 'openalex',
              id: resolved.id,
              q
            }
          })
          return true
        }
        if (resolved.targetType === 'entity' && resolved.id) {
          this.$router.push({
            path: '/scholar/entity',
            query: {
              provider: resolved.provider || 'openalex',
              entityType: resolved.entityType,
              id: resolved.id,
              name: resolved.name || q,
              q
            }
          })
          return true
        }
      } catch (e) {
        return false
      } finally {
        this.loading = false
      }
      return false
    },
    bulkImportReportMarkdown() {
      const summary = this.bulkImportSummary || {}
      const unsupported = summary.unsupported || []
      const notFound = summary.notFound || []
      const works = summary.results || this.results || []
      const lines = [
        '# OA 文献批量导入报告',
        '',
        `- 输入数量：${summary.inputCount || 0}`,
        `- 识别数量：${summary.recognizedCount || 0}`,
        `- 返回文献：${summary.foundCount || works.length || 0}`,
        `- 未识别：${unsupported.length}`,
        `- 未查到：${notFound.length}`,
        `- 生成时间：${new Date().toLocaleString()}`,
        ''
      ]
      if (unsupported.length) {
        lines.push('## 未识别条目')
        unsupported.forEach(item => {
          lines.push(`- ${item}`)
        })
        lines.push('')
      }
      if (notFound.length) {
        lines.push('## 已识别但未查到')
        notFound.forEach(item => {
          lines.push(`- ${item}`)
        })
        lines.push('')
      }
      lines.push('## 已返回文献')
      if (!works.length) {
        lines.push('- 暂无返回文献')
        return lines.join('\n')
      }
      works.forEach((work, index) => {
        const authors = this.formatAuthors(work.authors || [], 8) || '未知作者'
        const url = work.pdfUrl || work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)
        lines.push(`### ${index + 1}. ${work.title || 'Untitled work'}`)
        lines.push(`- 作者：${authors}`)
        lines.push(`- 年份：${work.publicationYear || '未知年份'}`)
        lines.push(`- 来源：${work.sourceName || '未知来源'}`)
        lines.push(`- 引用：${this.formatNumber(work.citedByCount || 0)}`)
        lines.push(`- 开放获取：${work.isOa ? this.oaStatusText(work.oaStatus) : '否'}`)
        if (work.doi) {
          lines.push(`- DOI：${this.doiUrl(work)}`)
        }
        if (url) {
          lines.push(`- 链接：${url}`)
        }
        lines.push('')
      })
      return lines.join('\n')
    },
    bulkImportReportCsv() {
      const summary = this.bulkImportSummary || {}
      const rows = []
      ;(summary.unsupported || []).forEach(item => {
        rows.push(['unsupported', item, '', '', '', '', '', '', ''])
      })
      ;(summary.notFound || []).forEach(item => {
        rows.push(['not_found', item, '', '', '', '', '', '', ''])
      })
      ;(summary.results || this.results || []).forEach(work => {
        rows.push([
          'found',
          '',
          work.title || '',
          this.formatAuthors(work.authors || [], 20),
          work.publicationYear || '',
          work.sourceName || '',
          work.citedByCount || 0,
          work.doi || '',
          work.pdfUrl || work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)
        ])
      })
      const headers = ['Status', 'Input', 'Title', 'Authors', 'Year', 'Source', 'Citations', 'DOI', 'URL']
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    async copyBulkImportReport() {
      if (!this.bulkImportSummary) {
        this.$message.warning('暂无批量导入报告')
        return
      }
      try {
        await this.copyText(this.bulkImportReportMarkdown())
        this.$message.success('批量导入报告已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadBulkImportReport(format) {
      if (!this.bulkImportSummary) {
        this.$message.warning('暂无批量导入报告')
        return
      }
      const exportFormat = format === 'csv' ? 'csv' : 'markdown'
      const content = exportFormat === 'csv'
        ? `\uFEFF${this.bulkImportReportCsv()}`
        : this.bulkImportReportMarkdown()
      this.downloadText(
        content,
        `scholar-bulk-import-report.${exportFormat === 'csv' ? 'csv' : 'md'}`,
        exportFormat === 'csv' ? 'text/csv;charset=utf-8' : 'text/markdown;charset=utf-8'
      )
      this.$message.success('批量导入报告已导出')
    },
    qualityWorks() {
      return (this.results || []).filter(Boolean)
    },
    qualityPercent(count) {
      const total = this.qualityWorks().length || 0
      return total ? `${this.formatDecimal((Number(count) / total) * 100, 1)}%` : '0%'
    },
    qualityMetricRows() {
      const works = this.qualityWorks()
      const total = works.length
      const count = predicate => works.filter(predicate).length
      const rows = [
        { key: 'doi', label: '有 DOI', count: count(work => !!work.doi) },
        { key: 'abstract', label: '有摘要', count: count(work => !!this.cleanText(work.abstractText)) },
        { key: 'pdf', label: '有 PDF', count: count(work => !!work.pdfUrl) },
        { key: 'oa', label: '开放获取', count: count(work => !!work.isOa) },
        { key: 'fulltext', label: '可全文检索', count: count(work => this.hasFulltext(work)) },
        { key: 'retracted', label: '已撤稿', count: count(work => this.isRetracted(work)) },
        { key: 'paratext', label: '非正文内容', count: count(work => this.isParatext(work)) }
      ]
      return rows.map(row => ({
        ...row,
        total,
        percent: this.qualityPercent(row.count)
      }))
    },
    qualityIssueWorks() {
      return this.qualityWorks()
        .map(work => {
          const issues = []
          if (this.isRetracted(work)) {
            issues.push('已撤稿')
          }
          if (this.isParatext(work)) {
            issues.push('非正文')
          }
          if (!work.doi) {
            issues.push('缺 DOI')
          }
          if (!this.cleanText(work.abstractText)) {
            issues.push('缺摘要')
          }
          if (!work.pdfUrl) {
            issues.push('无 PDF')
          }
          return { work, issues }
        })
        .filter(item => item.issues.length)
    },
    workBestUrl(work) {
      if (!work) {
        return ''
      }
      return work.pdfUrl || work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)
    },
    qualityReportMarkdown() {
      const works = this.qualityWorks()
      const rows = this.qualityMetricRows()
      const issues = this.qualityIssueWorks()
      const lines = [
        '# OA 文献当前页质量报告',
        '',
        `- 检索词：${this.keyword || '未填写'}`,
        `- 检索模式：${this.searchModeText(this.searchMode)}`,
        `- 检索模式说明：${this.currentSearchModeOption.strategy || this.currentSearchModeOption.description || '常规关键词检索'}`,
        `- 当前页文献：${this.formatNumber(works.length)}`,
        `- 总结果数：${this.formatNumber(this.total || 0)}`,
        `- 页码：${this.page}`,
        `- 每页数量：${this.size}`,
        `- 记录时间：${new Date().toLocaleString()}`,
        `- 检索链接：${this.currentSearchLink()}`
      ]
      if (this.activeFilterTags.length) {
        lines.push(`- 筛选条件：${this.activeFilterTags.map(item => item.label).join('；')}`)
      } else {
        lines.push('- 筛选条件：无额外筛选')
      }
      lines.push('', '## 字段覆盖', '', '| 指标 | 数量 | 占比 |', '| --- | ---: | ---: |')
      rows.forEach(row => {
        lines.push(`| ${this.markdownCell(row.label)} | ${this.formatNumber(row.count)} / ${this.formatNumber(row.total)} | ${row.percent} |`)
      })
      lines.push('', '## 需要核对的条目')
      if (!issues.length) {
        lines.push('- 当前页未发现撤稿、非正文、缺 DOI、缺摘要或无 PDF 标记。')
      } else {
        issues.slice(0, 30).forEach((item, index) => {
          const work = item.work || {}
          const url = this.workBestUrl(work)
          lines.push(`### ${index + 1}. ${work.title || 'Untitled work'}`)
          lines.push(`- 问题：${item.issues.join('、')}`)
          lines.push(`- 年份：${work.publicationYear || '未知年份'}`)
          lines.push(`- 来源：${work.sourceName || '未知来源'}`)
          if (work.doi) {
            lines.push(`- DOI：${this.doiUrl(work) || work.doi}`)
          }
          if (url) {
            lines.push(`- 链接：${url}`)
          }
          lines.push('')
        })
      }
      lines.push('', '## 说明', '- 本报告只基于当前页已返回元数据统计，不代表论文质量评价；撤稿、非正文、DOI、摘要、PDF 等字段以三方数据源返回为准。')
      return lines.join('\n')
    },
    qualityReportCsv() {
      const rows = []
      this.qualityMetricRows().forEach(row => {
        rows.push(['metric', row.label, row.count, row.total, row.percent, '', '', '', '', '', ''])
      })
      this.qualityIssueWorks().forEach(item => {
        const work = item.work || {}
        rows.push([
          'work_issue',
          item.issues.join('; '),
          '',
          '',
          '',
          work.title || '',
          this.formatAuthors(work.authors || [], 20),
          work.publicationYear || '',
          work.sourceName || '',
          work.doi || '',
          this.workBestUrl(work)
        ])
      })
      const headers = ['Section', 'Item', 'Count', 'Total', 'Percent', 'Title', 'Authors', 'Year', 'Source', 'DOI', 'URL']
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    async copyQualityReport() {
      if (!this.results.length) {
        this.$message.warning('当前没有可复制的质量报告')
        return
      }
      try {
        await this.copyText(this.qualityReportMarkdown())
        this.$message.success('质量报告已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadQualityReport(format) {
      if (!this.results.length) {
        this.$message.warning('当前没有可导出的质量报告')
        return
      }
      const exportFormat = format === 'csv' ? 'csv' : 'markdown'
      const content = exportFormat === 'csv'
        ? `\uFEFF${this.qualityReportCsv()}`
        : this.qualityReportMarkdown()
      this.downloadText(
        content,
        `scholar-quality-report.${exportFormat === 'csv' ? 'csv' : 'md'}`,
        exportFormat === 'csv' ? 'text/csv;charset=utf-8' : 'text/markdown;charset=utf-8'
      )
      this.$message.success('质量报告已导出')
    },
    isLikelyIdentifier(value) {
      const text = String(value || '').trim()
      return /^https?:\/\/(?:dx\.)?doi\.org\/10\.\d{4,9}\/\S+/i.test(text) ||
        /^doi:\s*10\.\d{4,9}\/\S+/i.test(text) ||
        /^10\.\d{4,9}\/\S+/i.test(text) ||
        /^pmid[:\s]*\d{6,9}$/i.test(text) ||
        /^(?:pmcid[:\s]*)?PMC\d{5,9}$/i.test(text) ||
        /^(?:https?:\/\/orcid\.org\/)?\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/i.test(text) ||
        /^(?:https?:\/\/ror\.org\/|ror[:\s]*)0[a-z0-9]{6,9}$/i.test(text) ||
        /^(?:issn[:\s]*)?\d{4}-\d{3}[\dX]$/i.test(text) ||
        /^(?:https?:\/\/openalex\.org\/)?[WAISTKPF]\d+$/i.test(text)
    },
    async fetchResults(append, options) {
      const refreshFacets = !append && (!options || options.refreshFacets !== false)
      if (append) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.results = []
        this.clearSelection()
        this.expandedWorkKeys = {}
        this.expandedWorkLoading = {}
      }
      this.errorMessage = ''
      this.quotaExhausted = false
      try {
        const response = await searchScholar(this.buildSearchParams(append))
        const incomingResults = response.results || []
        this.total = response.total || incomingResults.length
        this.nextCursor = response.nextCursor || ''
        this.cacheHit = !!response.cacheHit
        this.costUsd = response.costUsd
        this.providers = response.providers || []
        if (refreshFacets) {
          this.facets = this.emptyFacets()
        }
        this.results = append ? this.results.concat(incomingResults) : incomingResults
        if (refreshFacets) {
          this.fetchFacets(response.facets)
        }
        if (!append) {
          this.searchHistoryLoaded = false
        }
        this.quotaExhausted = false
      } catch (e) {
        if (this.isQuotaExhaustedError(e)) {
          this.quotaExhausted = true
          this.errorMessage = ''
          this.results = []
          this.total = 0
          this.nextCursor = ''
          this.costUsd = null
          this.providers = []
          this.facets = this.emptyFacets()
        } else {
          this.errorMessage = this.resolveErrorMessage(e)
        }
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    buildSearchParams(append) {
      const params = {
        q: this.keyword,
        searchMode: this.searchMode,
        page: this.page,
        size: this.size,
        oaOnly: this.filters.oaOnly,
        hasPdf: this.filters.hasPdf,
        hasFulltext: this.filters.hasFulltext,
        hasDoi: this.filters.hasDoi,
        hasAbstract: this.filters.hasAbstract,
        hasReferences: this.filters.hasReferences,
        hasPmid: this.filters.hasPmid,
        excludeRetracted: this.filters.excludeRetracted,
        excludeParatext: this.filters.excludeParatext,
        language: this.filters.language,
        countryCode: this.filters.countryCode,
        oaStatus: this.filters.oaStatus,
        openVersion: this.filters.openVersion,
        license: this.filters.license,
        sourceType: this.filters.sourceType,
        indexedIn: this.filters.indexedIn,
        impactLevel: this.filters.impactLevel,
        authorId: this.filters.authorId,
        institutionId: this.filters.institutionId,
        sourceId: this.filters.sourceId,
        topicId: this.filters.topicId,
        keywordId: this.filters.keywordId,
        funderId: this.filters.funderId,
        sdgId: this.filters.sdgId,
        domainId: this.filters.domainId,
        fieldId: this.filters.fieldId,
        subfieldId: this.filters.subfieldId,
        publisherId: this.filters.publisherId,
        citesWorkId: this.filters.citesWorkId,
        sort: this.filters.sort
      }
      if (append && this.nextCursor) {
        params.cursor = this.nextCursor
      }
      if (this.filters.fromYear) {
        params.fromYear = Number(this.filters.fromYear)
      }
      if (this.filters.toYear) {
        params.toYear = Number(this.filters.toYear)
      }
      if (this.filters.type) {
        params.type = this.filters.type
      }
      if (this.filters.minCitations) {
        params.minCitations = Number(this.filters.minCitations)
      }
      if (this.filters.maxCitations) {
        params.maxCitations = Number(this.filters.maxCitations)
      }
      return params
    },
    buildFacetParams() {
      const params = {
        q: this.keyword,
        searchMode: this.searchMode,
        oaOnly: this.filters.oaOnly,
        hasPdf: this.filters.hasPdf,
        hasFulltext: this.filters.hasFulltext,
        hasDoi: this.filters.hasDoi,
        hasAbstract: this.filters.hasAbstract,
        hasReferences: this.filters.hasReferences,
        hasPmid: this.filters.hasPmid,
        excludeRetracted: this.filters.excludeRetracted,
        excludeParatext: this.filters.excludeParatext,
        language: this.filters.language,
        countryCode: this.filters.countryCode,
        oaStatus: this.filters.oaStatus,
        openVersion: this.filters.openVersion,
        license: this.filters.license,
        sourceType: this.filters.sourceType,
        indexedIn: this.filters.indexedIn,
        impactLevel: this.filters.impactLevel,
        authorId: this.filters.authorId,
        institutionId: this.filters.institutionId,
        sourceId: this.filters.sourceId,
        topicId: this.filters.topicId,
        keywordId: this.filters.keywordId,
        funderId: this.filters.funderId,
        sdgId: this.filters.sdgId,
        domainId: this.filters.domainId,
        fieldId: this.filters.fieldId,
        subfieldId: this.filters.subfieldId,
        publisherId: this.filters.publisherId,
        citesWorkId: this.filters.citesWorkId
      }
      if (this.filters.fromYear) {
        params.fromYear = Number(this.filters.fromYear)
      }
      if (this.filters.toYear) {
        params.toYear = Number(this.filters.toYear)
      }
      if (this.filters.type) {
        params.type = this.filters.type
      }
      if (this.filters.minCitations) {
        params.minCitations = Number(this.filters.minCitations)
      }
      if (this.filters.maxCitations) {
        params.maxCitations = Number(this.filters.maxCitations)
      }
      return params
    },
    buildRouteQuery() {
      const query = {}
      if (this.keyword) {
        query.q = this.keyword
      }
      const add = (key, value) => {
        if (value !== '' && value !== null && value !== undefined && value !== false) {
          query[key] = String(value)
        }
      }
      add('oaOnly', this.filters.oaOnly ? 'true' : '')
      add('hasPdf', this.filters.hasPdf ? 'true' : '')
      add('hasFulltext', this.filters.hasFulltext ? 'true' : '')
      add('hasDoi', this.filters.hasDoi ? 'true' : '')
      add('hasAbstract', this.filters.hasAbstract ? 'true' : '')
      add('hasReferences', this.filters.hasReferences ? 'true' : '')
      add('hasPmid', this.filters.hasPmid ? 'true' : '')
      add('excludeRetracted', this.filters.excludeRetracted ? 'true' : '')
      add('excludeParatext', this.filters.excludeParatext ? 'true' : '')
      add('searchMode', this.searchMode && this.searchMode !== 'keyword' ? this.searchMode : '')
      add('page', this.page > 1 ? this.page : '')
      add('size', this.size !== 20 ? this.size : '')
      add('fromYear', this.filters.fromYear)
      add('toYear', this.filters.toYear)
      add('type', this.filters.type)
      add('language', this.filters.language)
      add('countryCode', this.filters.countryCode)
      add('oaStatus', this.filters.oaStatus)
      add('openVersion', this.filters.openVersion)
      add('license', this.filters.license)
      add('sourceType', this.filters.sourceType)
      add('indexedIn', this.filters.indexedIn)
      add('impactLevel', this.filters.impactLevel)
      add('minCitations', this.filters.minCitations)
      add('maxCitations', this.filters.maxCitations)
      add('authorId', this.filters.authorId)
      add('authorName', this.filters.authorName)
      add('institutionId', this.filters.institutionId)
      add('institutionName', this.filters.institutionName)
      add('sourceId', this.filters.sourceId)
      add('sourceName', this.filters.sourceName)
      add('topicId', this.filters.topicId)
      add('topicName', this.filters.topicName)
      add('keywordId', this.filters.keywordId)
      add('keywordName', this.filters.keywordName)
      add('funderId', this.filters.funderId)
      add('funderName', this.filters.funderName)
      add('sdgId', this.filters.sdgId)
      add('sdgName', this.filters.sdgName)
      add('domainId', this.filters.domainId)
      add('domainName', this.filters.domainName)
      add('fieldId', this.filters.fieldId)
      add('fieldName', this.filters.fieldName)
      add('subfieldId', this.filters.subfieldId)
      add('subfieldName', this.filters.subfieldName)
      add('publisherId', this.filters.publisherId)
      add('publisherName', this.filters.publisherName)
      add('citesWorkId', this.filters.citesWorkId)
      add('citesWorkName', this.filters.citesWorkName)
      add('sort', this.filters.sort && this.filters.sort !== 'relevance' ? this.filters.sort : '')
      return query
    },
    replaceRouteQuery() {
      const query = this.buildRouteQuery()
      if (this.isSameRouteQuery(this.$route.query, query)) {
        return
      }
      this.syncingRoute = true
      this.$router.replace({ path: '/scholar', query }).catch(() => {}).finally(() => {
        this.$nextTick(() => {
          this.syncingRoute = false
        })
      })
    },
    currentSearchLink() {
      const query = this.buildRouteQuery()
      const route = this.$router.resolve({ path: '/scholar', query })
      const origin = window.location.origin || ''
      return `${origin}${route.href}`
    },
    currentSearchReturnQuery() {
      const route = this.$router.resolve({ path: '/scholar', query: this.buildRouteQuery() })
      return {
        returnUrl: route.href,
        returnScroll: String(this.currentScrollTop())
      }
    },
    currentScrollTop() {
      if (typeof window === 'undefined') {
        return 0
      }
      return Math.max(
        window.pageYOffset || 0,
        document.documentElement ? document.documentElement.scrollTop || 0 : 0,
        document.body ? document.body.scrollTop || 0 : 0
      )
    },
    async copySearchLink() {
      const link = this.currentSearchLink()
      try {
        await this.copyText(link)
        this.$message.success('检索链接已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动复制浏览器地址')
      }
    },
    searchStrategyText() {
      const filters = this.activeFilterTags.map(item => item.label)
      const topFacetSections = this.facetSections
        .filter(section => section.items && section.items.length)
        .map(section => ({
          title: section.title,
          items: section.items.slice(0, 5)
        }))
      const metricRows = this.qualityMetricRows()
      const lines = [
        '# OA 文献检索策略记录',
        '',
        `- 检索词：${this.keyword || '未填写'}`,
        `- 检索模式：${this.searchModeText(this.searchMode)}`,
        `- 检索模式说明：${this.currentSearchModeOption.strategy || this.currentSearchModeOption.description || '常规关键词检索'}`,
        `- 排序：${this.sortText(this.filters.sort)}`,
        `- 结果数：${this.formatNumber(this.total || 0)}`,
        `- 当前页：第 ${this.page} 页，${this.results.length} 条结果`,
        `- 每页数量：${this.size}`,
        `- 数据源：${this.providers && this.providers.length ? this.providers.map(this.providerText).join('、') : 'OpenAlex'}`,
        `- 记录时间：${new Date().toLocaleString()}`,
        `- 检索链接：${this.currentSearchLink()}`,
        '',
        '## 筛选条件'
      ]
      if (filters.length) {
        filters.forEach(label => {
          lines.push(`- ${label}`)
        })
      } else {
        lines.push('- 无额外筛选')
      }
      if (topFacetSections.length) {
        lines.push('', '## 聚类概览')
        topFacetSections.forEach(section => {
          lines.push(`### ${section.title}`)
          section.items.forEach(item => {
            lines.push(`- ${item.label || item.name || item.id}：${this.formatNumber(item.count || 0)}`)
          })
        })
      }
      if (metricRows.length) {
        lines.push('', '## 当前页字段覆盖', '', '| 字段 | 覆盖 |', '| --- | ---: |')
        metricRows.forEach(row => {
          lines.push(`| ${this.markdownCell(row.label)} | ${this.formatNumber(row.count)} / ${this.formatNumber(row.total)} (${row.percent}) |`)
        })
      }
      lines.push(
        '',
        '## 接口参数',
        '',
        '```json',
        JSON.stringify(this.searchStrategyPackage().api, null, 2),
        '```',
        '',
        '## 说明',
        '- 本记录基于当前页面检索条件和三方接口返回元数据生成，结果数量、聚类和字段覆盖会随数据源更新而变化。',
        '- 接口参数用于复现实验检索或排查结果差异，不包含 API Key、费用或用户隐私信息。'
      )
      return lines.join('\n')
    },
    searchStrategyPackage() {
      const routeQuery = this.buildRouteQuery()
      return {
        version: 1,
        source: 'scholar-search-reproducible-query',
        exportedAt: new Date().toISOString(),
        route: {
          path: '/scholar',
          query: routeQuery,
          link: this.currentSearchLink()
        },
        api: {
          searchParams: this.buildSearchParams(false),
          facetParams: this.buildFacetParams()
        },
        ui: {
          keyword: this.keyword || '',
          searchMode: this.searchMode,
          searchModeText: this.searchModeText(this.searchMode),
          searchModeDescription: this.currentSearchModeOption.description || '',
          searchModeStrategy: this.currentSearchModeOption.strategy || '',
          searchModeApiParam: this.currentSearchModeOption.apiParam || '',
          sort: this.filters.sort || 'relevance',
          sortText: this.sortText(this.filters.sort),
          page: this.page,
          size: this.size,
          screeningView: this.screeningView
        },
        results: {
          total: this.total || 0,
          currentPageCount: (this.results || []).length,
          visibleCount: (this.visibleResults || []).length,
          selectedCount: (this.selectedWorks || []).length,
          hasNextCursor: !!this.nextCursor,
          cacheHit: !!this.cacheHit,
          providers: this.providers || []
        },
        filters: this.activeFilterTags.map(item => ({ key: item.key, label: item.label })),
        facets: this.facetSections
          .filter(section => section.items && section.items.length)
          .map(section => ({
            key: section.key,
            title: section.title,
            items: section.items.slice(0, 20).map(item => ({
              key: item.key || item.id || item.value || item.label,
              label: item.label || item.name || item.id || '',
              count: item.count || 0
            }))
          })),
        quality: this.qualityMetricRows(),
        screening: {
          view: this.screeningView,
          stats: this.screeningStats,
          markedTotal: this.screeningMarkCount || 0
        }
      }
    },
    async copySearchStrategy() {
      try {
        await this.copyText(this.searchStrategyText())
        this.$message.success('检索策略已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动复制浏览器地址')
      }
    },
    downloadSearchStrategy(format) {
      if (!this.hasSavableSearch() && !this.results.length) {
        this.$message.warning('请先输入关键词或设置筛选条件')
        return
      }
      const exportFormat = format === 'json' ? 'json' : 'markdown'
      const content = exportFormat === 'json'
        ? JSON.stringify(this.searchStrategyPackage(), null, 2)
        : this.searchStrategyText()
      this.downloadText(
        content,
        `scholar-search-strategy.${exportFormat === 'json' ? 'json' : 'md'}`,
        exportFormat === 'json' ? 'application/json;charset=utf-8' : 'text/markdown;charset=utf-8'
      )
      this.$message.success(exportFormat === 'json' ? '检索参数包已导出' : '检索策略记录已导出')
    },
    isSameRouteQuery(current, next) {
      const currentKeys = Object.keys(current || {}).filter(key => this.routeString(current, key) !== '')
      const nextKeys = Object.keys(next || {}).filter(key => this.routeString(next, key) !== '')
      if (currentKeys.length !== nextKeys.length) {
        return false
      }
      return nextKeys.every(key => this.routeString(current, key) === this.routeString(next, key))
    },
    applyRouteFilters(query) {
      let changed = false
      const set = (key, value) => {
        if (this.filters[key] !== value) {
          this.filters[key] = value
          changed = true
        }
      }
      set('oaOnly', this.routeBoolean(query, 'oaOnly'))
      set('hasPdf', this.routeBoolean(query, 'hasPdf'))
      set('hasFulltext', this.routeBoolean(query, 'hasFulltext'))
      set('hasDoi', this.routeBoolean(query, 'hasDoi'))
      set('hasAbstract', this.routeBoolean(query, 'hasAbstract'))
      set('hasReferences', this.routeBoolean(query, 'hasReferences'))
      set('hasPmid', this.routeBoolean(query, 'hasPmid'))
      set('excludeRetracted', this.routeBoolean(query, 'excludeRetracted'))
      set('excludeParatext', this.routeBoolean(query, 'excludeParatext'))
      const nextSearchMode = this.normalizeSearchMode(this.routeString(query, 'searchMode') || 'keyword')
      if (this.searchMode !== nextSearchMode) {
        this.searchMode = nextSearchMode
        changed = true
      }
      const nextPage = this.routePositiveInt(query, 'page', 1)
      if (this.page !== nextPage) {
        this.page = nextPage
        changed = true
      }
      const nextSize = this.normalizePageSize(this.routePositiveInt(query, 'size', 20))
      if (this.size !== nextSize) {
        this.size = nextSize
        changed = true
      }
      if (this.page > this.maxPageablePages) {
        this.page = this.maxPageablePages
        changed = true
      }
      set('fromYear', this.routeString(query, 'fromYear'))
      set('toYear', this.routeString(query, 'toYear'))
      set('type', this.routeString(query, 'type'))
      set('language', this.routeString(query, 'language'))
      set('countryCode', this.routeString(query, 'countryCode'))
      set('oaStatus', this.routeString(query, 'oaStatus'))
      set('openVersion', this.routeString(query, 'openVersion'))
      set('license', this.routeString(query, 'license'))
      set('sourceType', this.routeString(query, 'sourceType'))
      set('indexedIn', this.routeString(query, 'indexedIn'))
      set('impactLevel', this.routeString(query, 'impactLevel'))
      set('minCitations', this.routeString(query, 'minCitations'))
      set('maxCitations', this.routeString(query, 'maxCitations'))
      set('authorId', this.routeString(query, 'authorId'))
      set('authorName', this.routeString(query, 'authorName'))
      set('authorSelectedName', this.routeString(query, 'authorName'))
      set('institutionId', this.routeString(query, 'institutionId'))
      set('institutionName', this.routeString(query, 'institutionName'))
      set('institutionSelectedName', this.routeString(query, 'institutionName'))
      set('sourceId', this.routeString(query, 'sourceId'))
      set('sourceName', this.routeString(query, 'sourceName'))
      set('sourceSelectedName', this.routeString(query, 'sourceName'))
      set('topicId', this.routeString(query, 'topicId'))
      set('topicName', this.routeString(query, 'topicName'))
      set('topicSelectedName', this.routeString(query, 'topicName'))
      set('keywordId', this.routeString(query, 'keywordId'))
      set('keywordName', this.routeString(query, 'keywordName'))
      set('keywordSelectedName', this.routeString(query, 'keywordName'))
      set('funderId', this.routeString(query, 'funderId'))
      set('funderName', this.routeString(query, 'funderName'))
      set('funderSelectedName', this.routeString(query, 'funderName'))
      set('sdgId', this.routeString(query, 'sdgId'))
      set('sdgName', this.routeString(query, 'sdgName'))
      set('domainId', this.routeString(query, 'domainId'))
      set('domainName', this.routeString(query, 'domainName'))
      set('fieldId', this.routeString(query, 'fieldId'))
      set('fieldName', this.routeString(query, 'fieldName'))
      set('subfieldId', this.routeString(query, 'subfieldId'))
      set('subfieldName', this.routeString(query, 'subfieldName'))
      set('publisherId', this.routeString(query, 'publisherId'))
      set('publisherName', this.routeString(query, 'publisherName'))
      set('citesWorkId', this.routeString(query, 'citesWorkId'))
      set('citesWorkName', this.routeString(query, 'citesWorkName'))
      set('sort', this.routeString(query, 'sort') || 'relevance')
      return changed
    },
    routeString(query, key) {
      const value = query && query[key]
      const normalized = Array.isArray(value) ? value[0] : value
      return normalized === null || normalized === undefined ? '' : String(normalized)
    },
    routeBoolean(query, key) {
      return ['true', '1', 'yes'].includes(this.routeString(query, key).toLowerCase())
    },
    routePositiveInt(query, key, fallback) {
      const value = Number(this.routeString(query, key))
      return Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback
    },
    normalizePageSize(size) {
      return this.pageSizes.includes(size) ? size : 20
    },
    normalizeSearchMode(value) {
      return ['exact', 'semantic'].includes(value) ? value : 'keyword'
    },
    handleSearchModeChange() {
      this.searchMode = this.normalizeSearchMode(this.searchMode)
      if (this.keyword || this.results.length) {
        this.submitSearch()
      } else {
        this.replaceRouteQuery()
      }
    },
    applyQuickSort(value) {
      const nextSort = value || 'relevance'
      if (this.filters.sort === nextSort || this.loading) {
        return
      }
      this.filters.sort = nextSort
      if (this.keyword || this.hasActiveFilters()) {
        this.submitSearch()
      } else {
        this.replaceRouteQuery()
      }
    },
    currentPageFilterText(work) {
      return [
        work && work.title,
        work && work.doi,
        work && work.sourceName,
        work && work.publicationYear,
        work && work.type,
        this.formatAuthors((work && work.authors) || [], 8),
        this.formatInstitutions((work && work.institutions) || [], 6),
        ((work && work.topics) || []).map(item => item && item.name).filter(Boolean).join(' '),
        this.displayAbstract(work)
      ].filter(Boolean).join(' ').toLowerCase()
    },
    clearPageKeyword() {
      this.pageKeyword = ''
    },
    escapeRegExp(value) {
      return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    highlightTextParts(value) {
      const text = String(value || '')
      const keywords = this.highlightKeywords
      if (!text || !keywords.length) {
        return [{ text, matched: false }]
      }
      const pattern = keywords.map(item => this.escapeRegExp(item)).filter(Boolean).join('|')
      if (!pattern) {
        return [{ text, matched: false }]
      }
      const regex = new RegExp(`(${pattern})`, 'ig')
      const parts = []
      let lastIndex = 0
      let match
      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push({ text: text.slice(lastIndex, match.index), matched: false })
        }
        parts.push({ text: match[0], matched: true })
        lastIndex = match.index + match[0].length
      }
      if (lastIndex < text.length) {
        parts.push({ text: text.slice(lastIndex), matched: false })
      }
      return parts.length ? parts : [{ text, matched: false }]
    },
    hasActiveFilters() {
      const filters = this.filters
      return !!(
        filters.oaOnly ||
        filters.hasPdf ||
        filters.hasFulltext ||
        filters.hasDoi ||
        filters.hasAbstract ||
        filters.hasReferences ||
        filters.hasPmid ||
        filters.excludeRetracted ||
        filters.excludeParatext ||
        filters.fromYear ||
        filters.toYear ||
        filters.type ||
        filters.language ||
        filters.countryCode ||
        filters.oaStatus ||
        filters.openVersion ||
        filters.license ||
        filters.sourceType ||
        filters.indexedIn ||
        filters.impactLevel ||
        filters.minCitations ||
        filters.maxCitations ||
        filters.authorId ||
        filters.institutionId ||
        filters.sourceId ||
        filters.topicId ||
        filters.keywordId ||
        filters.funderId ||
        filters.sdgId ||
        filters.domainId ||
        filters.fieldId ||
        filters.subfieldId ||
        filters.publisherId ||
        filters.citesWorkId
      )
    },
    shouldShowFacetSearch(section) {
      return !!(section && section.items && section.items.length > this.facetPreviewLimit)
    },
    facetSearchKeyword(key) {
      return String((this.facetSearchKeywords && this.facetSearchKeywords[key]) || '').trim().toLowerCase()
    },
    filteredFacetItems(section) {
      const items = (section && section.items) || []
      const keyword = this.facetSearchKeyword(section && section.key)
      if (!keyword) {
        return items
      }
      return items.filter(item => {
        const text = [
          this.facetItemLabel(section.key, item),
          item.label,
          item.key
        ].filter(Boolean).join(' ').toLowerCase()
        return text.includes(keyword)
      })
    },
    facetSectionCountText(section) {
      if ((this.loading || this.facetsLoading) && !(section.items || []).length) {
        return '加载中'
      }
      const total = (section.items || []).length
      const keyword = this.facetSearchKeyword(section.key)
      if (!keyword) {
        return `${total}项`
      }
      return `${this.filteredFacetItems(section).length}/${total}项`
    },
    visibleFacetItems(section) {
      const items = this.filteredFacetItems(section)
      if (this.expandedFacets[section.key]) {
        return items
      }
      return items.slice(0, this.facetPreviewLimit)
    },
    toggleFacetSection(key) {
      this.$set(this.collapsedFacets, key, !this.collapsedFacets[key])
    },
    toggleFacetExpand(key) {
      this.$set(this.expandedFacets, key, !this.expandedFacets[key])
    },
    facetPercent(items, count) {
      const max = (items || []).reduce((value, item) => Math.max(value, item.count || 0), 1)
      return `${Math.max(6, Math.round(((count || 0) / max) * 100))}%`
    },
    facetItemLabel(key, item) {
      const raw = item.label || item.key || ''
      if (key === 'oaStatuses') {
        return this.oaStatusText(item.key || raw)
      }
      if (key === 'types') {
        return this.formatType(raw)
      }
      if (key === 'institutionCountries') {
        return this.countryText(this.openAlexId(item.key), raw)
      }
      if (key === 'languages') {
        return this.languageText(this.openAlexId(item.key || raw))
      }
      if (key === 'openVersions') {
        return this.openVersionText(item.key || raw)
      }
      if (key === 'licenses') {
        return this.licenseText(item.key || raw)
      }
      if (key === 'sourceTypes') {
        return this.sourceTypeText(item.key || raw)
      }
      if (key === 'indexedIn') {
        return this.indexedInText(item.key || raw)
      }
      if (key === 'sdgs') {
        const id = this.openAlexId(item.key || raw)
        return id && raw ? `SDG ${id} · ${raw}` : raw
      }
      if (key === 'domains' || key === 'fields' || key === 'subfields') {
        return raw
      }
      if (key === 'publishers') {
        return raw
      }
      return raw
    },
    isFacetActive(key, item) {
      const value = this.facetFilterValue(key, item)
      if (key === 'years') {
        return String(this.filters.fromYear) === value && String(this.filters.toYear) === value
      }
      if (key === 'oaStatuses') {
        return this.filters.oaStatus === value
      }
      if (key === 'types') {
        return this.filters.type === value
      }
      if (key === 'institutionCountries') {
        return this.filters.countryCode === value
      }
      if (key === 'languages') {
        return this.filters.language === value
      }
      if (key === 'openVersions') {
        return this.filters.openVersion === value
      }
      if (key === 'licenses') {
        return this.filters.license === value
      }
      if (key === 'sourceTypes') {
        return this.filters.sourceType === value
      }
      if (key === 'indexedIn') {
        return this.filters.indexedIn === value
      }
      if (key === 'topics') {
        return this.filters.topicId === value
      }
      if (key === 'sources') {
        return this.filters.sourceId === value
      }
      if (key === 'institutions') {
        return this.filters.institutionId === value
      }
      if (key === 'funders') {
        return this.filters.funderId === value
      }
      if (key === 'sdgs') {
        return this.filters.sdgId === value
      }
      if (key === 'domains') {
        return this.filters.domainId === value
      }
      if (key === 'fields') {
        return this.filters.fieldId === value
      }
      if (key === 'subfields') {
        return this.filters.subfieldId === value
      }
      if (key === 'publishers') {
        return this.filters.publisherId === value
      }
      return false
    },
    applyFacetFilter(key, item) {
      if (this.isFacetActive(key, item)) {
        this.clearFacetFilter(key)
      } else {
        this.setFacetFilter(key, item)
      }
      this.submitSearch()
      if (window.matchMedia('(max-width: 768px)').matches) {
        this.mobileFacetsOpen = false
        this.$nextTick(() => {
          const results = this.$el && this.$el.querySelector('.scholar-results')
          if (results) results.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      }
    },
    removeFilterTag(key) {
      this.clearFacetFilter(key)
      this.submitSearch()
    },
    clearAllFilters() {
      this.filters = this.defaultScholarFilters()
      this.searchMode = 'keyword'
      this.submitSearch()
    },
    setFacetFilter(key, item) {
      const value = this.facetFilterValue(key, item)
      const label = this.facetItemLabel(key, item)
      if (key === 'years') {
        this.filters.fromYear = value
        this.filters.toYear = value
      } else if (key === 'oaStatuses') {
        this.filters.oaStatus = value
      } else if (key === 'types') {
        this.filters.type = value
      } else if (key === 'institutionCountries') {
        this.filters.countryCode = value
      } else if (key === 'languages') {
        this.filters.language = value
      } else if (key === 'openVersions') {
        this.filters.openVersion = value
      } else if (key === 'licenses') {
        this.filters.license = value
      } else if (key === 'sourceTypes') {
        this.filters.sourceType = value
      } else if (key === 'indexedIn') {
        this.filters.indexedIn = value
      } else if (key === 'topics') {
        this.filters.topicId = value
        this.filters.topicName = label
        this.filters.topicSelectedName = label
      } else if (key === 'sources') {
        this.filters.sourceId = value
        this.filters.sourceName = label
        this.filters.sourceSelectedName = label
      } else if (key === 'institutions') {
        this.filters.institutionId = value
        this.filters.institutionName = label
        this.filters.institutionSelectedName = label
      } else if (key === 'funders') {
        this.filters.funderId = value
        this.filters.funderName = label
        this.filters.funderSelectedName = label
      } else if (key === 'sdgs') {
        this.filters.sdgId = value
        this.filters.sdgName = label
      } else if (key === 'domains') {
        this.filters.domainId = value
        this.filters.domainName = label
      } else if (key === 'fields') {
        this.filters.fieldId = value
        this.filters.fieldName = label
      } else if (key === 'subfields') {
        this.filters.subfieldId = value
        this.filters.subfieldName = label
      } else if (key === 'publishers') {
        this.filters.publisherId = value
        this.filters.publisherName = label
      }
    },
    clearFacetFilter(key) {
      if (key === 'searchMode') {
        this.searchMode = 'keyword'
      } else if (key === 'oaOnly') {
        this.filters.oaOnly = false
      } else if (key === 'hasPdf') {
        this.filters.hasPdf = false
      } else if (key === 'hasFulltext') {
        this.filters.hasFulltext = false
      } else if (key === 'hasDoi') {
        this.filters.hasDoi = false
      } else if (key === 'hasAbstract') {
        this.filters.hasAbstract = false
      } else if (key === 'hasReferences') {
        this.filters.hasReferences = false
      } else if (key === 'hasPmid') {
        this.filters.hasPmid = false
      } else if (key === 'excludeRetracted') {
        this.filters.excludeRetracted = false
      } else if (key === 'excludeParatext') {
        this.filters.excludeParatext = false
      } else if (key === 'years') {
        this.filters.fromYear = ''
        this.filters.toYear = ''
      } else if (key === 'citations') {
        this.filters.minCitations = ''
        this.filters.maxCitations = ''
      } else if (key === 'oaStatuses') {
        this.filters.oaStatus = ''
      } else if (key === 'types') {
        this.filters.type = ''
      } else if (key === 'authors') {
        this.filters.authorId = ''
        this.filters.authorName = ''
        this.filters.authorSelectedName = ''
      } else if (key === 'institutionCountries') {
        this.filters.countryCode = ''
      } else if (key === 'languages') {
        this.filters.language = ''
      } else if (key === 'openVersions') {
        this.filters.openVersion = ''
      } else if (key === 'licenses') {
        this.filters.license = ''
      } else if (key === 'sourceTypes') {
        this.filters.sourceType = ''
      } else if (key === 'indexedIn') {
        this.filters.indexedIn = ''
      } else if (key === 'impactLevel') {
        this.filters.impactLevel = ''
      } else if (key === 'topics') {
        this.filters.topicId = ''
        this.filters.topicName = ''
        this.filters.topicSelectedName = ''
      } else if (key === 'sources') {
        this.filters.sourceId = ''
        this.filters.sourceName = ''
        this.filters.sourceSelectedName = ''
      } else if (key === 'institutions') {
        this.filters.institutionId = ''
        this.filters.institutionName = ''
        this.filters.institutionSelectedName = ''
      } else if (key === 'keywords') {
        this.filters.keywordId = ''
        this.filters.keywordName = ''
        this.filters.keywordSelectedName = ''
      } else if (key === 'funders') {
        this.filters.funderId = ''
        this.filters.funderName = ''
        this.filters.funderSelectedName = ''
      } else if (key === 'sdgs') {
        this.filters.sdgId = ''
        this.filters.sdgName = ''
      } else if (key === 'domains') {
        this.filters.domainId = ''
        this.filters.domainName = ''
      } else if (key === 'fields') {
        this.filters.fieldId = ''
        this.filters.fieldName = ''
      } else if (key === 'subfields') {
        this.filters.subfieldId = ''
        this.filters.subfieldName = ''
      } else if (key === 'publishers') {
        this.filters.publisherId = ''
        this.filters.publisherName = ''
      } else if (key === 'citesWork') {
        this.filters.citesWorkId = ''
        this.filters.citesWorkName = ''
      }
    },
    facetFilterValue(key, item) {
      if (key === 'types') {
        return this.openAlexId(item.label || item.key || '')
      }
      if (key === 'topics' || key === 'sources' || key === 'institutions' || key === 'funders' || key === 'sdgs' || key === 'domains' || key === 'fields' || key === 'subfields' || key === 'publishers' || key === 'institutionCountries' || key === 'languages' || key === 'openVersions' || key === 'licenses' || key === 'sourceTypes' || key === 'indexedIn') {
        return this.openAlexId(item.key || item.label || '')
      }
      return String(item.key || item.label || '')
    },
    facetLabelByValue(sectionKey, value) {
      const section = this.facetSections.find(item => item.key === sectionKey)
      const bucket = section && section.items.find(item => this.facetFilterValue(sectionKey, item) === value)
      return bucket ? this.facetItemLabel(sectionKey, bucket) : ''
    },
    openAlexId(value) {
      const text = String(value || '').trim()
      const index = text.lastIndexOf('/')
      const raw = index >= 0 ? text.slice(index + 1) : text
      try {
        return decodeURIComponent(raw)
      } catch (e) {
        return raw
      }
    },
    countryText(code, fallback) {
      const option = this.countryOptions.find(item => item.value === code)
      return option ? option.label : (fallback || code || '未知')
    },
    languageText(code) {
      const option = this.languageOptions.find(item => item.value === code)
      return option ? option.label : (code || '未知')
    },
    suggestionTypeText(entityType) {
      const map = {
        work: '论文',
        author: '作者',
        source: '来源',
        institution: '机构',
        topic: '主题',
        keyword: '关键词',
        publisher: '出版商',
        funder: '资助方'
      }
      return map[entityType] || ''
    },
    searchModeText(mode) {
      const option = this.searchModeOptions.find(item => item.value === mode)
      return option ? option.label : '关键词'
    },
    sortText(sort) {
      const map = {
        relevance: '相关性优先',
        cited: '引用数优先',
        newest: '最新优先',
        oldest: '最早优先'
      }
      return map[sort] || sort || '相关性优先'
    },
    providerText(provider) {
      const map = {
        openalex: 'OpenAlex',
        crossref: 'Crossref',
        semantic: 'Semantic Scholar',
        semantic_scholar: 'Semantic Scholar'
      }
      return map[String(provider || '').toLowerCase()] || provider || 'OpenAlex'
    },
    async fetchFacets(fallbackFacets) {
      const requestId = ++this.facetsRequestId
      this.facetsLoading = true
      try {
        const facets = await getScholarFacets(this.buildFacetParams())
        if (requestId === this.facetsRequestId) {
          this.facets = this.normalizeFacets(facets)
        }
      } catch (e) {
        if (requestId === this.facetsRequestId && fallbackFacets) {
          this.facets = this.normalizeFacets(fallbackFacets)
        }
      } finally {
        if (requestId === this.facetsRequestId) {
          this.facetsLoading = false
        }
      }
    },
    emptyFacets() {
      return {
        years: [],
        types: [],
        oaStatuses: [],
        topics: [],
        institutionCountries: [],
        sources: [],
        institutions: [],
        languages: [],
        openVersions: [],
        licenses: [],
        sourceTypes: [],
        indexedIn: [],
        funders: [],
        sdgs: [],
        domains: [],
        fields: [],
        subfields: [],
        publishers: []
      }
    },
    normalizeFacets(facets) {
      return Object.assign(this.emptyFacets(), facets || {})
    },
    facetsFromResults(results) {
      const works = results || []
      const facets = this.emptyFacets()
      facets.years = this.countFacet(works
        .filter(work => work.publicationYear)
        .map(work => ({ key: String(work.publicationYear), label: String(work.publicationYear) })))
        .sort((a, b) => Number(b.key) - Number(a.key))
      facets.types = this.countFacet(works
        .filter(work => work.type)
        .map(work => ({ key: work.type, label: this.formatType(work.type) })))
      facets.oaStatuses = this.countFacet(works
        .filter(work => work.oaStatus)
        .map(work => ({ key: work.oaStatus, label: this.oaStatusText(work.oaStatus) })))
      facets.languages = this.countFacet(works
        .filter(work => work.language)
        .map(work => ({ key: work.language, label: this.languageText(work.language) })))
      facets.sources = this.countFacet(works
        .filter(work => work.sourceId || work.sourceName)
        .map(work => ({ key: work.sourceId || work.sourceName, label: work.sourceName || work.sourceId })))
      facets.topics = this.countFacet(works.flatMap(work => (work.topics || [])
        .filter(topic => topic && (topic.id || topic.name))
        .map(topic => ({ key: topic.id || topic.name, label: topic.name || topic.id }))))
      facets.institutions = this.countFacet(works.flatMap(work => (work.institutions || [])
        .filter(item => item && (item.id || item.name))
        .map(item => ({ key: item.id || item.name, label: item.name || item.id }))))
      facets.indexedIn = this.countFacet(works.flatMap(work => {
        const values = work.extra && Array.isArray(work.extra.indexedIn) ? work.extra.indexedIn : []
        return values.map(value => ({ key: value, label: this.indexedInText(value) }))
      }))
      return facets
    },
    countFacet(items) {
      const map = new Map()
      ;(items || []).forEach(item => {
        if (!item || !item.key) {
          return
        }
        const key = String(item.key)
        const current = map.get(key) || { key, label: item.label || key, count: 0 }
        current.count += 1
        map.set(key, current)
      })
      return Array.from(map.values()).sort((a, b) => b.count - a.count)
    },
    workSelectionKey(work) {
      return (work && (work.workKey || this.firstProviderId(work) || work.doi || work.title)) || ''
    },
    isWorkSelected(work) {
      const key = this.workSelectionKey(work)
      return !!key && this.selectedWorkKeys.includes(key)
    },
    toggleWorkSelection(work, checked) {
      const key = this.workSelectionKey(work)
      if (!key) {
        return
      }
      if (checked) {
        if (!this.selectedWorkKeys.includes(key)) {
          this.selectedWorks = this.selectedWorks.concat([work])
        }
        return
      }
      this.selectedWorks = this.selectedWorks.filter(item => this.workSelectionKey(item) !== key)
    },
    toggleCurrentPageSelection(checked) {
      if (checked) {
        const merged = this.selectedWorks.slice()
        const keys = merged.map(work => this.workSelectionKey(work))
        this.visibleResults.forEach(work => {
          const key = this.workSelectionKey(work)
          if (key && !keys.includes(key)) {
            merged.push(work)
            keys.push(key)
          }
        })
        this.selectedWorks = merged
      } else {
        const currentKeys = this.visibleResults.map(work => this.workSelectionKey(work))
        this.selectedWorks = this.selectedWorks.filter(work => !currentKeys.includes(this.workSelectionKey(work)))
      }
    },
    clearSelection() {
      this.selectedWorks = []
    },
    screeningStorageKey() {
      return 'scholar-screening-marks-v1'
    },
    loadScreeningMarks() {
      try {
        const raw = window.localStorage.getItem(this.screeningStorageKey())
        const parsed = raw ? JSON.parse(raw) : {}
        this.screeningMarks = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
      } catch (e) {
        this.screeningMarks = {}
      }
    },
    persistScreeningMarks() {
      try {
        window.localStorage.setItem(this.screeningStorageKey(), JSON.stringify(this.screeningMarks || {}))
      } catch (e) {
        // Local screening marks are a convenience; ignore storage quota/private-mode failures.
      }
    },
    screeningMark(work) {
      const key = this.workSelectionKey(work)
      return key ? this.screeningMarks[key] : null
    },
    screeningDecisionText(decision) {
      const map = {
        include: '纳入',
        exclude: '排除',
        maybe: '可能相关',
        pending: '待定'
      }
      return map[decision] || '待定'
    },
    screeningClass(work) {
      const mark = this.screeningMark(work)
      return mark && mark.decision ? `is-${mark.decision}` : 'is-pending'
    },
    setScreeningView(value) {
      if (!this.screeningViewOptions.some(item => item.value === value)) {
        return
      }
      this.screeningView = value
    },
    screeningReviewerName() {
      return this.userInfo || ''
    },
    setScreeningMark(work, patch) {
      const key = this.workSelectionKey(work)
      if (!key) {
        return
      }
      const previous = this.screeningMarks[key] || {}
      const mark = {
        decision: 'pending',
        excludeReason: '',
        notes: '',
        reviewer: this.screeningReviewerName(),
        ...previous,
        ...patch,
        work: this.screeningWorkSnapshot(work) || previous.work || null,
        updatedAt: new Date().toISOString()
      }
      this.$set(this.screeningMarks, key, mark)
      this.persistScreeningMarks()
    },
    clearScreeningMark(work) {
      const key = this.workSelectionKey(work)
      if (!key || !this.screeningMarks[key]) {
        return
      }
      this.$delete(this.screeningMarks, key)
      this.persistScreeningMarks()
      this.$message.success('已清除初筛标记')
    },
    async handleScreeningCommand(work, command) {
      if (!work || !command) {
        return
      }
      if (command === 'clear') {
        this.clearScreeningMark(work)
        return
      }
      if (command === 'exclude') {
        const current = this.screeningMark(work) || {}
        const result = await this.$prompt('填写排除原因，导出筛选表时会写入 Exclude Reason 列。', '排除原因', {
          confirmButtonText: '保存',
          cancelButtonText: '取消',
          inputValue: current.excludeReason || '',
          inputPlaceholder: '例如：主题不相关、非研究论文、无全文等'
        }).catch(() => null)
        if (!result) {
          return
        }
        this.setScreeningMark(work, {
          decision: 'exclude',
          excludeReason: this.cleanText(result.value || ''),
          reviewer: this.screeningReviewerName()
        })
        this.$message.success('已标记为排除')
        return
      }
      if (command === 'note') {
        const current = this.screeningMark(work) || {}
        const result = await this.$prompt('填写初筛备注，导出筛选表时会写入 Notes 列。', '初筛备注', {
          confirmButtonText: '保存',
          cancelButtonText: '取消',
          inputValue: current.notes || '',
          inputType: 'textarea',
          inputPlaceholder: '例如：方法相关，需阅读全文确认样本/指标'
        }).catch(() => null)
        if (!result) {
          return
        }
        this.setScreeningMark(work, {
          decision: current.decision || 'pending',
          notes: this.cleanText(result.value || ''),
          reviewer: this.screeningReviewerName()
        })
        this.$message.success('初筛备注已保存')
        return
      }
      const decisionMap = {
        include: 'include',
        maybe: 'maybe',
        pending: 'pending'
      }
      if (!decisionMap[command]) {
        return
      }
      this.setScreeningMark(work, {
        decision: decisionMap[command],
        excludeReason: command === 'include' ? '' : (this.screeningMark(work) && this.screeningMark(work).excludeReason) || '',
        reviewer: this.screeningReviewerName()
      })
      this.$message.success(`已标记为${this.screeningDecisionText(decisionMap[command])}`)
    },
    async handleBatchScreeningCommand(command) {
      const works = (this.selectedWorks || []).filter(Boolean)
      if (!works.length || !command) {
        return
      }
      if (command === 'clear') {
        works.forEach(work => {
          const key = this.workSelectionKey(work)
          if (key && this.screeningMarks[key]) {
            this.$delete(this.screeningMarks, key)
          }
        })
        this.persistScreeningMarks()
        this.$message.success(`已清除 ${works.length} 篇文献的初筛标记`)
        return
      }
      if (command === 'exclude') {
        const result = await this.$prompt(`将 ${works.length} 篇已选文献标记为排除，并填写统一排除原因。`, '批量排除', {
          confirmButtonText: '保存',
          cancelButtonText: '取消',
          inputPlaceholder: '例如：主题不相关、非研究论文、无全文等'
        }).catch(() => null)
        if (!result) {
          return
        }
        const excludeReason = this.cleanText(result.value || '')
        works.forEach(work => {
          this.setScreeningMark(work, {
            decision: 'exclude',
            excludeReason,
            reviewer: this.screeningReviewerName()
          })
        })
        this.$message.success(`已排除 ${works.length} 篇文献`)
        return
      }
      if (command === 'note') {
        const result = await this.$prompt(`为 ${works.length} 篇已选文献填写统一初筛备注。`, '批量备注', {
          confirmButtonText: '保存',
          cancelButtonText: '取消',
          inputType: 'textarea',
          inputPlaceholder: '例如：与研究问题相关，需阅读全文确认纳入标准'
        }).catch(() => null)
        if (!result) {
          return
        }
        const notes = this.cleanText(result.value || '')
        works.forEach(work => {
          const current = this.screeningMark(work) || {}
          this.setScreeningMark(work, {
            decision: current.decision || 'pending',
            notes,
            reviewer: this.screeningReviewerName()
          })
        })
        this.$message.success(`已为 ${works.length} 篇文献保存备注`)
        return
      }
      const decisionMap = {
        include: 'include',
        maybe: 'maybe',
        pending: 'pending'
      }
      if (!decisionMap[command]) {
        return
      }
      works.forEach(work => {
        const current = this.screeningMark(work) || {}
        this.setScreeningMark(work, {
          decision: decisionMap[command],
          excludeReason: command === 'include' ? '' : current.excludeReason || '',
          reviewer: this.screeningReviewerName()
        })
      })
      this.$message.success(`已将 ${works.length} 篇文献标记为${this.screeningDecisionText(decisionMap[command])}`)
    },
    handleBatchMoreCommand(command) {
      if (command === 'copy-reading') {
        this.copySelectedReadingList()
        return
      }
      if (command === 'copy-priority') {
        this.copyReadingPriorityList()
        return
      }
      if (command === 'download-priority') {
        this.downloadReadingPriorityList()
        return
      }
      if (command === 'copy-identifiers') {
        this.copySelectedIdentifiers()
        return
      }
      if (command === 'download-identifiers') {
        this.downloadSelectedIdentifiers()
      }
    },
    toggleBatchPanel(type) {
      if (type === 'more') {
        this.batchMoreVisible = !this.batchMoreVisible
        this.batchScreeningVisible = false
        return
      }
      if (type === 'screening') {
        this.batchScreeningVisible = !this.batchScreeningVisible
        this.batchMoreVisible = false
      }
    },
    runBatchMoreCommand(command) {
      this.batchMoreVisible = false
      this.handleBatchMoreCommand(command)
    },
    runBatchScreeningCommand(command) {
      this.batchScreeningVisible = false
      this.handleBatchScreeningCommand(command)
    },
    handleScreeningRecordCommand(command) {
      if (command === 'copy-summary') {
        this.copyScreeningSummary()
        return
      }
      if (command === 'download-summary') {
        this.downloadScreeningSummary()
        return
      }
      if (command === 'export-current') {
        this.exportScreeningPackage('current')
        return
      }
      if (command === 'export-all') {
        this.exportScreeningPackage('all')
        return
      }
      if (command === 'import') {
        this.triggerScreeningImport()
      }
    },
    handleResultsMoreCommand(command) {
      const actionMap = {
        'copy-strategy': () => this.copySearchStrategy(),
        'strategy-markdown': () => this.downloadSearchStrategy('markdown'),
        'strategy-json': () => this.downloadSearchStrategy('json'),
        'copy-quality': () => this.copyQualityReport(),
        'quality-markdown': () => this.downloadQualityReport('markdown'),
        'quality-csv': () => this.downloadQualityReport('csv'),
        'screening-copy-summary': () => this.handleScreeningRecordCommand('copy-summary'),
        'screening-download-summary': () => this.handleScreeningRecordCommand('download-summary'),
        'screening-export-current': () => this.handleScreeningRecordCommand('export-current'),
        'screening-export-all': () => this.handleScreeningRecordCommand('export-all'),
        'screening-import': () => this.handleScreeningRecordCommand('import'),
        'copy-bulk-report': () => this.copyBulkImportReport(),
        'bulk-report-markdown': () => this.downloadBulkImportReport('markdown'),
        'bulk-report-csv': () => this.downloadBulkImportReport('csv')
      }
      const action = actionMap[command]
      if (action) {
        action()
      }
    },
    toggleResultsMorePanel() {
      this.cleanupLegacyScholarExportMenus()
      this.resultsMoreVisible = !this.resultsMoreVisible
      if (this.resultsMoreVisible) {
        this.exportPanelVisible = false
      }
    },
    runResultsMoreCommand(command) {
      this.resultsMoreVisible = false
      this.handleResultsMoreCommand(command)
    },
    toggleExportPanel() {
      this.cleanupLegacyScholarExportMenus()
      this.exportPanelVisible = !this.exportPanelVisible
      if (this.exportPanelVisible) {
        this.resultsMoreVisible = false
      }
    },
    runExportCommand(command) {
      this.cleanupLegacyScholarExportMenus()
      this.exportPanelVisible = false
      this.exportResults(command)
    },
    cardActionKey(work, type) {
      const key = this.workSelectionKey(work) || this.firstProviderId(work) || (work && work.title) || ''
      return key ? `${type}:${key}` : ''
    },
    isCardActionPanelOpen(work, type) {
      const key = this.cardActionKey(work, type)
      return !!key && this.activeCardActionKey === key
    },
    toggleCardActionPanel(work, type) {
      const key = this.cardActionKey(work, type)
      if (!key) {
        return
      }
      this.activeCardActionKey = this.activeCardActionKey === key ? '' : key
    },
    runCardScreeningCommand(work, command) {
      this.activeCardActionKey = ''
      this.handleScreeningCommand(work, command)
    },
    runCardExpandSearch(work, key) {
      this.activeCardActionKey = ''
      this.applyWorkExpandSearch(work, key)
    },
    screeningKnownWorks() {
      const map = new Map()
      const addWork = work => {
        const key = this.workSelectionKey(work)
        if (key && !map.has(key)) {
          map.set(key, work)
        }
      }
      ;(this.results || []).forEach(addWork)
      ;(this.selectedWorks || []).forEach(addWork)
      ;(this.savedWorks || []).forEach(item => addWork(item && item.work))
      return map
    },
    screeningWorkSnapshot(work) {
      if (!work) {
        return null
      }
      return {
        workKey: this.workSelectionKey(work),
        title: work.title || '',
        doi: work.doi || '',
        year: work.publicationYear || '',
        source: work.sourceName || '',
        providerId: this.firstProviderId(work) || '',
        url: work.pdfUrl || work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work) || ''
      }
    },
    screeningPackageRecords(scope) {
      const knownWorks = this.screeningKnownWorks()
      const keys = scope === 'current'
        ? (this.results || []).map(work => this.workSelectionKey(work)).filter(key => key && this.screeningMarks[key])
        : Object.keys(this.screeningMarks || {})
      return keys.map(key => {
        const mark = this.screeningMarks[key] || {}
        const work = knownWorks.get(key) || null
        const snapshot = this.screeningWorkSnapshot(work) || mark.work || null
        return {
          workKey: key,
          decision: mark.decision || 'pending',
          decisionText: this.screeningDecisionText(mark.decision),
          excludeReason: mark.excludeReason || '',
          notes: mark.notes || '',
          reviewer: mark.reviewer || '',
          updatedAt: mark.updatedAt || '',
          work: snapshot,
          relationContext: mark.relationContext || null
        }
      })
    },
    screeningPackage(scope) {
      const records = this.screeningPackageRecords(scope)
      return {
        version: 1,
        source: 'scholar-search-screening',
        scope,
        exportedAt: new Date().toISOString(),
        search: {
          query: this.keyword || '',
          mode: this.searchModeText(this.searchMode),
          sort: this.sortText(this.filters.sort),
          filters: this.activeFilterTags.map(item => item.label),
          link: this.currentSearchLink()
        },
        stats: {
          recordCount: records.length,
          currentPageResultCount: (this.results || []).length,
          totalResultCount: this.total || 0
        },
        records
      }
    },
    exportScreeningPackage(scope) {
      const records = this.screeningPackageRecords(scope)
      if (!records.length) {
        this.$message.warning(scope === 'current' ? '当前页暂无初筛记录' : '暂无可导出的初筛记录')
        return
      }
      const content = JSON.stringify(this.screeningPackage(scope), null, 2)
      const filename = this.exportResultsFilename(scope === 'current' ? 'screening-current' : 'screening-all', 'records', 'json')
      this.downloadText(content, filename, 'application/json;charset=utf-8')
      this.$message.success(`已导出 ${records.length} 条初筛记录`)
    },
    screeningRecordLabel(record) {
      const work = record && record.work ? record.work : {}
      return work.title || work.doi || work.providerId || (record && record.workKey) || '未知文献'
    },
    screeningSummaryMarkdown() {
      const records = this.screeningPackageRecords('all')
      const count = key => records.filter(record => record.decision === key).length
      const missingExcludeReasons = records.filter(record => record.decision === 'exclude' && !record.excludeReason)
      const pendingRecords = records.filter(record => record.decision === 'pending')
      const lines = [
        '# OA 文献初筛摘要',
        '',
        `- 记录数：${records.length}`,
        `- 纳入：${count('include')}`,
        `- 可能相关：${count('maybe')}`,
        `- 待定：${count('pending')}`,
        `- 排除：${count('exclude')}`,
        `- 生成时间：${new Date().toLocaleString()}`,
        `- 当前检索词：${this.keyword || '未填写'}`,
        `- 当前检索链接：${this.currentSearchLink()}`,
        '',
        '## 说明',
        '- 本摘要基于当前浏览器本地保存或导入的人工初筛记录生成。',
        '- 文献题名、DOI、来源等仍以检索页和三方接口返回的元数据为准；导入记录缺少题名时会显示 workKey。',
        '',
        '## 初筛结果'
      ]
      if (!records.length) {
        lines.push('- 暂无初筛记录')
      } else {
        ;['include', 'maybe', 'pending', 'exclude'].forEach(decision => {
          const group = records.filter(record => record.decision === decision)
          if (!group.length) {
            return
          }
          lines.push('', `### ${this.screeningDecisionText(decision)}（${group.length}）`)
          group.forEach((record, index) => {
            const work = record.work || {}
            const meta = [work.year, work.source, record.reviewer ? `筛选人 ${record.reviewer}` : ''].filter(Boolean).join(' · ')
            lines.push(`${index + 1}. ${this.screeningRecordLabel(record)}`)
            if (meta) {
              lines.push(`   - 元数据：${meta}`)
            }
            if (work.doi) {
              lines.push(`   - DOI：${work.doi}`)
            }
            if (work.url) {
              lines.push(`   - 链接：${work.url}`)
            }
            if (record.relationContext) {
              lines.push(`   - 来源线索：${this.screeningRelationContextText(record.relationContext)}`)
            }
            if (record.excludeReason) {
              lines.push(`   - 排除原因：${record.excludeReason}`)
            }
            if (record.notes) {
              lines.push(`   - 备注：${record.notes}`)
            }
          })
        })
      }
      lines.push('', '## 待回补')
      if (!missingExcludeReasons.length && !pendingRecords.length) {
        lines.push('- 暂无明显待回补项')
      } else {
        if (missingExcludeReasons.length) {
          lines.push(`- ${missingExcludeReasons.length} 条排除记录缺少排除原因`)
        }
        if (pendingRecords.length) {
          lines.push(`- ${pendingRecords.length} 条记录仍为待定`)
        }
      }
      return lines.join('\n')
    },
    async copyScreeningSummary() {
      if (!this.screeningMarkCount) {
        this.$message.warning('暂无初筛记录')
        return
      }
      try {
        await this.copyText(this.screeningSummaryMarkdown())
        this.$message.success('初筛摘要已复制')
      } catch (e) {
        this.$message.error('复制失败，请稍后重试')
      }
    },
    downloadScreeningSummary() {
      if (!this.screeningMarkCount) {
        this.$message.warning('暂无初筛记录')
        return
      }
      this.downloadText(
        this.screeningSummaryMarkdown(),
        this.exportResultsFilename('screening-summary', 'markdown', 'md'),
        'text/markdown;charset=utf-8'
      )
      this.$message.success('初筛摘要已导出')
    },
    triggerScreeningImport() {
      const input = this.$refs.screeningImportInput
      if (input) {
        input.value = ''
        input.click()
      }
    },
    importScreeningPackage(event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null
      if (!file) {
        return
      }
      const reader = new FileReader()
      reader.onload = async () => {
        try {
          const payload = JSON.parse(String(reader.result || '{}'))
          const imported = this.normalizeScreeningImport(payload)
          if (!imported.length) {
            this.$message.warning('文件中没有可导入的初筛记录')
            return
          }
          const summary = await this.applyScreeningImport(imported)
          this.$message.success(this.screeningImportSummaryText(summary))
        } catch (e) {
          this.$message.error('初筛记录文件格式不正确')
        }
      }
      reader.onerror = () => {
        this.$message.error('读取初筛记录失败')
      }
      reader.readAsText(file)
    },
    async applyScreeningImport(imported) {
      const plan = this.screeningImportPlan(imported)
      let overwriteConflicts = false
      if (plan.conflicts.length) {
        overwriteConflicts = await this.confirmScreeningConflictOverwrite(plan).catch(() => false)
      }
      const enriches = plan.unchanged.filter(item => {
        const existing = this.screeningMarks[item.workKey] || {}
        return item.mark && item.mark.work && !existing.work
      })
      const recordsToApply = plan.creates.concat(overwriteConflicts ? plan.conflicts.map(item => item.incoming) : [])
      enriches.forEach(item => {
        this.$set(this.screeningMarks, item.workKey, {
          ...(this.screeningMarks[item.workKey] || {}),
          work: item.mark.work
        })
      })
      recordsToApply.forEach(item => {
        this.$set(this.screeningMarks, item.workKey, item.mark)
      })
      if (recordsToApply.length || enriches.length) {
        this.persistScreeningMarks()
      }
      return {
        created: plan.creates.length,
        enriched: enriches.length,
        unchanged: plan.unchanged.length,
        overwritten: overwriteConflicts ? plan.conflicts.length : 0,
        keptConflicts: overwriteConflicts ? 0 : plan.conflicts.length
      }
    },
    screeningImportPlan(imported) {
      const unique = new Map()
      ;(imported || []).forEach(item => {
        if (item && item.workKey) {
          unique.set(item.workKey, item)
        }
      })
      const creates = []
      const unchanged = []
      const conflicts = []
      Array.from(unique.values()).forEach(item => {
        const existing = this.screeningMarks[item.workKey]
        if (!existing) {
          creates.push(item)
          return
        }
        if (this.sameScreeningMark(existing, item.mark)) {
          unchanged.push(item)
          return
        }
        conflicts.push({ incoming: item, existing })
      })
      return { creates, unchanged, conflicts }
    },
    sameScreeningMark(left, right) {
      return ['decision', 'excludeReason', 'notes', 'reviewer'].every(key => {
        return this.cleanText(left && left[key]) === this.cleanText(right && right[key])
      })
    },
    confirmScreeningConflictOverwrite(plan) {
      const preview = plan.conflicts.slice(0, 3).map(item => {
        const key = item.incoming.workKey
        const oldDecision = this.screeningDecisionText(item.existing && item.existing.decision)
        const newDecision = this.screeningDecisionText(item.incoming.mark && item.incoming.mark.decision)
        return `${key}：本地 ${oldDecision}，导入 ${newDecision}`
      }).join('\n')
      const suffix = plan.conflicts.length > 3 ? `\n另有 ${plan.conflicts.length - 3} 条冲突。` : ''
      return this.$confirm(
        `检测到 ${plan.conflicts.length} 条初筛冲突，是否用导入记录覆盖本地记录？\n\n${preview}${suffix}`,
        '初筛记录冲突',
        {
          confirmButtonText: '覆盖冲突',
          cancelButtonText: '保留本地',
          type: 'warning'
        }
      ).then(() => true)
    },
    screeningImportSummaryText(summary) {
      const parts = []
      if (summary.created) {
        parts.push(`新增 ${summary.created} 条`)
      }
      if (summary.overwritten) {
        parts.push(`覆盖冲突 ${summary.overwritten} 条`)
      }
      if (summary.enriched) {
        parts.push(`补全元数据 ${summary.enriched} 条`)
      }
      if (summary.keptConflicts) {
        parts.push(`保留本地冲突 ${summary.keptConflicts} 条`)
      }
      if (summary.unchanged) {
        parts.push(`相同跳过 ${summary.unchanged} 条`)
      }
      return parts.length ? `初筛记录导入完成：${parts.join('，')}` : '初筛记录无变化'
    },
    normalizeScreeningImport(payload) {
      if (Array.isArray(payload && payload.records)) {
        return payload.records.map(record => this.normalizeScreeningRecord(record)).filter(Boolean)
      }
      if (payload && payload.marks && typeof payload.marks === 'object') {
        return Object.keys(payload.marks).map(key => this.normalizeScreeningRecord({
          workKey: key,
          ...(payload.marks[key] || {})
        })).filter(Boolean)
      }
      return []
    },
    normalizeScreeningRecord(record) {
      const workKey = this.cleanText(record && (record.workKey || record.key || (record.work && record.work.workKey)))
      if (!workKey) {
        return null
      }
      const decision = ['include', 'exclude', 'maybe', 'pending'].includes(record.decision) ? record.decision : 'pending'
      return {
        workKey,
        mark: {
          decision,
          excludeReason: this.cleanText(record.excludeReason || ''),
          notes: this.cleanText(record.notes || record.note || ''),
          reviewer: this.cleanText(record.reviewer || this.screeningReviewerName()),
          work: this.normalizeScreeningWorkSnapshot(record.work || record.workSnapshot || { workKey }),
          relationContext: this.normalizeScreeningRelationContext(record.relationContext),
          updatedAt: this.cleanText(record.updatedAt || '') || new Date().toISOString()
        }
      }
    },
    normalizeScreeningWorkSnapshot(work) {
      if (!work || typeof work !== 'object') {
        return null
      }
      return {
        workKey: this.cleanText(work.workKey || ''),
        title: this.cleanText(work.title || ''),
        doi: this.cleanText(work.doi || ''),
        year: this.cleanText(work.year || work.publicationYear || ''),
        source: this.cleanText(work.source || work.sourceName || ''),
        providerId: this.cleanText(work.providerId || ''),
        url: this.cleanText(work.url || work.pdfUrl || work.landingPageUrl || '')
      }
    },
    normalizeScreeningRelationContext(context) {
      if (!context || typeof context !== 'object') {
        return null
      }
      return {
        relationKey: this.cleanText(context.relationKey || ''),
        relationLabel: this.cleanText(context.relationLabel || ''),
        sourceWork: this.normalizeScreeningWorkSnapshot(context.sourceWork || {})
      }
    },
    screeningRelationContextText(context) {
      if (!context) {
        return ''
      }
      const source = context.sourceWork || {}
      const label = context.relationLabel || context.relationKey || '关联文献'
      const sourceTitle = source.title || source.doi || source.providerId || source.workKey || '当前文献'
      return `${label} · 来源：${sourceTitle}`
    },
    openCompareDialog() {
      if (this.selectedWorks.length < 2) {
        this.$message.warning('请选择至少 2 篇文献进行对比')
        return
      }
      this.compareVisible = true
    },
    compareTableRows(works) {
      return (works || []).filter(Boolean).map(work => {
        const topics = (work.topics || []).map(item => item && item.name).filter(Boolean).slice(0, 6).join('；')
        const url = work.pdfUrl || work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)
        const oaEvidence = this.openAccessEvidence(work)
        return {
          title: work.title || 'Untitled work',
          authors: this.formatAuthors(work.authors || [], 12) || '未知作者',
          year: work.publicationYear || '',
          source: work.sourceName || '未知来源',
          type: this.formatType(work.type) || '',
          citations: work.citedByCount || 0,
          oa: work.isOa ? this.oaStatusText(work.oaStatus) : '否',
          oaVersion: oaEvidence.version || '',
          oaLicense: oaEvidence.license || '',
          oaSource: [oaEvidence.sourceName, oaEvidence.sourceType].filter(Boolean).join(' · '),
          oaEvidence: this.openAccessEvidenceChips(work).join('；'),
          pdf: work.pdfUrl ? '有' : '无',
          doi: work.doi || '',
          topics,
          abstract: this.truncateText(work.abstractText || '', 320),
          url
        }
      })
    },
    compareTableCsv(works) {
      const headers = ['Title', 'Authors', 'Year', 'Source', 'Type', 'Citations', 'Open Access', 'OA Version', 'OA License', 'OA Source', 'OA Evidence', 'PDF', 'DOI', 'Topics', 'Abstract', 'URL']
      const rows = this.compareTableRows(works).map(item => [
        item.title,
        item.authors,
        item.year,
        item.source,
        item.type,
        item.citations,
        item.oa,
        item.oaVersion,
        item.oaLicense,
        item.oaSource,
        item.oaEvidence,
        item.pdf,
        item.doi,
        item.topics,
        item.abstract,
        item.url
      ])
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    markdownCell(value) {
      return String(value === null || value === undefined ? '' : value)
        .replace(/\|/g, '\\|')
        .replace(/\r?\n/g, '<br>')
    },
    compareTableMarkdown(works) {
      const rows = this.compareTableRows(works)
      const lines = [
        '# 已选文献对比表',
        '',
        `共 ${rows.length} 篇`,
        '',
        '| 题名 | 作者 | 年份 | 来源 | 类型 | 引用 | OA | OA 证据 | PDF | DOI | 主题 |',
        '| --- | --- | --- | --- | --- | ---: | --- | --- | --- | --- | --- |'
      ]
      rows.forEach(item => {
        lines.push([
          item.title,
          item.authors,
          item.year || '-',
          item.source,
          item.type || '-',
          this.formatNumber(item.citations || 0),
          item.oa,
          item.oaEvidence || '-',
          item.pdf,
          item.doi || '-',
          item.topics || '-'
        ].map(value => this.markdownCell(value)).join(' | ').replace(/^/, '| ').replace(/$/, ' |'))
      })
      lines.push('', '## 摘要与链接')
      rows.forEach((item, index) => {
        lines.push(`### ${index + 1}. ${item.title}`)
        lines.push(`- 摘要：${item.abstract || '暂无摘要'}`)
        if (item.url) {
          lines.push(`- 链接：${item.url}`)
        }
        lines.push('')
      })
      return lines.join('\n')
    },
    async copyCompareTable() {
      if (!this.selectedWorks.length) {
        this.$message.warning('请先选择文献')
        return
      }
      try {
        await this.copyText(this.compareTableMarkdown(this.selectedWorks))
        this.$message.success(`已复制 ${this.selectedWorks.length} 篇文献对比表`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadCompareTable(format) {
      if (!this.selectedWorks.length) {
        this.$message.warning('请先选择文献')
        return
      }
      const exportFormat = format === 'markdown' ? 'markdown' : 'csv'
      const content = exportFormat === 'markdown'
        ? this.compareTableMarkdown(this.selectedWorks)
        : `\uFEFF${this.compareTableCsv(this.selectedWorks)}`
      this.downloadText(
        content,
        `scholar-compare.${exportFormat === 'markdown' ? 'md' : 'csv'}`,
        exportFormat === 'markdown' ? 'text/markdown;charset=utf-8' : 'text/csv;charset=utf-8'
      )
      this.$message.success(`已导出 ${this.selectedWorks.length} 篇文献对比表`)
    },
    facetExportRows() {
      return this.facetSections
        .filter(section => section.items && section.items.length)
        .flatMap(section => {
          const sectionTotal = section.items.reduce((sum, item) => sum + Number(item.count || 0), 0)
          return section.items.map((item, index) => {
            const count = Number(item.count || 0)
            const share = sectionTotal ? (count / sectionTotal) * 100 : 0
            return {
              section: section.title,
              rank: index + 1,
              label: this.facetItemLabel(section.key, item),
              key: this.facetFilterValue(section.key, item),
              count,
              share,
              shareText: `${this.formatDecimal(share, 2)}%`
            }
          })
        })
    },
    facetSummaryMarkdown() {
      const rowsBySection = this.facetSections
        .filter(section => section.items && section.items.length)
        .map(section => ({
          section,
          rows: this.facetExportRows().filter(row => row.section === section.title)
        }))
      const lines = [
        '# OA 文献聚类统计',
        '',
        `- 检索词：${this.keyword || '未填写'}`,
        `- 检索模式：${this.searchModeText(this.searchMode)}`,
        `- 结果数：${this.formatNumber(this.total || 0)}`,
        `- 排序：${this.sortText(this.filters.sort)}`,
        `- 记录时间：${new Date().toLocaleString()}`,
        `- 检索链接：${this.currentSearchLink()}`
      ]
      if (this.activeFilterTags.length) {
        lines.push(`- 筛选条件：${this.activeFilterTags.map(item => item.label).join('；')}`)
      } else {
        lines.push('- 筛选条件：无额外筛选')
      }
      rowsBySection.forEach(({ section, rows }) => {
        lines.push('', `## ${section.title}`, '', '| 排名 | 项目 | 数量 | 占比 |', '| ---: | --- | ---: | ---: |')
        rows.forEach(row => {
          lines.push(`| ${row.rank} | ${this.markdownCell(row.label)} | ${this.formatNumber(row.count)} | ${row.shareText} |`)
        })
      })
      lines.push('', '## 说明', '- 聚类统计来自当前检索条件下的三方元数据聚合结果，数据会随三方来源更新而变化。')
      return lines.join('\n')
    },
    facetSummaryCsv() {
      const filterText = this.activeFilterTags.map(item => item.label).join('; ')
      const rows = this.facetExportRows().map(row => [
        this.keyword || '',
        this.searchModeText(this.searchMode),
        filterText,
        this.total || 0,
        row.section,
        row.rank,
        row.label,
        row.key,
        row.count,
        row.shareText
      ])
      const headers = ['Query', 'Search Mode', 'Filters', 'Total Results', 'Facet Section', 'Rank', 'Label', 'Key', 'Count', 'Section Share']
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    async copyFacetSummary() {
      if (!this.hasFacetData) {
        this.$message.warning('当前没有可复制的聚类统计')
        return
      }
      try {
        await this.copyText(this.facetSummaryMarkdown())
        this.$message.success('聚类统计已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadFacetSummary(format) {
      if (!this.hasFacetData) {
        this.$message.warning('当前没有可导出的聚类统计')
        return
      }
      const exportFormat = format === 'markdown' ? 'markdown' : 'csv'
      const content = exportFormat === 'markdown'
        ? this.facetSummaryMarkdown()
        : `\uFEFF${this.facetSummaryCsv()}`
      this.downloadText(
        content,
        `scholar-facets.${exportFormat === 'markdown' ? 'md' : 'csv'}`,
        exportFormat === 'markdown' ? 'text/markdown;charset=utf-8' : 'text/csv;charset=utf-8'
      )
      this.$message.success('聚类统计已导出')
    },
    selectedReadingListText(works) {
      const items = (works || []).filter(Boolean)
      const lines = [
        '# 文献阅读清单',
        '',
        `共 ${items.length} 篇`,
        ''
      ]
      items.forEach((work, index) => {
        const authors = this.formatAuthors(work.authors || [], 8) || '未知作者'
        const source = work.sourceName || '未知来源'
        const year = work.publicationYear || '未知年份'
        const citations = Number.isFinite(Number(work.citedByCount)) ? this.formatNumber(work.citedByCount) : '0'
        const doiUrl = this.doiUrl(work)
        const mainUrl = work.pdfUrl || work.landingPageUrl || doiUrl || this.firstProviderUrl(work)
        lines.push(`## ${index + 1}. ${work.title || 'Untitled work'}`)
        lines.push(`- 作者：${authors}`)
        lines.push(`- 年份：${year}`)
        lines.push(`- 来源：${source}`)
        lines.push(`- 类型：${this.formatType(work.type) || '未知类型'}`)
        lines.push(`- 引用：${citations}`)
        lines.push(`- 开放获取：${work.isOa ? this.oaStatusText(work.oaStatus) : '否'}`)
        if (work.doi) {
          lines.push(`- DOI：${doiUrl || work.doi}`)
        }
        if (mainUrl) {
          lines.push(`- 链接：${mainUrl}`)
        }
        if (work.abstractText) {
          lines.push(`- 摘要：${this.truncateText(work.abstractText, 260)}`)
        }
        lines.push('')
      })
      return lines.join('\n')
    },
    savedReadingListText(items) {
      const savedItems = (items || []).filter(item => item && item.work)
      const lines = [
        '# 收藏文献阅读清单',
        '',
        `共 ${savedItems.length} 篇`,
        ''
      ]
      savedItems.forEach((item, index) => {
        const work = item.work || {}
        const authors = this.formatAuthors(work.authors || [], 8) || '未知作者'
        const source = work.sourceName || '未知来源'
        const doiUrl = this.doiUrl(work)
        const mainUrl = work.pdfUrl || work.landingPageUrl || doiUrl || this.firstProviderUrl(work)
        const tags = this.cleanTags(item.tags || [])
        lines.push(`## ${index + 1}. ${work.title || 'Untitled work'}`)
        lines.push(`- 作者：${authors}`)
        lines.push(`- 年份：${work.publicationYear || '未知年份'}`)
        lines.push(`- 来源：${source}`)
        lines.push(`- 类型：${this.formatType(work.type) || '未知类型'}`)
        lines.push(`- 引用：${this.formatNumber(work.citedByCount || 0)}`)
        lines.push(`- 开放获取：${work.isOa ? this.oaStatusText(work.oaStatus) : '否'}`)
        if (tags.length) {
          lines.push(`- 收藏标签：${tags.join('、')}`)
        }
        if (item.note) {
          lines.push(`- 收藏备注：${this.cleanText(item.note)}`)
        }
        if (work.doi) {
          lines.push(`- DOI：${doiUrl || work.doi}`)
        }
        if (mainUrl) {
          lines.push(`- 链接：${mainUrl}`)
        }
        if (work.abstractText) {
          lines.push(`- 摘要：${this.truncateText(work.abstractText, 260)}`)
        }
        lines.push('')
      })
      return lines.join('\n')
    },
    async copySelectedReadingList() {
      if (!this.selectedWorks.length) {
        this.$message.warning('请先选择文献')
        return
      }
      try {
        await this.copyText(this.selectedReadingListText(this.selectedWorks))
        this.$message.success(`已复制 ${this.selectedWorks.length} 篇文献清单`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    readingPriorityRows(works) {
      const currentYear = new Date().getFullYear()
      return (works || []).filter(Boolean)
        .map(work => {
          const citations = Number(work.citedByCount || 0)
          const year = Number(work.publicationYear || 0)
          const reasons = []
          const cautions = []
          let score = 0
          if (work.pdfUrl) {
            score += 24
            reasons.push('有 PDF')
          }
          if (work.isOa) {
            score += 18
            reasons.push(this.oaStatusText(work.oaStatus) || '开放获取')
          }
          if (this.hasFulltext(work)) {
            score += 10
            reasons.push('可全文检索')
          }
          if (this.cleanText(work.abstractText)) {
            score += 14
            reasons.push('有摘要')
          }
          if (work.doi) {
            score += 10
            reasons.push('有 DOI')
          }
          if (citations > 0) {
            score += Math.min(28, Math.log10(citations + 1) * 9)
            reasons.push(`引用 ${this.formatNumber(citations)}`)
          }
          if (year && currentYear >= year) {
            const age = currentYear - year
            if (age <= 3) {
              score += 10
              reasons.push('近 3 年')
            } else if (age <= 8) {
              score += 5
              reasons.push('近 8 年')
            }
          }
          if (this.isRetracted(work)) {
            score -= 50
            cautions.push('已撤稿，引用前必须核对')
          }
          if (this.isParatext(work)) {
            score -= 24
            cautions.push('非正文内容')
          }
          if (!work.pdfUrl) {
            cautions.push('暂无 PDF')
          }
          if (!this.cleanText(work.abstractText)) {
            cautions.push('缺摘要')
          }
          return {
            work,
            score: Math.max(0, Math.round(score)),
            citations,
            year,
            reasons,
            cautions,
            url: work.pdfUrl || work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)
          }
        })
        .sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score
          }
          if (b.citations !== a.citations) {
            return b.citations - a.citations
          }
          return (b.year || 0) - (a.year || 0)
        })
        .map((row, index) => ({ ...row, rank: index + 1 }))
    },
    readingPriorityMarkdown(works) {
      const rows = this.readingPriorityRows(works)
      const lines = [
        '# 已选文献阅读优先级清单',
        '',
        `共 ${rows.length} 篇`,
        '',
        '说明：优先级只基于当前已选文献的三方元数据计算，综合开放获取、PDF、摘要、DOI、引用、发表年份、全文可检索、撤稿和非正文标记；它不是论文质量评价。',
        ''
      ]
      rows.forEach(row => {
        const work = row.work || {}
        const authors = this.formatAuthors(work.authors || [], 8) || '未知作者'
        lines.push(`## ${row.rank}. ${work.title || 'Untitled work'}`)
        lines.push(`- 优先级分：${row.score}`)
        lines.push(`- 作者：${authors}`)
        lines.push(`- 年份：${work.publicationYear || '未知年份'}`)
        lines.push(`- 来源：${work.sourceName || '未知来源'}`)
        lines.push(`- 推荐依据：${row.reasons.length ? row.reasons.join('、') : '暂无明显元数据优势'}`)
        if (row.cautions.length) {
          lines.push(`- 核对提醒：${row.cautions.join('、')}`)
        }
        if (work.doi) {
          lines.push(`- DOI：${this.doiUrl(work) || work.doi}`)
        }
        if (row.url) {
          lines.push(`- 链接：${row.url}`)
        }
        if (work.abstractText) {
          lines.push(`- 摘要：${this.truncateText(work.abstractText, 260)}`)
        }
        lines.push('')
      })
      return lines.join('\n')
    },
    async copyReadingPriorityList() {
      if (!this.selectedWorks.length) {
        this.$message.warning('请先选择文献')
        return
      }
      try {
        await this.copyText(this.readingPriorityMarkdown(this.selectedWorks))
        this.$message.success(`已复制 ${this.selectedWorks.length} 篇阅读优先级清单`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadReadingPriorityList() {
      if (!this.selectedWorks.length) {
        this.$message.warning('请先选择文献')
        return
      }
      this.downloadText(this.readingPriorityMarkdown(this.selectedWorks), 'scholar-reading-priority.md', 'text/markdown;charset=utf-8')
      this.$message.success('阅读优先级清单已导出')
    },
    selectedIdentifierRows(works) {
      return (works || []).filter(Boolean).map((work, index) => {
        const doiUrl = this.doiUrl(work)
        return {
          index: index + 1,
          title: work.title || 'Untitled work',
          authors: this.formatAuthors(work.authors || [], 12) || '未知作者',
          year: work.publicationYear || '',
          source: work.sourceName || '',
          doi: work.doi || '',
          doiUrl,
          pdfUrl: work.pdfUrl || '',
          landingPageUrl: work.landingPageUrl || '',
          providerUrl: this.firstProviderUrl(work) || '',
          bestUrl: work.pdfUrl || work.landingPageUrl || doiUrl || this.firstProviderUrl(work)
        }
      })
    },
    selectedIdentifiersMarkdown(works) {
      const rows = this.selectedIdentifierRows(works)
      const doiRows = rows.filter(row => row.doi)
      const lines = [
        '# 已选文献标识与链接清单',
        '',
        `共 ${rows.length} 篇；其中有 DOI ${doiRows.length} 篇`,
        '',
        '## DOI 列表'
      ]
      if (doiRows.length) {
        doiRows.forEach(row => {
          lines.push(`- ${row.doi}`)
        })
      } else {
        lines.push('- 当前已选文献暂无 DOI')
      }
      lines.push('', '## 详细链接')
      rows.forEach(row => {
        lines.push(`### ${row.index}. ${row.title}`)
        lines.push(`- 作者：${row.authors}`)
        lines.push(`- 年份：${row.year || '未知年份'}`)
        lines.push(`- 来源：${row.source || '未知来源'}`)
        lines.push(`- DOI：${row.doi || '暂无 DOI'}`)
        if (row.doiUrl) {
          lines.push(`- DOI URL：${row.doiUrl}`)
        }
        if (row.pdfUrl) {
          lines.push(`- PDF：${row.pdfUrl}`)
        }
        if (row.landingPageUrl) {
          lines.push(`- 原文页面：${row.landingPageUrl}`)
        }
        if (row.providerUrl) {
          lines.push(`- 三方详情：${row.providerUrl}`)
        }
        if (!row.bestUrl) {
          lines.push('- 链接：暂无可用链接')
        }
        lines.push('')
      })
      lines.push('## 说明', '- 本清单只整理当前已选文献已返回的 DOI、PDF、落地页和三方详情链接，链接可用性以三方站点实际访问为准。')
      return lines.join('\n')
    },
    selectedIdentifiersCsv(works) {
      const headers = ['Index', 'Title', 'Authors', 'Year', 'Source', 'DOI', 'DOI URL', 'PDF URL', 'Landing Page URL', 'Provider URL', 'Best URL']
      const rows = this.selectedIdentifierRows(works).map(row => [
        row.index,
        row.title,
        row.authors,
        row.year,
        row.source,
        row.doi,
        row.doiUrl,
        row.pdfUrl,
        row.landingPageUrl,
        row.providerUrl,
        row.bestUrl
      ])
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    async copySelectedIdentifiers() {
      if (!this.selectedWorks.length) {
        this.$message.warning('请先选择文献')
        return
      }
      try {
        await this.copyText(this.selectedIdentifiersMarkdown(this.selectedWorks))
        this.$message.success(`已复制 ${this.selectedWorks.length} 篇文献标识`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadSelectedIdentifiers() {
      if (!this.selectedWorks.length) {
        this.$message.warning('请先选择文献')
        return
      }
      this.downloadText(`\uFEFF${this.selectedIdentifiersCsv(this.selectedWorks)}`, 'scholar-selected-identifiers.csv', 'text/csv;charset=utf-8')
      this.$message.success('文献标识清单已导出')
    },
    async copyWorkReadingItem(work) {
      if (!work) {
        return
      }
      try {
        await this.copyText(this.selectedReadingListText([this.expandedWork(work) || work]))
        this.$message.success('文献条目已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    async copyWorkDoi(work) {
      const doi = work && work.doi ? String(work.doi).trim() : ''
      if (!doi) {
        this.$message.warning('暂无 DOI')
        return
      }
      try {
        await this.copyText(doi)
        this.$message.success('DOI 已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    async copySavedReadingList() {
      const items = this.filteredSavedWorks || []
      if (!items.length) {
        this.$message.warning('当前没有可复制的收藏文献')
        return
      }
      try {
        await this.copyText(this.savedReadingListText(items))
        this.$message.success(`已复制 ${items.length} 篇收藏文献清单`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    savedOrganizerWorks() {
      return (this.filteredSavedWorks || []).map(item => item && item.work).filter(Boolean)
    },
    handleSavedOrganizerCommand(command) {
      if (command === 'copy-identifiers') {
        this.copySavedIdentifiers()
        return
      }
      if (command === 'download-identifiers') {
        this.downloadSavedIdentifiers()
        return
      }
      if (command === 'copy-priority') {
        this.copySavedReadingPriority()
        return
      }
      if (command === 'download-priority') {
        this.downloadSavedReadingPriority()
      }
    },
    runSavedOrganizerCommand(command) {
      this.savedOrganizerVisible = false
      this.handleSavedOrganizerCommand(command)
    },
    async copySavedIdentifiers() {
      const works = this.savedOrganizerWorks()
      if (!works.length) {
        this.$message.warning('当前没有可复制的收藏文献')
        return
      }
      try {
        await this.copyText(this.selectedIdentifiersMarkdown(works))
        this.$message.success(`已复制 ${works.length} 篇收藏文献标识`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadSavedIdentifiers() {
      const works = this.savedOrganizerWorks()
      if (!works.length) {
        this.$message.warning('当前没有可导出的收藏文献')
        return
      }
      this.downloadText(`\uFEFF${this.selectedIdentifiersCsv(works)}`, 'scholar-saved-identifiers.csv', 'text/csv;charset=utf-8')
      this.$message.success('收藏文献标识清单已导出')
    },
    async copySavedReadingPriority() {
      const works = this.savedOrganizerWorks()
      if (!works.length) {
        this.$message.warning('当前没有可复制的收藏文献')
        return
      }
      try {
        await this.copyText(this.readingPriorityMarkdown(works))
        this.$message.success(`已复制 ${works.length} 篇收藏文献阅读优先级`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadSavedReadingPriority() {
      const works = this.savedOrganizerWorks()
      if (!works.length) {
        this.$message.warning('当前没有可导出的收藏文献')
        return
      }
      this.downloadText(this.readingPriorityMarkdown(works), 'scholar-saved-reading-priority.md', 'text/markdown;charset=utf-8')
      this.$message.success('收藏文献阅读优先级已导出')
    },
    async copySavedWorkNote(item) {
      if (!item || !item.work) {
        return
      }
      try {
        await this.copyText(this.savedReadingListText([item]))
        this.$message.success('收藏文献笔记已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    async querySearchSuggestions(value, callback) {
      const q = (value || '').trim()
      if (!q) {
        const history = await this.loadSearchHistory(false)
        callback(this.searchHistorySuggestionItems(history))
        return
      }
      if (q.length < 2) {
        callback(this.searchHistorySuggestionItems(this.searchHistory)
          .filter(item => item.value.toLowerCase().includes(q.toLowerCase())))
        return
      }
      try {
        const suggestions = await suggestScholar(q)
        callback((suggestions || []).slice(0, 8).map(item => ({
          value: item.title || item.externalId || item.id,
          id: item.id,
          hint: [item.hint, item.citedByCount ? `引 ${this.formatNumber(item.citedByCount)}` : ''].filter(Boolean).join(' · '),
          typeLabel: this.suggestionTypeText(item.entityType),
          raw: item
        })).filter(item => item.value))
      } catch (e) {
        callback([])
      }
    },
    handleSuggestionSelect(item) {
      if (!item || !item.value) {
        return
      }
      const raw = item.raw || {}
      if (raw.entityType === 'history') {
        this.applyHistoryQuery(item.value)
        return
      }
      if (raw.entityType === 'work' && raw.id) {
        this.$router.push({
          path: '/scholar/detail',
          query: {
            provider: 'openalex',
            id: this.openAlexId(raw.id),
            q: this.keyword || item.value
          }
        })
        return
      }
      if (this.applySuggestionEntityFilter(raw, item)) {
        return
      }
      this.keyword = item.value
      this.submitSearch()
    },
    applySuggestionEntityFilter(raw, item) {
      const fields = this.suggestionEntityFilterFields(raw && raw.entityType)
      const id = this.openAlexId((raw && raw.id) || (item && item.id) || '')
      if (!fields || !id) {
        return false
      }
      const [nameField, idField, selectedField] = fields
      const name = this.cleanText(
        (raw && (raw.title || raw.displayName || raw.display_name || raw.name || raw.externalId)) ||
        (item && item.value) ||
        id
      )
      this.keyword = ''
      this.searchMode = 'keyword'
      this.bulkImportActive = false
      this.bulkImportSummary = null
      this.filters = this.defaultScholarFilters()
      this.filters[idField] = id
      this.filters[nameField] = name || id
      if (selectedField) {
        this.filters[selectedField] = name || id
      }
      this.submitSearch()
      return true
    },
    suggestionEntityFilterFields(entityType) {
      const map = {
        author: ['authorName', 'authorId', 'authorSelectedName'],
        authors: ['authorName', 'authorId', 'authorSelectedName'],
        institution: ['institutionName', 'institutionId', 'institutionSelectedName'],
        institutions: ['institutionName', 'institutionId', 'institutionSelectedName'],
        source: ['sourceName', 'sourceId', 'sourceSelectedName'],
        sources: ['sourceName', 'sourceId', 'sourceSelectedName'],
        topic: ['topicName', 'topicId', 'topicSelectedName'],
        topics: ['topicName', 'topicId', 'topicSelectedName'],
        keyword: ['keywordName', 'keywordId', 'keywordSelectedName'],
        keywords: ['keywordName', 'keywordId', 'keywordSelectedName'],
        publisher: ['publisherName', 'publisherId', ''],
        publishers: ['publisherName', 'publisherId', ''],
        funder: ['funderName', 'funderId', 'funderSelectedName'],
        funders: ['funderName', 'funderId', 'funderSelectedName']
      }
      return map[String(entityType || '').toLowerCase()] || null
    },
    defaultAdvancedSearchForm() {
      return {
        searchMode: this.searchMode || 'keyword',
        keyword: this.keyword || '',
        exactPhrase: '',
        authorName: '',
        authorId: '',
        authorSelectedName: '',
        institutionName: '',
        institutionId: '',
        institutionSelectedName: '',
        sourceName: '',
        sourceId: '',
        sourceSelectedName: '',
        topicName: '',
        topicId: '',
        topicSelectedName: '',
        fromYear: '',
        toYear: '',
        minCitations: '',
        maxCitations: '',
        type: '',
        oaStatus: '',
        impactLevel: '',
        sort: 'relevance',
        oaOnly: false,
        hasPdf: false,
        hasFulltext: false,
        hasDoi: false,
        hasAbstract: false,
        hasReferences: false,
        hasPmid: false
      }
    },
    toggleAdvancedSearchPanel() {
      if (this.advancedSearchVisible) {
        this.advancedSearchVisible = false
        return
      }
      this.syncAdvancedSearchForm()
      this.advancedSearchVisible = true
    },
    syncAdvancedSearchForm() {
      this.advancedForm = {
        ...this.defaultAdvancedSearchForm(),
        searchMode: this.searchMode || 'keyword',
        keyword: this.keyword || '',
        authorName: this.filters.authorName || '',
        authorId: this.filters.authorId || '',
        authorSelectedName: this.filters.authorSelectedName || '',
        institutionName: this.filters.institutionName || '',
        institutionId: this.filters.institutionId || '',
        institutionSelectedName: this.filters.institutionSelectedName || '',
        sourceName: this.filters.sourceName || '',
        sourceId: this.filters.sourceId || '',
        sourceSelectedName: this.filters.sourceSelectedName || '',
        topicName: this.filters.topicName || '',
        topicId: this.filters.topicId || '',
        topicSelectedName: this.filters.topicSelectedName || '',
        fromYear: this.filters.fromYear || '',
        toYear: this.filters.toYear || '',
        minCitations: this.filters.minCitations || '',
        maxCitations: this.filters.maxCitations || '',
        type: this.filters.type || '',
        oaStatus: this.filters.oaStatus || '',
        impactLevel: this.filters.impactLevel || '',
        sort: this.filters.sort || 'relevance',
        oaOnly: !!this.filters.oaOnly,
        hasPdf: !!this.filters.hasPdf,
        hasFulltext: !!this.filters.hasFulltext,
        hasDoi: !!this.filters.hasDoi,
        hasAbstract: !!this.filters.hasAbstract,
        hasReferences: !!this.filters.hasReferences,
        hasPmid: !!this.filters.hasPmid
      }
    },
    resetAdvancedSearchForm() {
      this.advancedForm = this.defaultAdvancedSearchForm()
    },
    applyAdvancedSearch() {
      const form = this.advancedForm || {}
      const exactPhrase = this.cleanText(form.exactPhrase)
      this.keyword = exactPhrase || this.cleanText(form.keyword)
      this.searchMode = exactPhrase ? 'exact' : (form.searchMode || 'keyword')
      const nextFilters = this.defaultScholarFilters()
      ;[
        'authorName',
        'authorId',
        'authorSelectedName',
        'institutionName',
        'institutionId',
        'institutionSelectedName',
        'sourceName',
        'sourceId',
        'sourceSelectedName',
        'topicName',
        'topicId',
        'topicSelectedName',
        'fromYear',
        'toYear',
        'minCitations',
        'maxCitations',
        'type',
        'oaStatus',
        'impactLevel'
      ].forEach(key => {
        nextFilters[key] = form[key] || ''
      })
      ;['oaOnly', 'hasPdf', 'hasFulltext', 'hasDoi', 'hasAbstract', 'hasReferences', 'hasPmid'].forEach(key => {
        nextFilters[key] = !!form[key]
      })
      nextFilters.sort = form.sort || 'relevance'
      this.filters = nextFilters
      this.advancedSearchVisible = false
      this.filtersOpen = true
      this.submitSearch()
    },
    advancedEntityFields(key) {
      const map = {
        author: ['authorName', 'authorId', 'authorSelectedName'],
        institution: ['institutionName', 'institutionId', 'institutionSelectedName'],
        source: ['sourceName', 'sourceId', 'sourceSelectedName'],
        topic: ['topicName', 'topicId', 'topicSelectedName']
      }
      return map[key] || []
    },
    handleAdvancedEntityInput(key, value) {
      const [nameField, idField, selectedField] = this.advancedEntityFields(key)
      if (!nameField) {
        return
      }
      this.advancedForm[nameField] = value || ''
      if ((value || '') !== (this.advancedForm[selectedField] || '')) {
        this.advancedForm[idField] = ''
      }
    },
    handleAdvancedEntitySelect(key, item) {
      const [nameField, idField, selectedField] = this.advancedEntityFields(key)
      if (!nameField || !item) {
        return
      }
      this.advancedForm[nameField] = item.value || ''
      this.advancedForm[idField] = item.id || ''
      this.advancedForm[selectedField] = item.value || ''
    },
    queryAuthorSuggestions(value, callback) {
      this.queryEntitySuggestions('authors', value, callback)
    },
    queryInstitutionSuggestions(value, callback) {
      this.queryEntitySuggestions('institutions', value, callback)
    },
    querySourceSuggestions(value, callback) {
      this.queryEntitySuggestions('sources', value, callback)
    },
    queryTopicSuggestions(value, callback) {
      this.queryEntitySuggestions('topics', value, callback)
    },
    queryKeywordSuggestions(value, callback) {
      this.queryEntitySuggestions('keywords', value, callback)
    },
    queryFunderSuggestions(value, callback) {
      this.queryEntitySuggestions('funders', value, callback)
    },
    async queryEntitySuggestions(entity, value, callback) {
      const q = (value || '').trim()
      if (q.length < 2) {
        callback([])
        return
      }
      try {
        const suggestions = await suggestScholar(q, entity)
        callback((suggestions || []).slice(0, 8).map(item => ({
          value: item.title || item.externalId || item.id,
          id: item.id,
          hint: item.hint || item.externalId || item.entityType,
          raw: item
        })).filter(item => item.value && item.id))
      } catch (e) {
        callback([])
      }
    },
    entityFilterFields(key) {
      const map = {
        author: ['authorName', 'authorId', 'authorSelectedName'],
        institution: ['institutionName', 'institutionId', 'institutionSelectedName'],
        source: ['sourceName', 'sourceId', 'sourceSelectedName'],
        topic: ['topicName', 'topicId', 'topicSelectedName'],
        keyword: ['keywordName', 'keywordId', 'keywordSelectedName'],
        funder: ['funderName', 'funderId', 'funderSelectedName']
      }
      return map[key] || []
    },
    handleEntityFilterInput(key, value) {
      const [nameField, idField, selectedField] = this.entityFilterFields(key)
      if (!nameField) {
        return
      }
      if ((value || '') !== (this.filters[selectedField] || '')) {
        this.filters[idField] = ''
      }
    },
    handleEntityFilterSelect(key, item) {
      const [nameField, idField, selectedField] = this.entityFilterFields(key)
      if (!nameField || !item) {
        return
      }
      this.filters[nameField] = item.value || ''
      this.filters[idField] = item.id || ''
      this.filters[selectedField] = item.value || ''
    },
    async handlePageChange(page) {
      if (page === this.page || this.loading) {
        return
      }
      if (page > this.maxPageablePages) {
        this.$message.warning('最多支持翻看前 10,000 条结果，请缩小年份、主题、来源等筛选条件后继续检索')
        return
      }
      this.page = page
      this.nextCursor = ''
      await this.fetchResults(false, { refreshFacets: false })
      this.replaceRouteQuery()
      this.scrollResultsIntoView()
    },
    goRelativePage(delta) {
      const nextPage = Math.min(this.totalPages, Math.max(1, Number(this.page) + delta))
      if (nextPage !== this.page) {
        this.handlePageChange(nextPage)
      }
    },
    async handleSizeChange(size) {
      if (size === this.size || this.loading) {
        return
      }
      this.size = size
      this.page = 1
      this.nextCursor = ''
      await this.fetchResults(false, { refreshFacets: false })
      this.replaceRouteQuery()
      this.scrollResultsIntoView()
    },
    scrollResultsIntoView() {
      this.$nextTick(() => {
        const main = this.$el && this.$el.querySelector('.scholar-main')
        if (main && typeof main.scrollIntoView === 'function') {
          main.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    defaultScholarFilters() {
      return {
        oaOnly: false,
        hasPdf: false,
        hasFulltext: false,
        hasDoi: false,
        hasAbstract: false,
        hasReferences: false,
        hasPmid: false,
        excludeRetracted: false,
        excludeParatext: false,
        fromYear: '',
        toYear: '',
        type: '',
        language: '',
        countryCode: '',
        oaStatus: '',
        openVersion: '',
        license: '',
        sourceType: '',
        indexedIn: '',
        impactLevel: '',
        minCitations: '',
        maxCitations: '',
        authorName: '',
        authorId: '',
        authorSelectedName: '',
        institutionName: '',
        institutionId: '',
        institutionSelectedName: '',
        sourceName: '',
        sourceId: '',
        sourceSelectedName: '',
        topicName: '',
        topicId: '',
        topicSelectedName: '',
        keywordName: '',
        keywordId: '',
        keywordSelectedName: '',
        funderName: '',
        funderId: '',
        funderSelectedName: '',
        sdgId: '',
        sdgName: '',
        domainId: '',
        domainName: '',
        fieldId: '',
        fieldName: '',
        subfieldId: '',
        subfieldName: '',
        publisherId: '',
        publisherName: '',
        citesWorkId: '',
        citesWorkName: '',
        sort: 'relevance'
      }
    },
    resetFilters() {
      this.filters = this.defaultScholarFilters()
      if (this.keyword && this.keyword.trim()) {
        this.submitSearch()
      } else {
        this.resetSearchState()
      }
    },
    canSearchCitingWorks(work) {
      return !!(work && Number(work.citedByCount || 0) > 0 && this.firstProviderId(work))
    },
    searchCitingWorks(work) {
      const id = this.firstProviderId(work)
      if (!id) {
        this.$message.warning('暂无可检索的文献 ID')
        return
      }
      this.keyword = ''
      this.searchMode = 'keyword'
      this.bulkImportActive = false
      this.bulkImportSummary = null
      this.filters = this.defaultScholarFilters()
      this.filters.citesWorkId = id
      this.filters.citesWorkName = work && work.title ? work.title : id
      this.filters.sort = 'cited'
      this.submitSearch()
    },
    workExpandSearchOptions(work) {
      if (!work) {
        return []
      }
      const options = []
      const used = []
      const push = (key, label, hint, query) => {
        if (!key || used.includes(key) || !query) {
          return
        }
        used.push(key)
        options.push({ key, label, hint, query })
      }
      const workId = this.firstProviderId(work)
      if (workId && Number(work.citedByCount || 0) > 0) {
        push('cites', '引用本文的文献', '按引用数优先', {
          citesWorkId: workId,
          citesWorkName: work.title || workId
        })
      }
      if (work.sourceId) {
        push(`source:${work.sourceId}`, '同来源高引文献', work.sourceName || '', {
          sourceId: work.sourceId,
          sourceName: work.sourceName || ''
        })
      }
      ;(work.authors || []).filter(item => item && item.id).slice(0, 2).forEach(author => {
        push(`author:${author.id}`, `作者作品：${author.name || author.id}`, '按作者筛选', {
          authorId: author.id,
          authorName: author.name || ''
        })
      })
      ;(work.topics || []).filter(item => item && item.id).slice(0, 3).forEach(topic => {
        push(`topic:${topic.id}`, `同主题：${topic.name || topic.id}`, [topic.field, topic.domain].filter(Boolean).join(' · '), {
          topicId: topic.id,
          topicName: topic.name || ''
        })
      })
      ;(work.institutions || []).filter(item => item && item.id).slice(0, 2).forEach(institution => {
        push(`institution:${institution.id}`, `机构作品：${institution.name || institution.id}`, institution.countryCode || '', {
          institutionId: institution.id,
          institutionName: institution.name || ''
        })
      })
      return options
    },
    applyWorkExpandSearch(work, key) {
      const option = this.workExpandSearchOptions(work).find(item => item.key === key)
      if (!option) {
        return
      }
      this.keyword = ''
      this.searchMode = 'keyword'
      this.bulkImportActive = false
      this.bulkImportSummary = null
      this.filters = this.defaultScholarFilters()
      Object.keys(option.query || {}).forEach(field => {
        if (this.filters[field] !== undefined) {
          this.filters[field] = option.query[field]
        }
      })
      this.filters.sort = 'cited'
      this.submitSearch()
    },
    openWorkDetailPage(work) {
      const ref = this.firstProviderRef(work)
      if (!ref || !ref.provider || !ref.providerId) {
        this.$message.warning('暂无可查看详情')
        return
      }
      const query = {
        provider: ref.provider,
        id: ref.providerId
      }
      if (this.keyword) {
        query.q = this.keyword
      }
      Object.assign(query, this.currentSearchReturnQuery())
      const route = this.$router.resolve({
        path: '/scholar/detail',
        query
      })
      openScholarRouteInNewTab(route)
    },
    openEntityDetail(entityType, id, name, work) {
      if (!id) {
        return
      }
      const ref = this.firstProviderRef(work)
      const route = this.$router.resolve({
        path: '/scholar/entity',
        query: {
          provider: ref && ref.provider ? ref.provider : 'openalex',
          entityType,
          id,
          name: name || '',
          q: this.keyword || '',
          ...this.currentSearchReturnQuery()
        }
      })
      openScholarRouteInNewTab(route)
    },
    async openWorkDetail(work) {
      this.selectedWork = work
      this.detailVisible = true
      const ref = this.firstProviderRef(work)
      if (!ref || !ref.provider || !ref.providerId) {
        return
      }
      try {
        this.selectedWork = await getScholarWork(ref.provider, ref.providerId)
      } catch (e) {
      }
    },
    async toggleWorkExpanded(work) {
      if (!work || !work.workKey) {
        return
      }
      const key = work.workKey
      if (this.isWorkExpanded(work)) {
        this.$delete(this.expandedWorkKeys, key)
        return
      }
      this.$set(this.expandedWorkKeys, key, true)
      if (this.expandedWorkDetails[key]) {
        return
      }
      const ref = this.firstProviderRef(work)
      if (!ref || !ref.provider || !ref.providerId) {
        return
      }
      this.$set(this.expandedWorkLoading, key, true)
      try {
        const detail = await getScholarWork(ref.provider, ref.providerId)
        this.$set(this.expandedWorkDetails, key, detail)
      } catch (e) {
      } finally {
        this.$delete(this.expandedWorkLoading, key)
      }
    },
    isWorkExpanded(work) {
      return !!(work && work.workKey && this.expandedWorkKeys[work.workKey])
    },
    isWorkDetailLoading(work) {
      return !!(work && work.workKey && this.expandedWorkLoading[work.workKey])
    },
    expandedWork(work) {
      if (!work || !work.workKey) {
        return work || {}
      }
      return this.expandedWorkDetails[work.workKey] || work
    },
    displayAbstract(work) {
      const detail = this.expandedWork(work)
      const text = (detail && detail.abstractText) || (work && work.abstractText) || ''
      return this.isWorkExpanded(work) ? text : this.shorten(text, 260)
    },
    isPdfDownloading(work) {
      const key = this.workSelectionKey(work)
      return !!(key && this.pdfDownloadingKeys[key])
    },
    async downloadWorkPdf(work, event) {
      if (event && typeof event.stopPropagation === 'function') {
        event.stopPropagation()
      }
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault()
      }
      if (!work || !work.pdfUrl) {
        this.$message.warning('暂无可下载 PDF')
        return
      }
      const ref = this.firstProviderRef(work)
      if (!ref || !ref.provider || !ref.providerId) {
        this.$message.warning('暂无可下载 PDF')
        return
      }
      const key = this.workSelectionKey(work)
      if (key && this.pdfDownloadingKeys[key]) {
        return
      }
      if (key) {
        this.$set(this.pdfDownloadingKeys, key, true)
      }
      try {
        const result = await downloadScholarWorkPdf(ref.provider, ref.providerId)
        this.saveBlob(result.blob, result.filename || this.pdfFilename(work))
        this.$message.success('下载已开始')
      } catch (e) {
        await this.handlePdfDownloadFailure(work, e)
      } finally {
        if (key) {
          this.$delete(this.pdfDownloadingKeys, key)
        }
      }
    },
    pdfFallbackUrl(work) {
      return work && (work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)) || ''
    },
    async handlePdfDownloadFailure(work) {
      const pdfUrl = work && work.pdfUrl ? work.pdfUrl : ''
      const fallbackUrl = this.pdfFallbackUrl(work)
      const message = '当前文献暂未返回可直接下载的 PDF，可先复制 PDF 链接，或打开原文页手动下载。'
      if (!pdfUrl && !fallbackUrl) {
        this.$message.warning('当前文献暂未返回可直接下载的 PDF')
        return
      }
      try {
        await this.$confirm(
          message,
          'PDF 下载未完成',
          {
            confirmButtonText: pdfUrl ? '复制 PDF 链接' : '知道了',
            cancelButtonText: fallbackUrl ? '打开原文' : '取消',
            distinguishCancelAndClose: true,
            type: 'warning'
          }
        )
        if (pdfUrl) {
          await this.copyText(pdfUrl)
          this.$message.success('PDF 链接已复制')
        }
      } catch (action) {
        if (action === 'cancel' && fallbackUrl) {
          await this.openExternal(fallbackUrl)
        }
      }
    },
    async downloadSelectedPdfs() {
      if (this.batchPdfDownloading) {
        return
      }
      const works = this.selectedPdfWorks.slice(0, 10)
      if (!works.length) {
        this.$message.warning('已选文献暂无可下载 PDF')
        return
      }
      if (this.selectedPdfWorks.length > works.length) {
        this.$message.info(`为避免浏览器拦截，本次先下载前 ${works.length} 篇 PDF`)
      }
      this.batchPdfDownloading = true
      let success = 0
      let failed = 0
      try {
        for (const work of works) {
          const ref = this.firstProviderRef(work)
          const key = this.workSelectionKey(work)
          if (!ref || !ref.provider || !ref.providerId) {
            failed += 1
            continue
          }
          if (key) {
            this.$set(this.pdfDownloadingKeys, key, true)
          }
          try {
            const result = await downloadScholarWorkPdf(ref.provider, ref.providerId)
            this.saveBlob(result.blob, result.filename || this.pdfFilename(work))
            success += 1
          } catch (e) {
            failed += 1
          } finally {
            if (key) {
              this.$delete(this.pdfDownloadingKeys, key)
            }
          }
        }
        if (success && failed) {
          this.$message.warning(`已开始下载 ${success} 篇 PDF，${failed} 篇下载失败`)
        } else if (success) {
          this.$message.success(`已开始下载 ${success} 篇 PDF`)
        } else {
          this.$message.error('当前已选 PDF 均未能直接下载，请打开原文获取')
        }
      } finally {
        this.batchPdfDownloading = false
      }
    },
    isRetracted(work) {
      return !!(work && work.extra && work.extra.isRetracted)
    },
    isParatext(work) {
      return !!(work && work.extra && work.extra.isParatext)
    },
    hasFulltext(work) {
      return !!(work && work.extra && work.extra.hasFulltext)
    },
    bestOpenAccessLocation(work) {
      const extra = work && work.extra ? work.extra : {}
      const candidates = [
        extra.bestOaLocation,
        extra.primaryLocation
      ].concat(Array.isArray(extra.locations) ? extra.locations : [])
      return candidates.find(item => item && (item.pdfUrl || item.landingPageUrl || item.version || item.license || item.sourceIsInDoaj || item.sourceType)) || null
    },
    openAccessEvidence(work) {
      const location = this.bestOpenAccessLocation(work)
      const extra = work && work.extra ? work.extra : {}
      return {
        location,
        oaStatus: work && work.oaStatus ? this.oaStatusText(work.oaStatus) : '',
        version: location && location.version ? this.openVersionText(location.version) : '',
        license: location && location.license ? this.licenseText(location.license) : '',
        sourceType: location && location.sourceType ? this.sourceTypeText(location.sourceType) : '',
        sourceName: location && location.sourceName ? location.sourceName : '',
        hasPdf: !!(work && work.pdfUrl) || !!(location && location.pdfUrl),
        isDoaj: !!(location && location.sourceIsInDoaj),
        sourceIsOa: !!(location && location.sourceIsOa),
        hasFulltext: !!(extra && extra.hasFulltext),
        locationsCount: extra && extra.locationsCount !== null && extra.locationsCount !== undefined ? extra.locationsCount : ''
      }
    },
    openAccessEvidenceChips(work) {
      const evidence = this.openAccessEvidence(work)
      const chips = []
      if (evidence.oaStatus && evidence.oaStatus !== '未知') {
        chips.push(evidence.oaStatus)
      }
      if (evidence.version && evidence.version !== '未知') {
        chips.push(evidence.version)
      }
      if (evidence.license && evidence.license !== '未知') {
        chips.push(evidence.license)
      }
      if (evidence.hasPdf) {
        chips.push('PDF')
      }
      if (evidence.hasFulltext) {
        chips.push('全文可检索')
      }
      if (evidence.isDoaj) {
        chips.push('DOAJ')
      }
      if (evidence.locationsCount) {
        chips.push(`${this.formatNumber(evidence.locationsCount)} 个位置`)
      }
      return chips.slice(0, 6)
    },
    impactBadge(work) {
      const extra = work && work.extra ? work.extra : {}
      const percentile = extra.citationNormalizedPercentile || {}
      if (percentile.isInTop1Percent) {
        return 'Top 1%'
      }
      if (percentile.isInTop10Percent) {
        return 'Top 10%'
      }
      if (typeof extra.fwci === 'number') {
        return `FWCI ${this.formatDecimal(extra.fwci)}`
      }
      return ''
    },
    async toggleSaved(work) {
      if (!work || !work.workKey) {
        return
      }
      if (!this.requireScholarLogin()) {
        return
      }
      if (this.isSaved(work)) {
        try {
          await deleteSavedScholarWork(work.workKey)
          this.savedWorks = this.savedWorks.filter(item => item.work && item.work.workKey !== work.workKey)
          this.$message.success('已取消收藏')
        } catch (e) {
          this.$message.error(this.resolveErrorMessage(e, '收藏操作失败'))
        }
        return
      }
      this.openSaveDialog(work)
    },
    openSaveDialog(work, savedItem) {
      if (!work || !work.workKey) {
        return
      }
      if (!this.requireScholarLogin()) {
        return
      }
      const existing = savedItem || this.savedWorks.find(item => item.work && item.work.workKey === work.workKey) || null
      this.saveDialogWork = work
      this.saveDialogExisting = existing
      this.saveForm = {
        tags: this.cleanTags(existing && existing.tags && existing.tags.length ? existing.tags : ['OA文献']),
        note: existing && existing.note ? existing.note : ''
      }
      this.saveDialogVisible = true
    },
    async submitSaveDialog() {
      if (!this.saveDialogWork || this.saveDialogLoading) {
        return
      }
      this.saveDialogLoading = true
      try {
        const saved = await saveScholarWork(
          this.saveDialogWork,
          this.cleanTags(this.saveForm.tags),
          this.cleanText(this.saveForm.note)
        )
        this.savedWorks = [saved].concat(this.savedWorks.filter(item => item.work && item.work.workKey !== saved.work.workKey))
        this.saveDialogVisible = false
        this.$message.success(this.saveDialogExisting ? '收藏信息已更新' : '已收藏')
      } catch (e) {
        this.$message.error(this.resolveErrorMessage(e, '保存收藏失败'))
      } finally {
        this.saveDialogLoading = false
      }
    },
    async saveSelectedWorks() {
      if (!this.selectedWorks.length || this.savingSelected) {
        return
      }
      if (!this.requireScholarLogin()) {
        return
      }
      const works = this.selectedWorks.filter(work => work && work.workKey && !this.isSaved(work))
      if (!works.length) {
        this.$message.info('已选文献都已收藏')
        return
      }
      this.savingSelected = true
      try {
        const savedItems = []
        for (const work of works) {
          const saved = await saveScholarWork(work, ['OA文献'], '')
          savedItems.push(saved)
        }
        const savedKeys = new Set(savedItems.map(item => item.work && item.work.workKey).filter(Boolean))
        this.savedWorks = savedItems.concat(this.savedWorks.filter(item => !(item.work && savedKeys.has(item.work.workKey))))
        this.$message.success(`已收藏 ${savedItems.length} 篇文献`)
      } catch (e) {
        this.$message.error(this.resolveErrorMessage(e, '批量收藏失败'))
      } finally {
        this.savingSelected = false
      }
    },
    async loadSavedWorks() {
      if (!this.isScholarPersonalUser()) {
        this.savedWorks = []
        return
      }
      try {
        this.savedWorks = await listSavedScholarWorks()
      } catch (e) {
        this.savedWorks = []
      }
    },
    openSavedDialog() {
      if (!this.requireScholarLogin()) {
        return
      }
      this.$router.push('/scholar/saved')
    },
    openSaveSearchDialog() {
      if (!this.requireScholarLogin('请先登录个人账号或子账号后保存检索式')) {
        return
      }
      if (!this.hasSavableSearch()) {
        this.$message.warning('请先输入关键词或设置筛选条件')
        return
      }
      this.saveSearchForm = {
        name: this.defaultSavedSearchName(),
        note: ''
      }
      this.saveSearchDialogVisible = true
    },
    async submitSaveSearchDialog() {
      if (this.saveSearchLoading) {
        return
      }
      const query = this.buildRouteQuery()
      if (!Object.keys(query).length) {
        this.$message.warning('请先输入关键词或设置筛选条件')
        return
      }
      this.saveSearchLoading = true
      try {
        const saved = await saveScholarSearch(
          this.saveSearchForm.name,
          query,
          this.cleanText(this.saveSearchForm.note)
        )
        this.savedSearches = [saved].concat(this.savedSearches.filter(item => item.searchKey !== saved.searchKey))
        this.saveSearchDialogVisible = false
        this.$message.success('检索式已保存')
      } catch (e) {
        this.$message.error(this.resolveErrorMessage(e, '保存检索式失败'))
      } finally {
        this.saveSearchLoading = false
      }
    },
    openSavedSearchesDialog() {
      if (!this.requireScholarLogin('请先登录个人账号或子账号后查看检索式')) {
        return
      }
      this.savedSearchVisible = true
      this.loadSavedSearches()
    },
    async loadSavedSearches() {
      if (!this.isScholarPersonalUser()) {
        this.savedSearches = []
        return
      }
      this.savedSearchLoading = true
      try {
        this.savedSearches = await listSavedScholarSearches()
      } catch (e) {
        this.savedSearches = []
        this.$message.error(this.resolveErrorMessage(e, '检索式加载失败'))
      } finally {
        this.savedSearchLoading = false
      }
    },
    applySavedSearch(item) {
      const query = this.cleanRouteQueryObject(item && item.query)
      if (!Object.keys(query).length) {
        this.$message.warning('检索式缺少查询条件')
        return
      }
      this.savedSearchVisible = false
      this.bulkImportActive = false
      this.bulkImportSummary = null
      if (this.isSameRouteQuery(this.$route.query, query)) {
        this.applyRouteFilters(query)
        this.keyword = this.routeString(query, 'q')
        this.submitSearch({ resetPage: false })
        return
      }
      this.$router.push({ path: '/scholar', query }).catch(() => {})
    },
    async removeSavedSearch(item) {
      if (!item || !item.searchKey) {
        return
      }
      try {
        await deleteSavedScholarSearch(item.searchKey)
        this.savedSearches = this.savedSearches.filter(search => search.searchKey !== item.searchKey)
        this.$message.success('已删除检索式')
      } catch (e) {
        this.$message.error(this.resolveErrorMessage(e, '删除检索式失败'))
      }
    },
    hasSavableSearch() {
      return Object.keys(this.buildRouteQuery()).length > 0
    },
    defaultSavedSearchName() {
      const q = this.cleanText(this.keyword)
      const parts = []
      if (q) {
        parts.push(q)
      }
      if (this.activeFilterTags.length) {
        parts.push(`${this.activeFilterTags.length} 个筛选`)
      }
      return (parts.join(' · ') || '未命名检索').slice(0, 80)
    },
    savedSearchMeta(item) {
      const parts = []
      const query = item && item.query ? item.query : {}
      if (query.q) {
        parts.push(`关键词 ${query.q}`)
      }
      if (query.searchMode) {
        parts.push(this.searchModeText(query.searchMode))
      }
      if (item && item.updatedAt) {
        parts.push(this.formatDateTime(item.updatedAt))
      }
      return parts.join(' · ') || '保存的检索条件'
    },
    savedSearchTags(item) {
      const query = item && item.query ? item.query : {}
      return this.savedSearchFilterTags(query).slice(0, 12)
    },
    savedSearchFilterTags(query) {
      const tags = []
      const pushValue = (label, value) => {
        if (value !== undefined && value !== null && value !== '') {
          tags.push(`${label}：${this.truncateText(value, 42)}`)
        }
      }
      const pushFlag = (key, label) => {
        if (this.routeBoolean(query, key)) {
          tags.push(label)
        }
      }
      pushValue('关键词', query.q)
      pushValue('模式', query.searchMode ? this.searchModeText(query.searchMode) : '')
      pushFlag('oaOnly', '仅开放获取')
      pushFlag('hasPdf', '有 PDF')
      pushFlag('hasFulltext', '可全文检索')
      pushFlag('hasDoi', '有 DOI')
      pushFlag('hasAbstract', '有摘要')
      pushFlag('hasReferences', '有参考文献')
      pushFlag('hasPmid', 'PubMed 收录')
      pushFlag('excludeRetracted', '排除撤稿')
      pushFlag('excludeParatext', '排除非正文')
      if (query.fromYear && query.toYear && String(query.fromYear) === String(query.toYear)) {
        pushValue('年份', query.fromYear)
      } else {
        pushValue('年份', [query.fromYear || '不限', query.toYear || '不限'].filter(value => value !== '不限' || query.fromYear || query.toYear).join('-'))
      }
      if (query.minCitations || query.maxCitations) {
        pushValue('引用', `${query.minCitations || '不限'}-${query.maxCitations || '不限'}`)
      }
      pushValue('类型', query.type ? this.formatType(query.type) : '')
      pushValue('语言', query.language ? this.languageText(query.language) : '')
      pushValue('国家/地区', query.countryCode ? this.countryText(query.countryCode, query.countryCode) : '')
      pushValue('OA 状态', query.oaStatus ? this.oaStatusText(query.oaStatus) : '')
      pushValue('开放版本', query.openVersion ? this.openVersionText(query.openVersion) : '')
      pushValue('许可证', query.license ? this.licenseText(query.license) : '')
      pushValue('来源类型', query.sourceType ? this.sourceTypeText(query.sourceType) : '')
      pushValue('收录', query.indexedIn ? this.indexedInText(query.indexedIn) : '')
      pushValue('影响力', query.impactLevel ? this.impactLevelText(query.impactLevel) : '')
      pushValue('作者', query.authorName || query.authorId)
      pushValue('机构', query.institutionName || query.institutionId)
      pushValue('来源', query.sourceName || query.sourceId)
      pushValue('主题', query.topicName || query.topicId)
      pushValue('关键词聚类', query.keywordName || query.keywordId)
      pushValue('基金机构', query.funderName || query.funderId)
      pushValue('SDG', query.sdgName || query.sdgId)
      pushValue('领域', query.domainName || query.domainId)
      pushValue('学科', query.fieldName || query.fieldId)
      pushValue('子学科', query.subfieldName || query.subfieldId)
      pushValue('出版商', query.publisherName || query.publisherId)
      pushValue('引用文献', query.citesWorkName || query.citesWorkId)
      pushValue('排序', query.sort && query.sort !== 'relevance' ? this.sortText(query.sort) : '')
      return tags
    },
    savedSearchLink(item) {
      const query = this.cleanRouteQueryObject(item && item.query)
      const route = this.$router.resolve({ path: '/scholar', query })
      const origin = window.location.origin || ''
      return `${origin}${route.href}`
    },
    async copySavedSearchLink(item) {
      if (!item) {
        return
      }
      try {
        await this.copyText(this.savedSearchLink(item))
        this.$message.success('检索链接已复制')
      } catch (e) {
        this.$message.error('复制失败，请稍后重试')
      }
    },
    savedSearchStrategyText(item) {
      const query = this.cleanRouteQueryObject(item && item.query)
      const tags = this.savedSearchTags(item)
      const lines = [
        '# OA 文献保存检索式',
        '',
        `- 名称：${this.cleanText(item && item.name) || '保存的检索式'}`,
        `- 检索词：${query.q || '未填写'}`,
        `- 检索模式：${query.searchMode ? this.searchModeText(query.searchMode) : '关键词检索'}`,
        `- 排序：${this.sortText(query.sort || 'relevance')}`,
        `- 保存时间：${item && item.createdAt ? this.formatDateTime(item.createdAt) : '-'}`,
        `- 更新时间：${item && item.updatedAt ? this.formatDateTime(item.updatedAt) : '-'}`,
        `- 检索链接：${this.savedSearchLink(item)}`,
        '',
        '## 筛选条件'
      ]
      if (tags.length) {
        tags.forEach(tag => {
          lines.push(`- ${tag}`)
        })
      } else {
        lines.push('- 无额外筛选')
      }
      const note = this.cleanText(item && item.note)
      if (note) {
        lines.push('', '## 备注', note)
      }
      lines.push('', '## 说明', '- 本记录基于保存时的检索条件生成，结果数量和三方数据会随数据源更新而变化。')
      return lines.join('\n')
    },
    async copySavedSearchStrategy(item) {
      if (!item) {
        return
      }
      try {
        await this.copyText(this.savedSearchStrategyText(item))
        this.$message.success('检索策略已复制')
      } catch (e) {
        this.$message.error('复制失败，请稍后重试')
      }
    },
    cleanRouteQueryObject(query) {
      const cleaned = {}
      Object.entries(query || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          cleaned[key] = String(value)
        }
      })
      return cleaned
    },
    openSearchHistoryDialog() {
      if (!this.requireScholarLogin('请先登录个人账号或子账号后查看最近检索')) {
        return
      }
      this.searchHistoryVisible = true
      this.loadSearchHistory()
    },
    async loadSearchHistory(showError = true) {
      if (!this.isScholarPersonalUser()) {
        this.searchHistory = []
        this.searchHistoryLoaded = false
        return []
      }
      if (this.searchHistoryLoaded && !showError) {
        return this.searchHistory
      }
      this.searchHistoryLoading = true
      try {
        this.searchHistory = await listScholarSearchHistory(12)
        this.searchHistoryLoaded = true
        return this.searchHistory
      } catch (e) {
        this.searchHistory = []
        this.searchHistoryLoaded = false
        if (showError) {
          this.$message.error(this.resolveErrorMessage(e, '最近检索加载失败'))
        }
        return []
      } finally {
        this.searchHistoryLoading = false
      }
    },
    searchHistorySuggestionItems(items) {
      return (items || []).slice(0, 6).map(item => ({
        value: item.query,
        hint: this.historyMetaText(item),
        typeLabel: '最近检索',
        raw: {
          entityType: 'history',
          query: item.query
        }
      })).filter(item => item.value)
    },
    applySearchHistory(item) {
      const query = item && item.query ? item.query : ''
      if (!query) {
        return
      }
      this.applyHistoryQuery(query)
    },
    applyHistoryQuery(query) {
      const q = this.cleanText(query)
      if (!q) {
        return
      }
      this.searchMode = 'keyword'
      this.keyword = q
      this.bulkImportActive = false
      this.bulkImportSummary = null
      this.filters = this.defaultScholarFilters()
      this.searchHistoryVisible = false
      this.submitSearch()
    },
    searchHistoryLink(item) {
      const query = this.cleanText(item && item.query)
      const route = this.$router.resolve({ path: '/scholar', query: query ? { q: query } : {} })
      const origin = window.location.origin || ''
      return `${origin}${route.href}`
    },
    async copySearchHistoryLink(item) {
      if (!item) {
        return
      }
      try {
        await this.copyText(this.searchHistoryLink(item))
        this.$message.success('检索链接已复制')
      } catch (e) {
        this.$message.error('复制失败，请稍后重试')
      }
    },
    searchHistoryStrategyText(item) {
      const query = this.cleanText(item && item.query)
      const lines = [
        '# OA 文献历史检索记录',
        '',
        `- 检索词：${query || '未填写'}`,
        '- 检索模式：关键词检索',
        `- 当时结果数：${item && item.resultCount !== null && item.resultCount !== undefined ? this.formatNumber(item.resultCount) : '-'}`,
        `- 检索时间：${item && item.createdAt ? this.formatDateTime(item.createdAt) : '-'}`,
        `- 检索链接：${this.searchHistoryLink(item)}`,
        '',
        '## 说明',
        '- 本记录来自最近检索历史，可用于复现当时的关键词检索；结果数量会随三方数据源更新而变化。'
      ]
      return lines.join('\n')
    },
    async copySearchHistoryStrategy(item) {
      if (!item) {
        return
      }
      try {
        await this.copyText(this.searchHistoryStrategyText(item))
        this.$message.success('检索策略已复制')
      } catch (e) {
        this.$message.error('复制失败，请稍后重试')
      }
    },
    async exportResults(command) {
      const [scope, format] = String(command || 'current:bibtex').split(':')
      const extensionMap = {
        bibtex: 'bib',
        ris: 'ris',
        csv: 'csv',
        screening: 'csv',
        gbt7714: 'txt',
        markdown: 'md'
      }
      const typeMap = {
        bibtex: 'application/x-bibtex;charset=utf-8',
        ris: 'application/x-research-info-systems;charset=utf-8',
        csv: 'text/csv;charset=utf-8',
        screening: 'text/csv;charset=utf-8',
        gbt7714: 'text/plain;charset=utf-8',
        markdown: 'text/markdown;charset=utf-8'
      }
      if (!extensionMap[format]) {
        return
      }
      let works = this.results
      let scopeLabel = 'current'
      if (scope === 'selected') {
        works = this.selectedWorks
        scopeLabel = 'selected'
      }
      if (scope === 'visible') {
        works = this.visibleResults
        scopeLabel = this.screeningView === 'all' ? 'visible' : `visible-${this.screeningView}`
      }
      if (scope === 'marked') {
        works = (this.results || []).filter(work => !!this.screeningMark(work))
        scopeLabel = 'marked'
      }
      if (scope === 'top100') {
        works = await this.fetchTopExportWorks(100)
        scopeLabel = 'top100'
      }
      if (scope === 'marked' && format === 'screening') {
        const records = this.screeningPackageRecords('all')
        if (!records.length) {
          this.$message.warning('暂无可导出的初筛记录')
          return
        }
        const content = `\uFEFF${this.screeningRecordsSheetCsv(records, scopeLabel)}`
        this.downloadText(content, this.exportResultsFilename(scopeLabel, format, extensionMap[format]), typeMap[format])
        this.$message.success(`已导出 ${records.length} 条初筛记录`)
        return
      }
      if (!works.length) {
        this.$message.warning('当前没有可导出的文献')
        return
      }
      const content = format === 'markdown'
        ? this.selectedReadingListText(works)
        : (format === 'screening'
          ? `\uFEFF${this.screeningSheetCsv(works, scopeLabel)}`
          : `${format === 'csv' ? '\uFEFF' : ''}${formatWorks(works, format, this.formatType)}`)
      this.downloadText(content, this.exportResultsFilename(scopeLabel, format, extensionMap[format]), typeMap[format])
      this.$message.success(`已导出 ${works.length} 篇文献`)
    },
    exportResultsFilename(scopeLabel, format, extension) {
      const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      const parts = [
        'scholar',
        this.safeFilenamePart(this.keyword || 'all'),
        this.safeFilenamePart(this.searchMode || 'keyword'),
        this.safeFilenamePart(scopeLabel || 'current'),
        this.safeFilenamePart(format || 'export'),
        datePart
      ].filter(Boolean)
      return `${parts.join('-')}.${extension || 'txt'}`
    },
    safeFilenamePart(value) {
      return String(value || '')
        .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 60)
    },
    screeningSheetCsv(works, scopeLabel = '') {
      const filterText = this.activeFilterTags.map(item => item.label).join('; ')
      const exportedAt = new Date().toLocaleString()
      const searchLink = this.currentSearchLink()
      const scopeTextMap = {
        current: '当前页',
        selected: '已选文献',
        top100: '前 100 条',
        visible: '当前视图',
        'visible-unmarked': '当前视图：未初筛',
        'visible-marked': '当前视图：已标记',
        'visible-include': '当前视图：纳入',
        'visible-maybe': '当前视图：可能相关',
        'visible-pending': '当前视图：待定',
        'visible-exclude': '当前视图：排除',
        marked: '已标记文献'
      }
      const scopeText = scopeTextMap[scopeLabel] || scopeLabel || ''
      const headers = [
        'Screening Decision',
        'Exclude Reason',
        'Reviewer',
        'Notes',
        'Export Scope',
        'Search Query',
        'Search Mode',
        'Sort',
        'Filter Tags',
        'Exported At',
        'Search Link',
        'Discovery Relation',
        'Discovery Source Work',
        'Discovery Source URL',
        'Title',
        'Authors',
        'Year',
        'Source',
        'Type',
        'Language',
        'Citations',
        'OA',
        'OA Status',
        'OA Version',
        'OA License',
        'OA Source',
        'OA Evidence',
        'Has PDF',
        'Has Abstract',
        'Retracted',
        'Paratext',
        'DOI',
        'PDF URL',
        'Landing URL',
        'Provider URL',
        'Abstract',
        'Topics'
      ]
      const rows = (works || []).filter(Boolean).map(work => {
        const mark = this.screeningMark(work) || {}
        const relationContext = mark.relationContext || null
        const sourceWork = relationContext && relationContext.sourceWork ? relationContext.sourceWork : {}
        const oaEvidence = this.openAccessEvidence(work)
        return [
          mark.decision ? this.screeningDecisionText(mark.decision) : '',
          mark.excludeReason || '',
          mark.reviewer || '',
          mark.notes || '',
          scopeText,
          this.keyword || '',
          this.searchModeText(this.searchMode),
          this.sortText(this.filters.sort),
          filterText,
          exportedAt,
          searchLink,
          relationContext ? relationContext.relationLabel || relationContext.relationKey || '' : '',
          sourceWork.title || sourceWork.doi || sourceWork.providerId || sourceWork.workKey || '',
          sourceWork.url || '',
          work.title || '',
          this.formatAuthors(work.authors || [], 20),
          work.publicationYear || '',
          work.sourceName || '',
          this.formatType(work.type),
          work.language || '',
          work.citedByCount || 0,
          work.isOa ? 'yes' : 'no',
          work.oaStatus || '',
          oaEvidence.version || '',
          oaEvidence.license || '',
          [oaEvidence.sourceName, oaEvidence.sourceType].filter(Boolean).join(' · '),
          this.openAccessEvidenceChips(work).join('; '),
          work.pdfUrl ? 'yes' : 'no',
          this.cleanText(work.abstractText) ? 'yes' : 'no',
          this.isRetracted(work) ? 'yes' : 'no',
          this.isParatext(work) ? 'yes' : 'no',
          work.doi || '',
          work.pdfUrl || '',
          work.landingPageUrl || '',
          this.firstProviderUrl(work) || '',
          this.cleanText(work.abstractText),
          (work.topics || []).map(item => item && item.name).filter(Boolean).join('; ')
        ]
      })
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    screeningRecordsSheetCsv(records, scopeLabel = 'marked') {
      const filterText = this.activeFilterTags.map(item => item.label).join('; ')
      const exportedAt = new Date().toLocaleString()
      const searchLink = this.currentSearchLink()
      const scopeText = '已标记文献'
      const headers = [
        'Screening Decision',
        'Exclude Reason',
        'Reviewer',
        'Notes',
        'Export Scope',
        'Search Query',
        'Search Mode',
        'Sort',
        'Filter Tags',
        'Exported At',
        'Search Link',
        'Discovery Relation',
        'Discovery Source Work',
        'Discovery Source URL',
        'Title',
        'Authors',
        'Year',
        'Source',
        'Type',
        'Language',
        'Citations',
        'OA',
        'OA Status',
        'OA Version',
        'OA License',
        'OA Source',
        'OA Evidence',
        'Has PDF',
        'Has Abstract',
        'Retracted',
        'Paratext',
        'DOI',
        'PDF URL',
        'Landing URL',
        'Provider URL',
        'Abstract',
        'Topics'
      ]
      const rows = (records || []).filter(Boolean).map(record => {
        const work = record.work || {}
        const relationContext = record.relationContext || null
        const sourceWork = relationContext && relationContext.sourceWork ? relationContext.sourceWork : {}
        return [
          record.decisionText || this.screeningDecisionText(record.decision),
          record.excludeReason || '',
          record.reviewer || '',
          record.notes || '',
          scopeText,
          this.keyword || '',
          this.searchModeText(this.searchMode),
          this.sortText(this.filters.sort),
          filterText,
          exportedAt,
          searchLink,
          relationContext ? relationContext.relationLabel || relationContext.relationKey || '' : '',
          sourceWork.title || sourceWork.doi || sourceWork.providerId || sourceWork.workKey || '',
          sourceWork.url || '',
          work.title || record.workKey || '',
          '',
          work.year || '',
          work.source || '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          work.url && String(work.url).toLowerCase().includes('.pdf') ? 'yes' : '',
          '',
          '',
          '',
          work.doi || '',
          work.url && String(work.url).toLowerCase().includes('.pdf') ? work.url : '',
          work.url && !String(work.url).toLowerCase().includes('.pdf') ? work.url : '',
          work.providerId || '',
          '',
          ''
        ]
      })
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    async fetchTopExportWorks(limit) {
      if (this.exportingTopResults) {
        return []
      }
      this.exportingTopResults = true
      try {
        const params = {
          ...this.buildSearchParams(false),
          page: 1,
          size: limit,
          cursor: ''
        }
        const response = await searchScholar(params)
        return response.results || []
      } catch (e) {
        this.$message.error(this.resolveErrorMessage(e, '前 100 条导出失败'))
        return []
      } finally {
        this.exportingTopResults = false
      }
    },
    exportSavedWorks(format) {
      const works = this.filteredSavedWorks.map(item => item.work).filter(Boolean)
      if (!works.length) {
        this.$message.warning('当前没有可导出的收藏文献')
        return
      }
      const extensionMap = {
        bibtex: 'bib',
        ris: 'ris',
        csv: 'csv',
        gbt7714: 'txt',
        markdown: 'md'
      }
      const typeMap = {
        bibtex: 'application/x-bibtex;charset=utf-8',
        ris: 'application/x-research-info-systems;charset=utf-8',
        csv: 'text/csv;charset=utf-8',
        gbt7714: 'text/plain;charset=utf-8',
        markdown: 'text/markdown;charset=utf-8'
      }
      const exportFormat = extensionMap[format] ? format : 'bibtex'
      const content = exportFormat === 'markdown'
        ? this.savedReadingListText(this.filteredSavedWorks)
        : `${exportFormat === 'csv' ? '\uFEFF' : ''}${formatWorks(works, exportFormat, this.formatType)}`
      this.downloadText(content, `scholar-saved.${extensionMap[exportFormat]}`, typeMap[exportFormat])
      this.$message.success(`已导出 ${works.length} 篇收藏文献`)
    },
    toggleSavedPanel(type) {
      if (type === 'export') {
        this.savedExportVisible = !this.savedExportVisible
        this.savedOrganizerVisible = false
        return
      }
      if (type === 'organizer') {
        this.savedOrganizerVisible = !this.savedOrganizerVisible
        this.savedExportVisible = false
      }
    },
    runSavedExportCommand(command) {
      this.savedExportVisible = false
      this.exportSavedWorks(command)
    },
    openCitationDialog(work) {
      if (!work) {
        return
      }
      this.citationWork = this.expandedWork(work) || work
      this.citationFormat = 'gbt7714'
      this.citationVisible = true
    },
    async copyCitation() {
      if (!this.citationText) {
        return
      }
      try {
        await this.copyText(this.citationText)
        this.$message.success('引用格式已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadSingleCitation() {
      if (!this.citationWork || !this.citationText) {
        return
      }
      const extensionMap = {
        bibtex: 'bib',
        ris: 'ris',
        gbt7714: 'txt',
        apa: 'txt',
        mla: 'txt'
      }
      const typeMap = {
        bibtex: 'application/x-bibtex;charset=utf-8',
        ris: 'application/x-research-info-systems;charset=utf-8'
      }
      const filename = `${this.citationFilename(this.citationWork)}.${extensionMap[this.citationFormat] || 'txt'}`
      this.downloadText(this.citationText, filename, typeMap[this.citationFormat] || 'text/plain;charset=utf-8')
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return
      }
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', 'readonly')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textarea)
      if (!success) {
        throw new Error('copy failed')
      }
    },
    citationFilename(work) {
      const title = work && work.title ? work.title : 'scholar-citation'
      return title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'scholar-citation'
    },
    toBibTex(works) {
      return works.map(work => {
        const type = this.bibTexType(work.type)
        const key = this.citationKey(work)
        const fields = [
          ['title', work.title],
          ['author', (work.authors || []).map(item => item.name).filter(Boolean).join(' and ')],
          ['journal', work.sourceName],
          ['year', work.publicationYear],
          ['doi', work.doi],
          ['url', work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)]
        ].filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
        const body = fields.map(([field, value]) => `  ${field} = {${this.escapeBibTex(value)}}`).join(',\n')
        return `@${type}{${key},\n${body}\n}`
      }).join('\n\n')
    },
    toRis(works) {
      return works.map(work => {
        const lines = [`TY  - ${this.risType(work.type)}`]
        if (work.title) lines.push(`TI  - ${work.title}`)
        ;(work.authors || []).forEach(author => {
          if (author.name) lines.push(`AU  - ${author.name}`)
        })
        if (work.publicationYear) lines.push(`PY  - ${work.publicationYear}`)
        if (work.sourceName) lines.push(`T2  - ${work.sourceName}`)
        if (work.doi) lines.push(`DO  - ${work.doi}`)
        const url = work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)
        if (url) lines.push(`UR  - ${url}`)
        lines.push('ER  -')
        return lines.join('\n')
      }).join('\n\n')
    },
    toCsv(works) {
      const headers = ['Title', 'Authors', 'Year', 'Source', 'Type', 'Language', 'Citations', 'OA', 'OA Status', 'DOI', 'PDF URL', 'Landing URL']
      const rows = works.map(work => [
        work.title || '',
        this.formatAuthors(work.authors || [], 20),
        work.publicationYear || '',
        work.sourceName || '',
        this.formatType(work.type),
        work.language || '',
        work.citedByCount || 0,
        work.isOa ? 'yes' : 'no',
        work.oaStatus || '',
        work.doi || '',
        work.pdfUrl || '',
        work.landingPageUrl || ''
      ])
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    downloadText(content, filename, type) {
      const blob = new Blob([content], { type: type || 'text/plain;charset=utf-8' })
      this.saveBlob(blob, filename)
    },
    saveBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    },
    pdfFilename(work) {
      const title = work && work.title ? work.title : 'scholar-work'
      return `${title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 90) || 'scholar-work'}.pdf`
    },
    citationKey(work) {
      const firstAuthor = (work.authors || []).map(item => item.name).filter(Boolean)[0] || 'work'
      const authorKey = firstAuthor.split(/\s+/).slice(-1)[0].replace(/[^a-z0-9]/gi, '') || 'work'
      const year = work.publicationYear || 'nodate'
      const id = (work.doi || this.firstProviderId(work) || work.workKey || '').replace(/[^a-z0-9]/gi, '').slice(-6)
      return `${authorKey}${year}${id}`.replace(/[^a-z0-9]/gi, '').replace(/^\d+/, 'work')
    },
    bibTexType(type) {
      const typeMap = {
        book: 'book',
        'book-chapter': 'incollection',
        'proceedings-article': 'inproceedings',
        'conference-paper': 'inproceedings',
        dataset: 'misc'
      }
      return typeMap[type] || 'article'
    },
    risType(type) {
      const typeMap = {
        book: 'BOOK',
        'book-chapter': 'CHAP',
        'proceedings-article': 'CPAPER',
        'conference-paper': 'CPAPER',
        preprint: 'GEN'
      }
      return typeMap[type] || 'JOUR'
    },
    escapeBibTex(value) {
      return String(value).replace(/[{}]/g, ' ').replace(/\s+/g, ' ').trim()
    },
    csvCell(value) {
      const text = String(value === null || value === undefined ? '' : value)
      return `"${text.replace(/"/g, '""')}"`
    },
    historyMetaText(item) {
      const parts = []
      if (item && item.resultCount !== null && item.resultCount !== undefined) {
        parts.push(`${this.formatNumber(item.resultCount)} 条结果`)
      }
      if (item && item.createdAt) {
        parts.push(this.formatDateTime(item.createdAt))
      }
      return parts.join(' · ')
    },
    formatDateTime(value) {
      if (!value) {
        return ''
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return String(value).replace('T', ' ').slice(0, 16)
      }
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    getRagMetadata() {
      return {
        token: localStorage.getItem('token')
      }
    },
    resolveRagErrorMessage(error, fallbackMessage) {
      if (!error) {
        return fallbackMessage
      }
      const metadata = error.metadata || {}
      const candidates = [error.message, metadata['grpc-message'], metadata.message, metadata.details]
      for (let item of candidates) {
        if (Array.isArray(item)) {
          item = item[0]
        }
        if (item) {
          return item
        }
      }
      return fallbackMessage
    },
    async ensureRagScopeReady() {
      if (this.currentRagScopeId) {
        return true
      }
      const request = new RagBootstrapRequest()
      const response = await ragServiceClient.ragBootstrap(request, this.getRagMetadata())
      const scopes = (response.getScopesList ? response.getScopesList() : []).map(scope => ({
        scopeId: scope.getScopeId ? scope.getScopeId() : '',
        scopeType: scope.getScopeType ? scope.getScopeType() : ''
      }))
      const personalScope = scopes.find(scope => scope.scopeType === 'PERSONAL')
      this.currentRagScopeId = personalScope
        ? personalScope.scopeId
        : (response.getDefaultScopeId ? response.getDefaultScopeId() : '')
      return !!this.currentRagScopeId
    },
    mapRagKnowledgeBase(item) {
      if (!item) {
        return null
      }
      return {
        id: item.getId ? item.getId() : '',
        name: item.getName ? item.getName() : '',
        description: item.getDescription ? item.getDescription() : '',
        scopeId: item.getScopeId ? item.getScopeId() : '',
        knowledgeCount: item.getKnowledgeCount ? item.getKnowledgeCount() : 0,
        documentCount: item.getDocumentCount ? item.getDocumentCount() : 0,
        processingCount: item.getProcessingCount ? item.getProcessingCount() : 0
      }
    },
    async loadRagKnowledgeBases() {
      this.ragKnowledgeBaseLoading = true
      try {
        if (!await this.ensureRagScopeReady()) {
          throw new Error('个人知识库初始化失败')
        }
        const request = new RagKnowledgeBasePageRequest()
        request.setScopeId(this.currentRagScopeId)
        request.setPageNum(1)
        request.setPageSize(100)
        const response = await ragServiceClient.ragKnowledgeBasePage(request, this.getRagMetadata())
        this.ragKnowledgeBases = (response.getKnowledgeBasesList ? response.getKnowledgeBasesList() : [])
          .map(this.mapRagKnowledgeBase)
          .filter(item => item && item.id)
        if (!this.ragKnowledgeBases.length) {
          this.ragImportTargetMode = 'new'
          this.ragImportKnowledgeBaseId = ''
          return
        }
        if (!this.ragImportKnowledgeBaseId || !this.ragKnowledgeBases.some(item => item.id === this.ragImportKnowledgeBaseId)) {
          this.ragImportKnowledgeBaseId = this.ragKnowledgeBases[0].id
        }
        if (this.ragImportTargetMode !== 'new') {
          this.ragImportTargetMode = 'existing'
        }
      } finally {
        this.ragKnowledgeBaseLoading = false
      }
    },
    normalizeRagImportWorks(input) {
      const source = Array.isArray(input)
        ? input
        : (input ? [input] : ((this.selectedWorks && this.selectedWorks.length) ? this.selectedWorks : this.visibleResults))
      const map = new Map()
      ;(source || []).forEach(work => {
        if (!work) {
          return
        }
        const key = this.workSelectionKey(work)
        if (key && !map.has(key)) {
          map.set(key, work)
        }
      })
      return Array.from(map.values())
    },
    ragImportDefaultName(works) {
      const seed = this.cleanText(this.keyword) || (works && works[0] && this.cleanText(works[0].title)) || 'OA文献'
      const time = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-')
      return `OA文献-${seed.slice(0, 18)}-${time}`
    },
    async openRagImportDialog(input) {
      if (!this.requireScholarLogin('请先登录个人账号或子账号后导入 RAG')) {
        return
      }
      const works = this.normalizeRagImportWorks(input)
      if (!works.length) {
        this.$message.warning('请先选择或检索出要导入 RAG 的文献')
        return
      }
      this.ragImportWorks = works
      this.ragImportIncludePdf = true
      this.ragImportProgress = {
        total: 0,
        done: 0,
        failed: 0,
        pdfDone: 0,
        pdfFailed: 0,
        message: ''
      }
      this.ragImportNewName = this.ragImportDefaultName(works)
      this.ragImportNewDescription = '由 OA 文献检索页导入的文献元数据和可下载 PDF。'
      this.ragImportVisible = true
      try {
        await this.loadRagKnowledgeBases()
      } catch (e) {
        this.ragImportTargetMode = 'new'
        this.$message.warning(this.resolveRagErrorMessage(e, '知识库列表加载失败，可尝试新建知识库导入'))
      }
    },
    async createRagKnowledgeBaseForImport() {
      const name = this.cleanText(this.ragImportNewName)
      if (!name) {
        throw new Error('请输入知识库名称')
      }
      if (!await this.ensureRagScopeReady()) {
        throw new Error('个人知识库初始化失败')
      }
      const request = new RagKnowledgeBaseCreateRequest()
      request.setScopeId(this.currentRagScopeId)
      request.setName(name)
      request.setDescription(this.cleanText(this.ragImportNewDescription) || 'OA 文献检索导入')
      request.setType('DOCUMENT')
      request.setParserStrategy('auto')
      request.setChunkStrategy('general')
      request.setStorageProvider('minio')
      const response = await ragServiceClient.ragKnowledgeBaseCreate(request, this.getRagMetadata())
      const knowledgeBase = this.mapRagKnowledgeBase(response.getKnowledgeBase ? response.getKnowledgeBase() : null)
      if (!knowledgeBase || !knowledgeBase.id) {
        throw new Error('创建知识库失败：未返回知识库 ID')
      }
      this.ragKnowledgeBases = [knowledgeBase].concat(this.ragKnowledgeBases.filter(item => item.id !== knowledgeBase.id))
      this.ragImportKnowledgeBaseId = knowledgeBase.id
      return knowledgeBase.id
    },
    async resolveRagImportKnowledgeBaseId() {
      if (this.ragImportTargetMode === 'new') {
        return this.createRagKnowledgeBaseForImport()
      }
      if (!this.ragImportKnowledgeBaseId) {
        throw new Error('请选择目标知识库')
      }
      if (!await this.ensureRagScopeReady()) {
        throw new Error('个人知识库初始化失败')
      }
      return this.ragImportKnowledgeBaseId
    },
    async uploadRagFile(knowledgeBaseId, name, bytes) {
      const request = new RagKnowledgeBaseUploadRequest()
      request.setScopeId(this.currentRagScopeId)
      request.setKnowledgeBaseId(knowledgeBaseId)
      request.setName(name)
      request.setFile(bytes)
      const response = await ragServiceClient.ragKnowledgeBaseUpload(request, this.getRagMetadata())
      if (response.getStatus && !response.getStatus()) {
        throw new Error(response.getMessage ? response.getMessage() : '上传失败')
      }
      return response
    },
    sanitizeRagFileName(value, fallback) {
      return (this.cleanText(value) || fallback || 'scholar-work')
        .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 90) || fallback || 'scholar-work'
    },
    buildRagWorkMarkdown(work) {
      const topics = (work.topics || []).map(item => item && item.name).filter(Boolean).join('、')
      const providerUrl = this.firstProviderUrl(work)
      const lines = [
        `# ${work.title || 'Untitled work'}`,
        '',
        '## 基本信息',
        `- 作者：${this.formatAuthors(work.authors || [], 20) || '未知作者'}`,
        `- 机构：${this.formatInstitutions(work.institutions || [], 12) || '未知机构'}`,
        `- 年份：${work.publicationYear || '-'}`,
        `- 来源：${work.sourceName || '-'}`,
        `- 类型：${this.formatType(work.type) || work.type || '-'}`,
        `- 语言：${work.language || '-'}`,
        `- 引用数：${this.formatNumber(work.citedByCount || 0)}`,
        `- 开放获取：${work.isOa ? this.oaStatusText(work.oaStatus) : '否'}`,
        `- DOI：${work.doi || '-'}`,
        `- OpenAlex：${this.firstProviderId(work) || '-'}`,
        `- 原文链接：${work.landingPageUrl || this.doiUrl(work) || providerUrl || '-'}`,
        `- PDF 链接：${work.pdfUrl || '-'}`,
        '',
        '## 主题',
        topics || '暂无主题',
        '',
        '## 摘要',
        this.cleanText(work.abstractText) || '暂无摘要',
        '',
        '## 导入说明',
        `- 导入时间：${this.formatDateTime(new Date().toISOString())}`,
        '- 来源：OA 文献检索平台',
        '- 说明：本文档由检索元数据生成；如同批次包含 PDF，RAG 会另行解析 PDF 全文。'
      ]
      return lines.join('\n')
    },
    async blobToUint8Array(blob) {
      const buffer = await blob.arrayBuffer()
      return new Uint8Array(buffer)
    },
    async uploadWorkMetadataToRag(knowledgeBaseId, work) {
      const markdown = this.buildRagWorkMarkdown(work)
      const baseName = this.sanitizeRagFileName(work.title || work.doi || this.firstProviderId(work), 'scholar-work')
      await this.uploadRagFile(knowledgeBaseId, `${baseName}.md`, new TextEncoder().encode(markdown))
    },
    canImportWorkPdfToRag(work) {
      const ref = this.firstProviderRef(work)
      return !!(work && work.pdfUrl && ref && ref.provider && ref.providerId)
    },
    async uploadWorkPdfToRag(knowledgeBaseId, work) {
      if (!this.canImportWorkPdfToRag(work)) {
        return false
      }
      const ref = this.firstProviderRef(work)
      const result = await downloadScholarWorkPdf(ref.provider, ref.providerId)
      const bytes = await this.blobToUint8Array(result.blob)
      await this.uploadRagFile(knowledgeBaseId, result.filename || this.pdfFilename(work), bytes)
      return true
    },
    async submitRagImport() {
      if (this.ragImportLoading) {
        return
      }
      const works = this.normalizeRagImportWorks(this.ragImportWorks)
      if (!works.length) {
        this.$message.warning('没有可导入的文献')
        return
      }
      this.ragImportLoading = true
      let successCount = 0
      let failedCount = 0
      let pdfSuccessCount = 0
      let pdfFailedCount = 0
      try {
        const knowledgeBaseId = await this.resolveRagImportKnowledgeBaseId()
        const pdfTargets = this.ragImportIncludePdf ? works.filter(work => this.canImportWorkPdfToRag(work)) : []
        this.ragImportProgress = {
          total: works.length + pdfTargets.length,
          done: 0,
          failed: 0,
          pdfDone: 0,
          pdfFailed: 0,
          message: ''
        }
        for (let index = 0; index < works.length; index++) {
          const work = works[index]
          const title = work.title || work.doi || this.firstProviderId(work) || `第 ${index + 1} 篇文献`
          try {
            this.ragImportProgress.message = `正在写入元数据：${this.shorten(title, 28)}`
            await this.uploadWorkMetadataToRag(knowledgeBaseId, work)
            successCount++
          } catch (e) {
            failedCount++
            this.ragImportProgress.failed = failedCount
          } finally {
            this.ragImportProgress.done++
          }
          if (this.ragImportIncludePdf && this.canImportWorkPdfToRag(work)) {
            try {
              this.ragImportProgress.message = `正在上传 PDF：${this.shorten(title, 28)}`
              if (await this.uploadWorkPdfToRag(knowledgeBaseId, work)) {
                pdfSuccessCount++
                this.ragImportProgress.pdfDone = pdfSuccessCount
              }
            } catch (e) {
              pdfFailedCount++
              this.ragImportProgress.pdfFailed = pdfFailedCount
            } finally {
              this.ragImportProgress.done++
            }
          }
        }
        this.ragImportVisible = false
        await this.loadRagKnowledgeBases().catch(() => {})
        const parts = [`已提交 ${successCount} 篇文献元数据到 RAG`]
        if (pdfSuccessCount) {
          parts.push(`${pdfSuccessCount} 个 PDF`)
        }
        if (failedCount || pdfFailedCount) {
          this.$message.warning(`${parts.join('，')}；${failedCount} 篇元数据、${pdfFailedCount} 个 PDF 导入失败`)
        } else {
          this.$message.success(parts.join('，'))
        }
      } catch (e) {
        this.$message.error(this.resolveRagErrorMessage(e, e.message || '导入 RAG 失败'))
      } finally {
        this.ragImportLoading = false
        this.ragImportProgress.message = ''
      }
    },
    requireScholarLogin(message) {
      this.syncUserInfoFromStorage()
      if (this.isScholarPersonalUser()) {
        return true
      }
      this.$message.warning(message || '请先登录个人账号或子账号后使用文献收藏')
      this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
      return false
    },
    isScholarPersonalUser() {
      return !!this.userId
    },
    openSavedWork(work) {
      this.savedVisible = false
      this.openWorkDetailPage(work)
    },
    isSaved(work) {
      return !!(work && work.workKey && this.savedWorks.some(item => item.work && item.work.workKey === work.workKey))
    },
    formatAuthors(authors, limit) {
      const max = limit || 4
      const names = (authors || []).map(item => item.name).filter(Boolean)
      if (names.length <= max) {
        return names.join('、')
      }
      return `${names.slice(0, max).join('、')} 等 ${names.length} 位`
    },
    formatInstitutions(institutions, limit) {
      const max = limit || 3
      const names = (institutions || []).map(item => item.name).filter(Boolean)
      if (names.length <= max) {
        return names.join('、')
      }
      return `${names.slice(0, max).join('、')} 等`
    },
    visiblePeople(items, limit) {
      return (items || []).filter(item => item && item.name).slice(0, limit || 3)
    },
    visibleTopics(work) {
      return (work.topics || []).filter(item => item.name).slice(0, 5)
    },
    shorten(text, maxLength) {
      if (!text || text.length <= maxLength) {
        return text || ''
      }
      return `${text.slice(0, maxLength)}...`
    },
    firstProviderRef(work) {
      return work && work.providerRefs && work.providerRefs.length ? work.providerRefs[0] : null
    },
    firstProviderId(work) {
      const ref = this.firstProviderRef(work)
      return ref ? ref.providerId : ''
    },
    firstProviderUrl(work) {
      const ref = this.firstProviderRef(work)
      return ref ? ref.url : ''
    },
    doiUrl(work) {
      return work && work.doi ? `https://doi.org/${work.doi}` : ''
    },
    biblioText(work) {
      const biblio = work && work.extra && work.extra.biblio ? work.extra.biblio : {}
      const volume = this.cleanText(biblio.volume)
      const issue = this.cleanText(biblio.issue)
      const firstPage = this.cleanText(biblio.firstPage)
      const lastPage = this.cleanText(biblio.lastPage)
      const volumeIssue = volume && issue ? `${volume}(${issue})` : (volume || (issue ? `(${issue})` : ''))
      const pages = firstPage && lastPage && firstPage !== lastPage ? `${firstPage}-${lastPage}` : (firstPage || lastPage)
      return [volumeIssue, pages].filter(Boolean).join(': ')
    },
    cleanText(value) {
      return String(value === null || value === undefined ? '' : value).replace(/\s+/g, ' ').trim()
    },
    cleanTag(value) {
      return this.cleanText(value).replace(/[，,;；]+/g, ' ').trim().slice(0, 24)
    },
    cleanTags(values) {
      const tags = Array.isArray(values) ? values : []
      const seen = new Set()
      return tags
        .map(tag => this.cleanTag(tag))
        .filter(tag => {
          if (!tag) {
            return false
          }
          const key = tag.toLowerCase()
          if (seen.has(key)) {
            return false
          }
          seen.add(key)
          return true
        })
        .slice(0, 8)
    },
    openExternal(url) {
      return openExternalWithConfirm(this, url)
    },
    yearPercent(count) {
      return `${Math.max(8, Math.round(((count || 0) / this.maxYearCount) * 100))}%`
    },
    formatNumber(value) {
      const number = Number(value || 0)
      return number.toLocaleString()
    },
    truncateText(text, limit = 260) {
      const value = String(text || '').replace(/\s+/g, ' ').trim()
      if (!value || value.length <= limit) {
        return value
      }
      return `${value.slice(0, limit).trim()}...`
    },
    formatDecimal(value, digits = 2) {
      const number = Number(value)
      if (!Number.isFinite(number)) {
        return ''
      }
      return number.toFixed(digits).replace(/\.?0+$/, '')
    },
    formatType(type) {
      const typeMap = {
        article: '论文',
        preprint: '预印本',
        'book-chapter': '图书章节',
        book: '书籍',
        'proceedings-article': '会议论文',
        'conference-paper': '会议论文',
        'conference-abstract': '会议摘要',
        review: '综述',
        dissertation: '学位论文',
        dataset: '数据集',
        report: '报告',
        editorial: '社论',
        letter: '通信',
        paratext: '附属材料',
        'peer-review': '同行评议',
        erratum: '勘误',
        software: '软件',
        standard: '标准',
        libguides: '图书馆指南',
        'supplementary-materials': '补充材料',
        reference: '参考条目',
        retraction: '撤稿说明',
        other: '其他'
      }
      const option = this.typeOptions.find(item => item.value === type)
      return option ? option.label : (typeMap[type] || type)
    },
    oaStatusText(status) {
      const map = {
        gold: '金色 OA',
        green: '绿色 OA',
        bronze: '青铜 OA',
        hybrid: '混合 OA',
        diamond: '钻石 OA',
        closed: '非开放',
        unknown: '未知'
      }
      return map[status] || status || '未知'
    },
    openVersionText(version) {
      const map = {
        any: '任意开放版本',
        published: '正式发表版',
        acceptedOrPublished: '接受稿或发表版',
        publishedversion: '正式发表版',
        publishedVersion: '正式发表版',
        acceptedversion: '作者接受稿',
        acceptedVersion: '作者接受稿',
        submittedversion: '投稿稿',
        submittedVersion: '投稿稿'
      }
      return map[version] || version || '未知'
    },
    licenseText(license) {
      return license ? String(license).replace(/-/g, ' ').toUpperCase() : '未知'
    },
    sourceTypeText(type) {
      const map = {
        journal: '期刊',
        repository: '仓储',
        conference: '会议',
        'ebook platform': '电子书平台',
        'book series': '图书丛书',
        ebook_platform: '电子书平台',
        book_series: '图书丛书',
        metadata: '元数据',
        other: '其他'
      }
      return map[type] || type || '未知'
    },
    indexedInText(value) {
      const key = String(value || '').toLowerCase()
      const map = {
        crossref: 'Crossref',
        pubmed: 'PubMed',
        datacite: 'DataCite',
        doaj: 'DOAJ',
        arxiv: 'arXiv',
        mag: 'MAG'
      }
      return map[key] || value || '未知'
    },
    indexedBadges(work) {
      const values = work && work.extra && Array.isArray(work.extra.indexedIn) ? work.extra.indexedIn : []
      return values.map(value => this.indexedInText(value)).filter(Boolean).slice(0, 3)
    },
    impactLevelText(level) {
      const map = {
        top1: '同领域 Top 1%',
        top10: '同领域 Top 10%',
        fwci_gt_1: 'FWCI > 1'
      }
      return map[level] || level || '未知'
    },
    resolveErrorMessage(error, fallback) {
      if (error && error.response && error.response.data) {
        const data = error.response.data
        return data.message || data.detail || data.error || fallback || '检索失败，请稍后重试'
      }
      return (error && error.message) || fallback || '检索失败，请稍后重试'
    },
    isQuotaExhaustedError(error) {
      const data = error && error.response && error.response.data
      return !!(data && data.code === 'provider_quota_exhausted')
    },
    syncUserInfoFromStorage() {
      const authContext = localStorage.getItem('auth-context')
      if (!authContext) {
        this.resetHeaderUserState()
        return
      }
      try {
        const users = JSON.parse(authContext)
        this.resetHeaderUserState()
        users.forEach(item => {
          if (item.type === 'Person' || item.type === 'Child') {
            this.userInfo = this.safeDecodeName(item)
            this.userId = item.name
            this.userType = item.type === 'Person' ? '0' : '1'
            if (!item.isRoaming) {
              const roaming = localStorage.getItem(this.userId)
              this.showRoaming = !roaming || roaming !== '1'
            } else {
              this.userType = '2'
              this.showRoaming = false
            }
            if (this.groupUserInfo === '') {
              this.groupUserInfo = '机构登录'
            }
            this.showloginOut = true
          }
          if (item.type === 'Group') {
            this.groupUserInfos.push(item)
            this.groupUserInfo = this.safeDecodeName(item)
          }
        })
      } catch (e) {
        this.resetHeaderUserState()
      }
    },
    syncHeaderUserStatus() {
      const token = localStorage.getItem('token')
      if (!token) {
        this.syncUserInfoFromStorage()
        return
      }
      authServiceClient
        .getUserStatus(userStatusRequest, { token })
        .then(res => {
          if (!res.getStatus()) {
            localStorage.removeItem('token')
            localStorage.removeItem('auth-context')
            this.resetHeaderUserState()
            return
          }
          this.syncUserInfoFromStorage()
        })
        .catch(() => {
          localStorage.removeItem('token')
          localStorage.removeItem('auth-context')
          this.resetHeaderUserState()
        })
    },
    resetHeaderUserState() {
      this.userInfo = ''
      this.groupUserInfo = ''
      this.groupUserInfos = []
      this.showloginOut = false
      this.showRoaming = false
      this.userId = ''
      this.userType = ''
    },
    safeDecodeName(item) {
      if (!item) {
        return ''
      }
      if (item.realName !== '' && item.realName !== undefined) {
        try {
          return Base64.decode(item.realName)
        } catch (e) {
          return item.name || ''
        }
      }
      return item.name || ''
    },
    handleAuthMessage(e) {
      const data = e && e.data && e.data.data
      if (data === 'params') {
        this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
      }
      if (data === 'close' && typeof layer !== 'undefined') {
        layer.closeAll()
        this.syncHeaderUserStatus()
        setTimeout(() => this.loadSavedWorks(), 500)
      }
    },
    gotoPage(type) {
      if (type === 'text') {
        this.toPath('/translate', 'txt')
      } else if (type === 'doc') {
        this.toPath('/translate', 'doc')
      } else if (type === 'ai') {
        this.toPath('/translate', 'AITranslate')
      } else if (type === 'lange') {
        this.toPath('/translate', 'more')
      } else if (type === 'img') {
        this.toPath('/translate', 'img')
      } else if (type === 'polish') {
        this.toPath('/translate', 'polish')
      } else if (type === 'aiPPT') {
        this.toPath('/translate', 'aiPPT')
      } else if (type === 'rag') {
        this.toPath('/rag')
      } else if (type === 'pro') {
        this.toPath('/trans-pro')
      }
    },
    openNewMessage() {
      this.$alert('服务升级中，敬请期待', '产品升级', {
        confirmButtonText: '确定'
      })
    },
    logOut(val) {
      if (val === 'toCenter') {
        window.open('http://user.wesitrans.com', '_blank')
        return
      }
      if (val === 'toAccount') {
        window.open('https://user.wesitrans.com/account', '_blank')
        return
      }
      if (val === 'toOrder') {
        window.open('https://user.wesitrans.com/order', '_blank')
        return
      }
      if (val === 'toTransPro') {
        this.toPath('/trans-pro')
        return
      }
      if (val === 'standLangSet') {
        this.toPath('/translate', 'setting')
        return
      }
      if (val === 'convertDoc') {
        this.toPath('/translate', 'convert')
        return
      }
      if (val === 'mySelfDoc') {
        this.toPath('/list-pro')
        return
      }
      if (val === 'webTranslate') {
        this.toPath('/translate', 'plugin')
        return
      }
      if (val === 'smartClassify') {
        this.toPath('/smart-classify')
        return
      }
      const request = outRequest
      request.setName(val === undefined ? this.userInfo : val)
      authServiceClient
        .logOut(request, {
          token: localStorage.getItem('token')
        })
        .then(() => {
          window.location.reload()
          this.$router.push('/index')
        })
        .catch(() => {
          localStorage.removeItem('token')
          localStorage.removeItem('auth-context')
          this.resetHeaderUserState()
          this.$router.push('/index')
          this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
        })
    },
    toCenter() {
      window.open('https://user.wesitrans.com/account', '_blank')
    },
    getQrcode() {
      this.qrcodeUrl = 'http://common.wesitrans.com/common/roaming/qrcode'
      this.isRoamingTimer = setInterval(this.roamingEvent, 1000)
      this.isRoamingTimerOut = setTimeout(this.clearTime, 60000)
    },
    clearTime() {
      clearInterval(this.isRoamingTimer)
      clearTimeout(this.isRoamingTimerOut)
    },
    roamingEvent() {
      if (this.userId && localStorage.getItem(this.userId) !== '1') {
        const request = checkRoamingRequest
        request.setUserId(this.userId)
        translateServiceClient
          .checkRoaming(request, {
            token: localStorage.getItem('token')
          })
          .then(res => {
            localStorage.setItem(this.userId, res.getMessage())
            if (res.getMessage() === '1') {
              window.location.reload()
            }
          })
          .catch(() => {})
      }
    },
    removeQrcode() {
      this.qrcodeUrl = ''
      clearInterval(this.isRoamingTimer)
      clearTimeout(this.isRoamingTimerOut)
    },
    toPath(url, type) {
      if (url === '/trans-pro') {
        window.open('/trans-pro/index', '_self')
        return
      }
      if (url === '/list' || url === '/list-pro') {
        authServiceClient
          .getUserStatus(userStatusRequest, {
            token: localStorage.getItem('token')
          })
          .then(res => {
            if (!res.getStatus()) {
              localStorage.removeItem('token')
              localStorage.removeItem('auth-context')
              if (this.hasH5) {
                this.$router.push('/h5/login')
                return
              }
              this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
              return
            }
            if (localStorage.getItem('auth-context') && localStorage.getItem('token')) {
              authServiceClient
                .checkUserPower(userPowerRequest, {
                  token: localStorage.getItem('token')
                })
                .then(powerRes => {
                  if (powerRes.getStatus()) {
                    if (type) {
                      this.$router.push({
                        path: url,
                        query: { type }
                      })
                      return
                    }
                    this.$router.push(url)
                    return
                  }
                  this.$message({
                    center: true,
                    type: 'error',
                    showClose: true,
                    duration: 3000,
                    message: powerRes.getMessage()
                  })
                  this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
                })
                .catch(() => {
                  this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
                })
            }
          })
          .catch(() => {
            this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
          })
        return
      }
      if (type) {
        this.$router.push({
          path: url,
          query: { type }
        })
        return
      }
      this.$router.push(url)
    },
    showIframe(url) {
      const index = this.$authPopup.open(url)
      if (index === null) {
        this.$router.push(url)
      }
      return index
    }
  }
}
</script>

<style lang="less" scoped src="../assets/v2/css/trans/common.less"/>
<style lang="less" scoped>
.scholar-page {
  min-width: 1200px;
  min-height: 100vh;
  background: var(--bg-page);
  font-family: Microsoft YaHei;
}

.scholar-shell {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: var(--bg-page);
}

.scholar-shell::before {
  content: "";
  position: absolute;
  top: 70px;
  left: 0;
  right: 0;
  height: 1160px;
  pointer-events: none;
  background: url(../assets/chat/bg.png) top center / 1920px 1148px no-repeat;
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 64%, rgba(0, 0, 0, 0.55) 82%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 0%, #000 64%, rgba(0, 0, 0, 0.55) 82%, transparent 100%);
}

.scholar-trans-header {
  position: relative;
  z-index: 1;
  min-width: 1200px;
  height: 70px;
  margin-top: -1px;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  background-size: 1929px 79px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.18);
}

.scholar-trans-header .box_2 {
  width: 1200px;
  height: 59px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.scholar-trans-header .image_1 {
  width: 104px;
  height: 28px;
  cursor: pointer;
}

.scholar-trans-header .text_1 {
  width: 182px;
  height: 28px;
  margin: 0 0 0 119px;
  display: flex;
  justify-content: space-between;
  overflow-wrap: break-word;
  color: var(--foreground-secondary);
  font-size: 16px;
  font-family: MicrosoftYaHei;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  white-space: nowrap;
}

.scholar-trans-header .text_1 span {
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--foreground-secondary);
  font-size: 16px;
  font-family: MicrosoftYaHei;
  font-weight: 400;
  cursor: pointer;
}

.scholar-trans-header .thumbnail_1 {
  width: 14px;
  height: 14px;
  margin: 6px 0 0 20px;
  cursor: pointer;
}

.scholar-trans-header .text_2 {
  max-width: 80px;
  height: 24px;
  margin: 7px 0 0 8px;
  overflow: hidden;
  overflow-wrap: break-word;
  color: var(--muted-foreground);
  font-size: 16px;
  font-family: MicrosoftYaHei;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.scholar-trans-header .thumbnail_4 {
  width: 10px;
  height: 5px;
}

.scholar-main {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0 auto;
  padding: 42px 24px 48px;
  box-sizing: border-box;
}

.scholar-main.is-search-idle {
  display: flex;
  align-items: center;
  flex-direction: column;
  min-height: calc(100vh - 154px);
  padding-top: clamp(96px, 16vh, 176px);
}

.scholar-search-intro {
  margin-bottom: 24px;
  color: var(--foreground);
  text-align: center;
}

.scholar-search-intro > i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 24px;
}

.scholar-search-intro h1 {
  margin: 0;
  color: var(--foreground);
  font-size: 24px;
  font-weight: 600;
  line-height: 34px;
}

.scholar-search-intro p {
  margin: 8px 0 0;
  color: var(--muted-fg-light);
  font-size: 14px;
  line-height: 22px;
}

.scholar-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.scholar-hero p {
  margin: 0;
  color: var(--muted-foreground);
  font-size: 15px;
  line-height: 24px;
}

.scholar-hero-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.scholar-rag-import-btn {
  height: 32px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: var(--primary);
  box-shadow: none;
}

.scholar-rag-import-btn:hover,
.scholar-rag-import-btn:focus {
  border: 0;
  background: transparent;
  color: var(--primary-dark);
}

.scholar-saved-btn {
  height: 36px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: var(--primary);
  box-shadow: none;
}

.scholar-saved-btn:hover,
.scholar-saved-btn:focus {
  border: 0;
  background: transparent;
  color: var(--primary-dark);
}

.scholar-search-card {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 18px;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 5px 7px 0 rgba(123, 136, 146, 0.17);
}

.scholar-search-card.is-initial {
  width: 760px;
  max-width: 100%;
  padding: 14px;
  flex-wrap: nowrap;
  box-sizing: border-box;
}

.scholar-search-card.is-initial .scholar-search-input {
  min-width: 0;
}

.scholar-search-card.is-initial .scholar-search-btn {
  flex: 0 0 104px;
}

.scholar-search-input {
  flex: 1;
  min-width: 280px;
}

.scholar-search-mode {
  width: 124px;
  flex: 0 0 124px;
}

/deep/ .scholar-search-mode .el-input__inner {
  height: 40px;
  border-color: var(--border);
  border-radius: 6px;
  color: var(--foreground);
  font-weight: 700;
  line-height: 40px;
}

/deep/ .scholar-search-mode .el-input__inner:hover,
/deep/ .scholar-search-mode .el-input.is-focus .el-input__inner {
  border-color: var(--primary);
}

/deep/ .scholar-search-input .el-input__inner:hover,
/deep/ .scholar-search-input .el-input.is-focus .el-input__inner,
/deep/ .scholar-search-input .el-input__inner:focus {
  border-color: var(--primary);
}

/deep/ .scholar-search-mode .el-input__suffix {
  display: flex;
  align-items: center;
}

.scholar-search-mode-option {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 42px;
  line-height: 1.4;
}

.scholar-search-mode-option strong {
  color: var(--foreground);
  font-size: 13px;
}

.scholar-search-mode-option span {
  color: var(--muted-fg-light);
  font-size: 12px;
}

.scholar-search-mode-option em {
  color: var(--primary);
  font-size: 11px;
  font-style: normal;
}

.scholar-search-btn {
  width: 96px;
  height: 40px;
  border-color: var(--primary);
  background: var(--primary);
}

.scholar-filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 96px;
  height: 40px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--muted-foreground);
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.scholar-filter-btn:hover,
.scholar-filter-btn:focus {
  border-color: var(--primary);
  color: var(--primary);
  background: #ffffff;
}

.scholar-advanced-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 112px;
  height: 40px;
  padding: 0;
  border: 1px solid var(--primary);
  border-radius: 4px;
  background: var(--primary);
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.scholar-advanced-btn:hover,
.scholar-advanced-btn:focus,
.scholar-advanced-btn.active {
  border-color: var(--primary-dark);
  background: var(--primary-dark);
  color: #ffffff;
}

.scholar-bulk-import-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 104px;
  height: 40px;
  padding: 0;
  border: 1px solid var(--primary);
  border-radius: 4px;
  background: var(--primary);
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.scholar-bulk-import-btn:hover,
.scholar-bulk-import-btn:focus {
  border-color: var(--primary-dark);
  background: var(--primary-dark);
  color: #ffffff;
}

.scholar-advanced-bar {
  margin-top: 14px;
  padding: 16px 18px;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 5px 7px rgba(123, 136, 146, 0.12);
  box-sizing: border-box;
}

.scholar-advanced-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.scholar-advanced-line {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 10px;
}

.scholar-advanced-line.is-full {
  grid-template-columns: 1fr;
}

.scholar-advanced-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.scholar-advanced-grid /deep/ .el-autocomplete,
.scholar-advanced-grid /deep/ .el-select,
.scholar-advanced-line /deep/ .el-select {
  width: 100%;
}

.scholar-advanced-checks {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px 22px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--primary-alpha-05);
}

.scholar-advanced-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.scholar-advanced-bar /deep/ .el-input__inner {
  height: 36px;
  border-color: var(--border);
  border-radius: 4px;
}

.scholar-advanced-bar /deep/ .el-input__inner:focus {
  border-color: var(--primary);
}

.scholar-history-btn,
.scholar-saved-search-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 112px;
  height: 40px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--muted-foreground);
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.scholar-history-btn:hover,
.scholar-history-btn:focus,
.scholar-saved-search-btn:hover,
.scholar-saved-search-btn:focus {
  border-color: var(--primary);
  color: var(--primary);
}

.scholar-active-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  margin-top: 10px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.88);
  border-radius: 6px;
  box-shadow: 0 4px 7px 0 rgba(123, 136, 146, 0.1);
  box-sizing: border-box;
}

.scholar-active-filter-label {
  flex: 0 0 auto;
  color: var(--muted-foreground);
  font-size: 13px;
  line-height: 24px;
}

.scholar-active-filter-tag {
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  max-width: min(360px, calc(100% - 118px));
  box-sizing: border-box;
  height: 28px;
  padding: 0 10px 0 12px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 13px;
  line-height: 26px;
  cursor: pointer;
  outline: none;
}

.scholar-active-filter-tag.is-entity-filter {
  border-color: var(--primary-alpha-05);
  background: linear-gradient(180deg, var(--primary-alpha-05) 0%, var(--primary-alpha-05) 100%);
  color: var(--primary);
}

.scholar-active-filter-tag span {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-active-filter-tag i {
  flex: 0 0 auto;
  margin-left: 6px;
  font-size: 12px;
}

.scholar-active-filter-tag:hover,
.scholar-active-filter-tag:focus {
  border-color: var(--primary);
  background: var(--primary-alpha-05);
}

.scholar-active-filter-clear {
  flex: 0 0 auto;
  margin-left: auto;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: #ffffff;
  color: var(--muted-foreground);
  font-size: 13px;
  line-height: 26px;
  cursor: pointer;
  outline: none;
}

.scholar-active-filter-clear:hover,
.scholar-active-filter-clear:focus {
  border-color: var(--primary);
  color: var(--primary);
  background: var(--primary-alpha-08);
}

.scholar-filter-bar {
  margin-top: 14px;
  padding: 16px 18px;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 5px 7px 0 rgba(123, 136, 146, 0.12);
  box-sizing: border-box;
}

.scholar-filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
}

.scholar-filter-row + .scholar-filter-row {
  margin-top: 12px;
}

.scholar-filter-row-main {
  align-items: center;
}

.scholar-filter-checks {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 22px;
  width: 100%;
  min-height: 32px;
}

.scholar-filter-row-controls {
  align-items: stretch;
}

.scholar-filter-controls {
  display: grid;
  flex: 0 0 100%;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  width: 100%;
  min-width: 0;
}

.scholar-filter-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.scholar-filter-label {
  min-width: 34px;
  color: var(--muted-foreground);
  font-size: 14px;
  text-align: right;
}

.scholar-filter-select {
  width: 100%;
}

.scholar-quality-select {
  min-width: 148px;
}

.scholar-year-input {
  width: 118px;
}

.scholar-citation-input {
  width: 118px;
}

.scholar-filter-row-entities {
  align-items: flex-start;
}

.scholar-filter-entity-grid {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(3, minmax(150px, 1fr));
  gap: 10px;
  min-width: 0;
}

.scholar-filter-autocomplete {
  width: 100%;
}

.scholar-year-line {
  color: var(--muted-fg-light);
  font-size: 14px;
}

.scholar-filter-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.scholar-filter-apply {
  width: 64px;
  height: 32px;
  padding: 0;
  border-color: var(--primary);
  background: var(--primary);
  border-radius: 4px;
  color: #ffffff;
  line-height: 30px;
}

.scholar-filter-apply:hover,
.scholar-filter-apply:focus {
  border-color: var(--primary-dark);
  background: var(--primary-dark);
  color: #ffffff;
}

.scholar-filter-reset {
  width: 64px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--muted-foreground);
  line-height: 30px;
}

.scholar-filter-reset:hover,
.scholar-filter-reset:focus {
  border-color: var(--primary);
  background: #ffffff;
  color: var(--primary);
}

.scholar-content {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-top: 24px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.scholar-sidebar {
  width: 250px;
  flex: 0 0 250px;
}

.scholar-side-card,
.scholar-results {
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 5px 7px 0 rgba(123, 136, 146, 0.17);
}

.scholar-side-card {
  margin-bottom: 16px;
  padding: 18px;
}

.scholar-card-title {
  color: var(--foreground-secondary);
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
}

.scholar-facet-card {
  padding: 0;
  overflow: hidden;
}

.scholar-facet-head {
  display: flex;
  align-items: center;
  width: 100%;
  height: 52px;
  padding: 0 16px;
  border: none;
  background: #ffffff;
  color: var(--foreground-secondary);
  cursor: pointer;
  outline: none;
}

.scholar-facet-head span {
  flex: 1;
  text-align: left;
  font-size: 15px;
  font-weight: bold;
}

.scholar-facet-head em {
  margin-right: 8px;
  color: var(--muted-foreground);
  font-size: 12px;
  font-style: normal;
  font-weight: normal;
}

.scholar-facet-head i {
  color: var(--muted-foreground);
  font-size: 14px;
}

.scholar-facet-head:hover span,
.scholar-facet-head:hover i {
  color: var(--primary);
}

.scholar-facet-body {
  padding: 0 16px 14px;
}

.scholar-facet-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scholar-facet-search {
  margin-bottom: 2px;
}

.scholar-facet-search /deep/ .el-input__inner {
  height: 28px;
  border-color: var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--foreground);
  font-size: 12px;
}

.scholar-facet-search /deep/ .el-input__prefix {
  display: flex;
  align-items: center;
}

.scholar-facet-item {
  display: grid;
  grid-template-columns: minmax(76px, 1fr) 70px auto;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--foreground-secondary);
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  outline: none;
}

.scholar-facet-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-facet-track {
  height: 6px;
  overflow: hidden;
  border-radius: 6px;
  background: var(--bg-subtle);
}

.scholar-facet-progress {
  display: block;
  height: 100%;
  border-radius: 6px;
  background: var(--primary);
}

.scholar-facet-item em {
  color: var(--muted-foreground);
  font-size: 12px;
  font-style: normal;
}

.scholar-facet-item:hover,
.scholar-facet-item.active {
  color: var(--primary);
}

.scholar-facet-item.active .scholar-facet-progress {
  background: #f5a623;
}

.scholar-stat-value {
  margin-top: 16px;
  color: var(--foreground);
  font-size: 30px;
  font-weight: bold;
  line-height: 38px;
}

.scholar-stat-label {
  margin-top: 2px;
  color: var(--muted-foreground);
  font-size: 13px;
}

.scholar-stat-split {
  height: 1px;
  margin: 16px 0;
  background: var(--bg-subtle);
}

.scholar-stat-row,
.scholar-oa-item,
.scholar-year-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.scholar-stat-row {
  margin-top: 12px;
}

.scholar-stat-row span {
  color: var(--foreground);
  font-size: 22px;
  font-weight: bold;
}

.scholar-stat-row em,
.scholar-oa-item em,
.scholar-year-item em {
  color: var(--muted-foreground);
  font-size: 13px;
  font-style: normal;
}

.scholar-facet-export-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--bg-subtle);
}

.scholar-facet-export-actions /deep/ .el-button {
  height: 28px;
  margin-left: 0;
  padding: 0 8px;
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
}

.scholar-facet-export-actions /deep/ .el-button.is-disabled {
  color: var(--muted-fg-light);
}

.scholar-year-list,
.scholar-oa-list {
  margin-top: 12px;
}

.scholar-year-item {
  gap: 10px;
  margin-bottom: 10px;
  color: var(--foreground-secondary);
  font-size: 13px;
}

.scholar-year-track {
  flex: 1;
  height: 6px;
  overflow: hidden;
  border-radius: 6px;
  background: var(--bg-subtle);
}

.scholar-year-progress {
  height: 100%;
  border-radius: 6px;
  background: var(--primary);
}

.scholar-oa-item {
  height: 30px;
  color: var(--foreground-secondary);
  font-size: 14px;
}

.scholar-facet-more {
  display: block;
  width: 100%;
  height: 28px;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--primary);
  font-size: 13px;
  line-height: 28px;
  text-align: left;
  cursor: pointer;
  outline: none;
}

.scholar-facet-more:hover,
.scholar-facet-more:focus {
  color: var(--primary-dark);
}

.scholar-empty-mini {
  margin-top: 12px;
  color: var(--muted-fg-light);
  font-size: 13px;
}

.scholar-results {
  flex: 1 1 0;
  width: auto;
  max-width: none;
  min-width: 0;
  min-height: 640px;
  padding: 20px;
  overflow: hidden;
  box-sizing: border-box;
}

.scholar-results-head {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 14px;
  min-width: 0;
  color: var(--muted-foreground);
  font-size: 14px;
}

.scholar-results-meta {
  min-width: 0;
}

.scholar-results-head strong {
  color: var(--foreground-secondary);
  font-size: 18px;
}

.scholar-sort-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.scholar-sort-chips button {
  height: 24px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: #ffffff;
  color: var(--muted-foreground);
  font-size: 12px;
  line-height: 22px;
  cursor: pointer;
  outline: none;
}

.scholar-sort-chips button:hover,
.scholar-sort-chips button.active {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-weight: 700;
}

.scholar-screening-stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.scholar-screening-stats button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 8px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: #ffffff;
  color: var(--muted-foreground);
  font-size: 12px;
  line-height: 22px;
  cursor: pointer;
}

.scholar-screening-stats button:hover,
.scholar-screening-stats button.active {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-screening-stats em {
  font-style: normal;
  font-weight: 700;
}

.scholar-batch-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
  color: var(--muted-fg-light);
  font-size: 13px;
}

.scholar-batch-tools button {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--primary);
  font-size: 13px;
  cursor: pointer;
}

.scholar-batch-tools button:disabled {
  color: var(--muted-fg-light);
  cursor: not-allowed;
}

.scholar-batch-more,
.scholar-batch-screening,
.scholar-batch-rag {
  line-height: 18px;
}

.scholar-batch-more.active,
.scholar-batch-screening.active {
  color: var(--primary-dark);
  font-weight: 700;
}

.scholar-batch-more i,
.scholar-batch-screening i {
  margin-left: 2px;
  font-size: 12px;
}

.scholar-batch-panel {
  width: 100%;
  min-width: 0;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  box-sizing: border-box;
}

.scholar-batch-panel strong {
  display: block;
  margin: 0 0 8px;
  color: var(--foreground);
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

.scholar-batch-panel > div {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.scholar-batch-panel button {
  min-width: 0;
  min-height: 30px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--foreground-secondary);
  font-size: 12px;
  line-height: 17px;
  text-align: left;
  cursor: pointer;
}

.scholar-batch-panel button:hover,
.scholar-batch-panel button:focus {
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-results-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px 10px;
  width: 100%;
  min-width: 0;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-05);
  box-sizing: border-box;
}

.scholar-results-actions > .el-dropdown,
.scholar-results-actions > .el-button,
.scholar-results-actions > .el-select,
.scholar-results-actions > .scholar-top-pager,
.scholar-results-actions > .scholar-page-filter,
.scholar-results-actions > .scholar-page-filter-summary {
  flex: 0 0 auto;
}

.scholar-results-more-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  width: 100%;
  margin-top: 2px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.02);
  box-sizing: border-box;
}

.scholar-results-more-group {
  min-width: 0;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
}

.scholar-results-more-group h4 {
  margin: 0 0 8px;
  color: var(--foreground);
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

.scholar-results-more-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.scholar-export-panel {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
}

.scholar-export-panel .scholar-results-more-actions {
  grid-template-columns: 1fr;
}

.scholar-results-more-actions button {
  min-width: 0;
  min-height: 30px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-05);
  color: var(--foreground-secondary);
  font-size: 12px;
  line-height: 17px;
  text-align: left;
  cursor: pointer;
}

.scholar-results-more-actions button:hover,
.scholar-results-more-actions button:focus {
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-results-more-actions button:disabled {
  color: var(--muted-fg-light);
  cursor: not-allowed;
  background: var(--bg-subtle);
}

@media (max-width: 1180px) {
  .scholar-export-panel {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .scholar-export-panel {
    grid-template-columns: 1fr;
  }
}

.scholar-screening-view-count {
  display: inline-flex;
  align-items: center;
  height: 22px;
  margin-left: 8px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
}

.scholar-screening-view-select {
  width: 116px;
}

.scholar-page-filter {
  width: 160px;
}

.scholar-page-filter /deep/ .el-input__inner {
  height: 30px;
  border-color: var(--border);
  border-radius: 4px;
  color: var(--foreground);
  font-size: 13px;
}

.scholar-page-filter-summary {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 8px;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.scholar-top-pager {
  display: inline-flex;
  align-items: center;
  height: 30px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
}

.scholar-top-pager button {
  width: 30px;
  height: 28px;
  border: 0;
  background: transparent;
  color: var(--primary);
  font-size: 14px;
  line-height: 28px;
  cursor: pointer;
}

.scholar-top-pager button:hover:not(:disabled) {
  background: var(--primary-alpha-08);
}

.scholar-top-pager button:disabled {
  color: var(--muted-fg-light);
  cursor: not-allowed;
}

.scholar-top-pager span {
  min-width: 58px;
  padding: 0 8px;
  border-left: 1px solid var(--border);
  border-right: 1px solid var(--border);
  color: var(--muted-foreground);
  font-size: 12px;
  line-height: 28px;
  text-align: center;
  white-space: nowrap;
}

.scholar-density-select {
  width: 100px;
}

.scholar-hidden-file {
  display: none;
}

.scholar-link-btn {
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--muted-foreground);
  font-size: 13px;
}

.scholar-link-btn:hover,
.scholar-link-btn:focus {
  border-color: var(--primary);
  color: var(--primary-dark);
  background: var(--primary-alpha-08);
}

.scholar-save-search-btn {
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 13px;
}

.scholar-save-search-btn:hover,
.scholar-save-search-btn:focus {
  border-color: var(--primary);
  color: var(--primary-dark);
  background: var(--primary-alpha-08);
}

.scholar-more-btn {
  height: 30px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--muted-foreground);
  font-size: 13px;
}

.scholar-more-btn:hover,
.scholar-more-btn:focus {
  border-color: var(--primary);
  color: var(--primary-dark);
  background: var(--primary-alpha-08);
}

.scholar-more-btn.active {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary-dark);
}

.scholar-export-btn {
  height: 30px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 13px;
}

.scholar-export-btn:hover,
.scholar-export-btn:focus {
  border-color: var(--primary);
  color: var(--primary-dark);
  background: var(--primary-alpha-08);
}

.scholar-export-btn.active {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary-dark);
}

.scholar-error {
  margin-bottom: 14px;
}

.scholar-list {
  min-height: 320px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.scholar-empty-state {
  height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--muted-fg-light);
  text-align: center;
}

.scholar-empty-state i {
  margin-bottom: 16px;
  color: var(--primary);
  font-size: 42px;
}

.scholar-empty-state div {
  color: var(--foreground-secondary);
  font-size: 18px;
  font-weight: bold;
}

.scholar-empty-state p {
  margin: 10px 0 0;
  color: var(--muted-foreground);
  font-size: 14px;
}

.scholar-empty-actions {
  margin-top: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.scholar-empty-actions .el-button {
  min-width: 92px;
  height: 30px;
  padding: 0 12px;
  border-radius: 4px;
}

.scholar-work-card {
  padding: 16px 16px 14px;
  margin-bottom: 14px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #ffffff;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.scholar-work-card:hover {
  border-color: rgba(5, 150, 105, 0.45);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
}

.scholar-work-card.selected {
  border-color: rgba(5, 150, 105, 0.65);
  background: var(--primary-alpha-08);
}

.scholar-results.is-compact .scholar-work-card {
  padding: 12px 12px 10px;
  margin-bottom: 10px;
}

.scholar-work-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.scholar-work-select {
  flex: 0 0 auto;
  margin-top: 3px;
}

.scholar-work-title {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: var(--foreground);
  font-size: 17px;
  font-weight: bold;
  line-height: 25px;
  cursor: pointer;
}

.scholar-work-title:hover {
  color: var(--primary);
}

.scholar-highlight-text mark,
.scholar-abstract mark {
  padding: 0 2px;
  border-radius: 2px;
  background: #fff3bf;
  color: inherit;
  font-weight: 700;
}

.scholar-results.is-compact .scholar-work-title {
  font-size: 15px;
  line-height: 22px;
}

.scholar-oa-badge {
  display: inline-block;
  height: 22px;
  margin-right: 6px;
  padding: 0 8px;
  border-radius: 11px;
  background: #e6f7ed;
  color: #17a35b;
  font-size: 12px;
  line-height: 22px;
  vertical-align: 1px;
}

.scholar-fulltext-badge {
  display: inline-block;
  height: 22px;
  margin-right: 6px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--bg-subtle);
  color: var(--muted-foreground);
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
  vertical-align: 1px;
}

.scholar-risk-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  margin-right: 6px;
  padding: 0 8px;
  border-radius: 11px;
  background: #fff7ed;
  color: #b45309;
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
  vertical-align: 1px;
}

.scholar-risk-badge.danger {
  background: #fff1f2;
  color: #be123c;
}

.scholar-indexed-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  margin-right: 6px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--bg-subtle);
  color: var(--muted-foreground);
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
  vertical-align: 1px;
}

.scholar-screening-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  margin-right: 6px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
  vertical-align: 1px;
}

.scholar-screening-badge.is-include {
  background: #ecfdf3;
  color: #078649;
}

.scholar-screening-badge.is-exclude {
  background: #fff1f2;
  color: #be123c;
}

.scholar-screening-badge.is-maybe {
  background: #fff7ed;
  color: #c46a00;
}

.scholar-screening-badge.is-pending {
  background: var(--primary-alpha-05);
  color: var(--muted-fg-light);
}

.scholar-work-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.scholar-cite-badge {
  height: 24px;
  padding: 0 9px;
  border-radius: 12px;
  background: #fff7eb;
  color: #cc8800;
  font-size: 12px;
  line-height: 24px;
  white-space: nowrap;
}

.scholar-impact-badge {
  height: 24px;
  padding: 0 9px;
  border-radius: 12px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
  line-height: 24px;
  white-space: nowrap;
}

.scholar-pill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  min-width: auto;
  padding: 0 9px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--foreground-secondary);
  font-size: 12px;
  line-height: 22px;
  white-space: nowrap;
  cursor: pointer;
}

.scholar-pill-btn.primary {
  border-color: var(--primary);
  background: var(--primary);
  color: #ffffff;
}

.scholar-pill-btn:disabled {
  opacity: 0.68;
  cursor: not-allowed;
}

.scholar-pdf-download-btn {
  font-family: inherit;
}

.scholar-meta,
.scholar-institution,
.scholar-doi {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 9px;
  max-width: 100%;
  min-width: 0;
  color: var(--foreground-secondary);
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.scholar-results.is-compact .scholar-meta,
.scholar-results.is-compact .scholar-institution,
.scholar-results.is-compact .scholar-doi {
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
}

.scholar-meta span,
.scholar-institution,
.scholar-doi {
  min-width: 0;
  max-width: 100%;
}

.scholar-meta i,
.scholar-institution i {
  margin-right: 4px;
  color: var(--muted-foreground);
}

.scholar-inline-link {
  display: inline;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: inherit;
  cursor: pointer;
}

.scholar-inline-link + .scholar-inline-link::before {
  content: '、';
  color: var(--muted-foreground);
}

.scholar-inline-link:hover {
  color: var(--primary);
}

.scholar-meta em,
.scholar-institution em {
  color: var(--muted-foreground);
  font-style: normal;
}

.scholar-doi {
  color: var(--primary-dark);
  word-break: break-all;
}

.scholar-doi-label {
  color: var(--muted-fg-light);
  font-weight: 700;
}

.scholar-doi-copy {
  display: inline;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  line-height: inherit;
  text-align: left;
  word-break: break-all;
  cursor: pointer;
}

.scholar-doi-copy:hover,
.scholar-doi-copy:focus {
  color: var(--primary-dark);
  text-decoration: underline;
  outline: none;
}

.scholar-provider-id {
  color: var(--muted-foreground);
  cursor: pointer;
}

.scholar-oa-evidence {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.scholar-oa-evidence span {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--primary-alpha-05);
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
}

.scholar-results.is-compact .scholar-oa-evidence {
  margin-top: 6px;
}

.scholar-abstract {
  margin: 10px 0 0;
  max-width: 100%;
  color: var(--foreground-secondary);
  font-size: 14px;
  line-height: 24px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.scholar-results.is-compact .scholar-abstract {
  margin-top: 7px;
  font-size: 13px;
  line-height: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.scholar-results.is-compact .scholar-abstract.expanded {
  display: block;
  overflow: visible;
}

.scholar-abstract.expanded {
  color: var(--foreground-secondary);
}

.scholar-inline-detail {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 6px;
  background: var(--primary-alpha-05);
  border: 1px solid var(--border);
}

.scholar-inline-row {
  display: flex;
  gap: 12px;
  color: var(--foreground-secondary);
  font-size: 13px;
  line-height: 22px;
}

.scholar-inline-row + .scholar-inline-row {
  margin-top: 6px;
}

.scholar-inline-row strong {
  flex: 0 0 40px;
  color: var(--foreground-secondary);
}

.scholar-inline-row span {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.scholar-work-footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
  min-width: 0;
}

.scholar-results.is-compact .scholar-work-footer {
  margin-top: 8px;
}

.scholar-tags {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  max-width: calc(100% - 280px);
}

.scholar-tags button {
  height: 24px;
  padding: 0 9px;
  border: 0;
  border-radius: 12px;
  background: var(--bg-subtle);
  color: var(--muted-foreground);
  font-size: 12px;
  line-height: 24px;
  cursor: pointer;
}

.scholar-tags button:hover {
  background: rgba(5, 150, 105, 0.18);
}

.scholar-results.is-compact .scholar-tags {
  gap: 6px;
}

.scholar-results.is-compact .scholar-tags button {
  height: 22px;
  padding: 0 8px;
  font-size: 12px;
  line-height: 22px;
}

.scholar-card-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-left: auto;
  max-width: 460px;
}

.scholar-action-link {
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--foreground);
  font-size: 14px;
  line-height: 24px;
  cursor: pointer;
}

.scholar-action-link:hover {
  color: var(--primary-dark);
}

.scholar-action-link.active {
  color: var(--primary-dark);
  font-weight: 700;
}

.scholar-card-action-panel {
  flex: 0 0 100%;
  width: 100%;
  margin-top: 2px;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  box-sizing: border-box;
}

.scholar-card-action-panel > strong {
  display: block;
  margin-bottom: 8px;
  color: var(--foreground);
  font-size: 13px;
  line-height: 18px;
}

.scholar-card-action-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.scholar-card-action-grid button {
  min-width: 0;
  min-height: 30px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--foreground-secondary);
  font-size: 12px;
  line-height: 17px;
  text-align: left;
  cursor: pointer;
}

.scholar-card-action-grid button:hover,
.scholar-card-action-grid button:focus {
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-card-action-grid button:disabled {
  color: var(--muted-fg-light);
  cursor: not-allowed;
  background: var(--bg-subtle);
}

.scholar-card-action-grid.is-expand {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.scholar-card-action-grid.is-expand button span,
.scholar-card-action-grid.is-expand button em {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-card-action-grid.is-expand button span {
  color: inherit;
  font-weight: 700;
}

.scholar-card-action-grid.is-expand button em {
  margin-top: 3px;
  color: var(--muted-fg-light);
  font-size: 12px;
  font-style: normal;
}

.scholar-expand-option,
.scholar-expand-option strong,
.scholar-expand-option em {
  display: block;
  min-width: 0;
  max-width: 100%;
}

.scholar-expand-option strong,
.scholar-expand-option em {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-expand-option strong {
  color: var(--foreground);
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}

.scholar-expand-option em {
  margin-top: 3px;
  color: var(--muted-fg-light);
  font-size: 12px;
  font-style: normal;
  line-height: 18px;
}

.scholar-pagination {
  display: flex;
  justify-content: center;
  padding: 18px 0 4px;
  text-align: center;
}

/deep/ .scholar-pagination .el-pagination {
  white-space: normal;
}

.scholar-footer {
  position: relative;
  z-index: 1;
  min-width: 1200px;
  height: 130px;
  align-items: center;
  justify-content: center;
  background-color: var(--primary);
  color: rgba(233, 238, 255, 1);
  font-size: 14px;
  line-height: 26px;
  text-align: center;
}

.scholar-footer a,
.scholar-footer-link {
  display: inline;
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(233, 238, 255, 1);
  font: inherit;
  cursor: pointer;
}

.scholar-detail h2 {
  margin: 0 0 12px;
  color: var(--foreground);
  font-size: 22px;
  line-height: 32px;
}

.scholar-detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.scholar-detail-meta span {
  padding: 4px 10px;
  border-radius: 4px;
  background: var(--bg-page);
  color: var(--foreground-secondary);
  font-size: 13px;
}

.scholar-detail p {
  color: var(--foreground-secondary);
  font-size: 14px;
  line-height: 25px;
}

.scholar-detail-row {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  color: var(--foreground-secondary);
  font-size: 14px;
  line-height: 24px;
}

.scholar-detail-row strong {
  width: 48px;
  color: var(--foreground-secondary);
}

.scholar-detail-actions {
  margin-top: 22px;
  text-align: right;
}

.scholar-saved-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 0;
  border-bottom: 1px solid var(--bg-subtle);
}

.scholar-saved-filter {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px auto auto auto;
  gap: 10px;
  margin-bottom: 10px;
}

.scholar-copy-saved-btn {
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-copy-saved-btn.active,
.scholar-saved-filter .scholar-export-btn.active {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-saved-panel {
  grid-column: 1 / -1;
  min-width: 0;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  box-sizing: border-box;
}

.scholar-saved-panel strong {
  display: block;
  margin: 0 0 8px;
  color: var(--foreground);
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

.scholar-saved-panel > div {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.scholar-saved-panel button {
  min-width: 0;
  min-height: 30px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--foreground-secondary);
  font-size: 12px;
  line-height: 17px;
  text-align: left;
  cursor: pointer;
}

.scholar-saved-panel button:hover,
.scholar-saved-panel button:focus {
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-saved-item strong {
  color: var(--foreground-secondary);
  font-size: 15px;
  line-height: 24px;
  cursor: pointer;
}

.scholar-saved-item strong:hover {
  color: var(--primary);
}

.scholar-saved-item p {
  margin: 6px 0 0;
  color: var(--muted-foreground);
  font-size: 13px;
}

.scholar-saved-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.scholar-saved-tags span {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--primary-alpha-05);
  color: var(--primary);
  font-size: 12px;
  line-height: 22px;
}

.scholar-saved-note {
  max-width: 540px;
  color: var(--foreground-secondary) !important;
  line-height: 20px;
}

.scholar-saved-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 220px;
}

.scholar-saved-search-list {
  min-height: 160px;
}

.scholar-saved-search-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 0;
  border-bottom: 1px solid var(--bg-subtle);
}

.scholar-saved-search-item strong {
  color: var(--foreground);
  font-size: 15px;
  line-height: 24px;
}

.scholar-saved-search-item p {
  margin: 6px 0 0;
  color: var(--muted-foreground);
  font-size: 13px;
  line-height: 20px;
}

.scholar-saved-search-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.scholar-saved-search-tags span {
  display: inline-flex;
  align-items: center;
  max-width: 220px;
  height: 22px;
  padding: 0 8px;
  overflow: hidden;
  border-radius: 11px;
  background: var(--primary-alpha-05);
  color: var(--primary);
  font-size: 12px;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-save-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.scholar-save-work-title {
  margin-bottom: 4px;
  color: var(--foreground);
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.scholar-save-form label {
  color: var(--muted-foreground);
  font-size: 13px;
  font-weight: 700;
}

.scholar-save-tags-input {
  width: 100%;
}

.scholar-history-list {
  min-height: 180px;
}

.scholar-history-item {
  width: 100%;
  min-height: 52px;
  padding: 10px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  border-bottom: 1px solid var(--bg-subtle);
  text-align: left;
}

.scholar-history-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.scholar-history-main:hover span,
.scholar-history-main:focus span {
  color: var(--primary);
}

.scholar-history-main span {
  flex: 1;
  min-width: 0;
  color: var(--foreground);
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  overflow-wrap: anywhere;
}

.scholar-history-main em {
  flex: 0 0 auto;
  color: var(--muted-fg-light);
  font-size: 12px;
  font-style: normal;
  line-height: 22px;
  white-space: nowrap;
}

.scholar-history-copy {
  flex: 0 0 auto;
  padding: 0;
}

.scholar-citation-title {
  margin-bottom: 14px;
  color: var(--foreground);
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.scholar-citation-tabs {
  margin-bottom: 14px;
}

.scholar-citation-text {
  min-height: 126px;
  max-height: 260px;
  margin: 0;
  padding: 14px 16px;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--primary-alpha-05);
  color: var(--foreground);
  font-family: Consolas, Menlo, Monaco, monospace;
  font-size: 13px;
  line-height: 22px;
  white-space: pre-wrap;
  word-break: break-word;
}

.scholar-citation-note {
  margin: 10px 0 0;
  color: var(--muted-fg-light);
  font-size: 13px;
  line-height: 20px;
}

.scholar-citation-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.scholar-rag-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.scholar-rag-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.scholar-rag-summary > div {
  padding: 14px 16px;
  border: 1px solid var(--primary-alpha-05);
  border-radius: 6px;
  background: var(--primary-alpha-05);
}

.scholar-rag-summary strong {
  display: block;
  color: var(--foreground);
  font-size: 24px;
  line-height: 30px;
}

.scholar-rag-summary span {
  color: var(--muted-fg-light);
  font-size: 13px;
}

.scholar-rag-note {
  margin: 0;
  color: var(--muted-foreground);
  font-size: 13px;
  line-height: 22px;
}

.scholar-rag-form {
  padding: 14px 16px 4px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #ffffff;
}

.scholar-rag-select {
  width: 100%;
}

.scholar-rag-select em {
  float: right;
  color: var(--muted-fg-light);
  font-style: normal;
  font-size: 12px;
}

.scholar-rag-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 6px;
  background: var(--primary-alpha-08);
}

.scholar-rag-progress span {
  color: var(--foreground-secondary);
  font-size: 13px;
}

.scholar-rag-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow: auto;
}

.scholar-rag-preview > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 32px;
  padding: 7px 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
}

.scholar-rag-preview span {
  min-width: 0;
  color: var(--foreground-secondary);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-rag-preview em {
  flex-shrink: 0;
  padding: 2px 6px;
  border-radius: 10px;
  background: var(--primary-alpha-05);
  color: var(--primary);
  font-size: 12px;
  font-style: normal;
}

.scholar-rag-preview p {
  margin: 2px 0 0;
  color: var(--muted-fg-light);
  font-size: 12px;
}

.scholar-bulk-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.scholar-bulk-note {
  margin: 0;
  color: var(--muted-foreground);
  font-size: 14px;
  line-height: 22px;
}

.scholar-bulk-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 2px;
}

.scholar-bulk-summary span {
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  line-height: 24px;
}

.scholar-compare-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.scholar-compare-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.scholar-compare-summary span {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 13px;
  background: var(--primary-alpha-05);
  color: var(--primary);
  font-size: 13px;
}

.scholar-compare-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.scholar-compare-actions .el-button {
  margin-left: 0;
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-compare-title {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--foreground);
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  text-align: left;
  cursor: pointer;
}

.scholar-compare-title:hover,
.scholar-compare-title:focus {
  color: var(--primary);
}

.scholar-compare-sub {
  margin-top: 4px;
  color: var(--muted-fg-light);
  font-size: 12px;
  line-height: 18px;
}

.scholar-compare-yes {
  color: #0a9a57;
  font-weight: 700;
}

.scholar-compare-no {
  color: var(--muted-fg-light);
}

.scholar-compare-evidence {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.scholar-compare-evidence span {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--primary-alpha-05);
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
}

.scholar-compare-doi {
  color: var(--muted-foreground);
  font-size: 12px;
  word-break: break-all;
}

/deep/ .scholar-compare-table th {
  background: var(--primary-alpha-08);
  color: var(--muted-foreground);
  font-weight: 700;
}

/deep/ .scholar-search-input .el-input__inner {
  height: 40px;
  border-radius: 6px;
}

/deep/ .scholar-search-input .el-input__prefix {
  display: flex;
  align-items: center;
  height: 100%;
}

/deep/ .scholar-search-input .el-input__prefix i {
  display: flex;
  align-items: center;
  height: 100%;
  line-height: 1;
}

/deep/ .scholar-suggest-popper li {
  height: auto;
  padding: 8px 14px;
  line-height: 20px;
}

.scholar-suggest-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: var(--foreground);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-suggest-title span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scholar-suggest-title em {
  flex: 0 0 auto;
  color: var(--primary);
  font-size: 12px;
  font-style: normal;
}

.scholar-suggest-hint {
  margin-top: 2px;
  color: var(--muted-fg-light);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/deep/ .el-button--primary {
  background: var(--primary);
  border-color: var(--primary);
}

/deep/ .el-button--text {
  color: var(--primary);
}

/deep/ .el-dialog {
  border-radius: 6px;
}

.scholar-mobile-facet-toggle {
  display: none;
}

@media (max-width: 768px) {
  .scholar-page,
  .scholar-shell,
  .scholar-main,
  .scholar-content {
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  .scholar-page {
    min-height: 100vh;
    min-height: 100dvh;
    overflow-x: hidden;
  }

  .scholar-shell {
    overflow-x: hidden;
    overflow-y: visible;
  }

  .scholar-shell::before {
    top: 56px;
    background-size: auto 1000px;
  }

  .scholar-main {
    margin: 0;
    padding: 18px 10px 32px;
  }

  .scholar-main.is-search-idle {
    min-height: calc(100dvh - 120px);
    padding: 72px 16px 40px;
  }

  .scholar-search-intro {
    margin-bottom: 18px;
  }

  .scholar-search-intro h1 {
    font-size: 21px;
    line-height: 30px;
  }

  .scholar-hero {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 12px;
  }

  .scholar-hero > div:first-child,
  .scholar-hero-actions {
    width: 100%;
    min-width: 0;
  }

  .scholar-hero p {
    font-size: 13px;
    line-height: 21px;
  }

  .scholar-hero-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .scholar-rag-import-btn,
  .scholar-saved-btn {
    width: 100%;
    min-height: 40px;
    height: auto;
    margin-left: 0;
  }

  .scholar-search-card {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    padding: 12px;
  }

  .scholar-search-card.is-initial {
    width: 100%;
    padding: 12px;
  }

  .scholar-search-mode {
    grid-column-start: 1;
    grid-column-end: -1;
    width: 100%;
    min-width: 0;
    flex: none;
  }

  .scholar-search-input {
    grid-column-start: 1;
    grid-column-end: 4;
    width: 100%;
    min-width: 0;
    flex: none;
  }

  .scholar-search-btn {
    grid-column-start: 4;
    grid-column-end: 5;
    width: 100%;
    min-width: 0;
    height: 44px;
    margin-left: 0;
  }

  .scholar-filter-btn,
  .scholar-advanced-btn,
  .scholar-bulk-import-btn,
  .scholar-history-btn {
    grid-column: span 2;
    width: 100%;
    min-width: 0;
    min-height: 42px;
    height: auto;
  }

  .scholar-saved-search-btn {
    grid-column-start: 1;
    grid-column-end: -1;
    width: 100%;
    min-width: 0;
    min-height: 42px;
    height: auto;
  }

  .scholar-advanced-bar,
  .scholar-filter-bar {
    padding: 12px;
  }

  .scholar-advanced-line {
    grid-template-columns: minmax(0, 1fr);
  }

  .scholar-advanced-grid,
  .scholar-filter-controls,
  .scholar-filter-entity-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .scholar-advanced-bar /deep/ .el-input__inner,
  .scholar-filter-bar /deep/ .el-input__inner,
  .scholar-filter-bar /deep/ .el-select .el-input__inner {
    height: 42px;
    line-height: 42px;
  }

  .scholar-advanced-checks,
  .scholar-filter-checks {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: 10px;
  }

  .scholar-advanced-checks /deep/ .el-checkbox,
  .scholar-filter-checks /deep/ .el-checkbox {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    margin-right: 0;
    white-space: normal;
  }

  .scholar-advanced-checks /deep/ .el-checkbox__label,
  .scholar-filter-checks /deep/ .el-checkbox__label {
    min-width: 0;
    padding-left: 6px;
    line-height: 20px;
    white-space: normal;
  }

  .scholar-advanced-footer,
  .scholar-filter-actions {
    display: flex;
    width: 100%;
    margin-left: 0;
    gap: 8px;
  }

  .scholar-advanced-footer /deep/ .el-button,
  .scholar-filter-actions /deep/ .el-button {
    flex: 1 1 0;
    min-width: 0;
    min-height: 42px;
    width: auto;
    height: auto;
    margin-left: 0;
  }

  .scholar-filter-range {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) auto minmax(0, 1fr);
    width: 100%;
    gap: 6px;
  }

  .scholar-year-input,
  .scholar-citation-input {
    width: 100%;
    min-width: 0;
  }

  .scholar-active-filters {
    flex-wrap: nowrap;
    gap: 6px;
    padding: 8px 10px;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .scholar-active-filters::-webkit-scrollbar { display: none; }

  .scholar-active-filter-tag {
    flex: 0 0 auto;
    max-width: 260px;
    min-height: 36px;
    height: auto;
    border-radius: 18px;
    line-height: 34px;
  }

  .scholar-active-filter-clear {
    flex: 0 0 auto;
    min-height: 36px;
    height: auto;
    margin-left: 0;
    border-radius: 18px;
    line-height: 34px;
  }

  .scholar-content {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-top: 16px;
  }

  .scholar-mobile-facet-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 44px;
    padding: 0 14px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: #ffffff;
    color: var(--foreground-secondary);
    font-size: 14px;
    font-weight: 700;
    box-shadow: 0 4px 12px rgba(59, 92, 116, .08);
  }

  .scholar-mobile-facet-toggle span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  .scholar-mobile-facet-toggle span i { color: var(--primary); }
  .scholar-mobile-facet-toggle em {
    margin-left: auto;
    color: var(--muted-fg-light);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
  }

  .scholar-mobile-facet-toggle.active {
    border-color: var(--primary);
    background: var(--primary-alpha-08);
    color: var(--primary);
  }

  .scholar-sidebar {
    display: none;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    flex: 0 0 auto;
  }

  .scholar-sidebar.is-mobile-open {
    display: block;
    max-height: 680px;
    max-height: ~"min(72dvh, 680px)";
    padding: 2px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
  }

  .scholar-side-card {
    margin-bottom: 12px;
    padding: 14px;
  }

  .scholar-facet-card { padding: 0; }
  .scholar-facet-head {
    height: auto;
    min-height: 44px;
    padding: 8px 14px;
  }

  .scholar-facet-item,
  .scholar-facet-more {
    min-height: 40px;
    height: auto;
  }

  .scholar-results {
    flex: 0 0 auto;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 0;
    padding: 12px;
  }

  .scholar-results-head strong {
    font-size: 17px;
    line-height: 24px;
  }

  .scholar-sort-chips,
  .scholar-screening-stats {
    flex-wrap: nowrap;
    max-width: 100%;
    padding-bottom: 2px;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .scholar-sort-chips::-webkit-scrollbar,
  .scholar-screening-stats::-webkit-scrollbar { display: none; }

  .scholar-sort-chips button,
  .scholar-screening-stats button {
    flex: 0 0 auto;
    min-height: 34px;
    height: auto;
    border-radius: 17px;
    line-height: 32px;
  }

  .scholar-batch-panel > div,
  .scholar-card-action-grid,
  .scholar-card-action-grid.is-expand {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .scholar-batch-panel button,
  .scholar-card-action-grid button,
  .scholar-results-more-actions button {
    min-height: 40px;
  }

  .scholar-results-actions {
    gap: 8px;
    padding: 8px;
  }

  .scholar-results-actions > .scholar-page-filter {
    flex: 1 1 100%;
    width: 100%;
  }

  .scholar-results-actions > .scholar-top-pager {
    flex: 0 0 auto;
  }

  .scholar-results-actions > .el-select {
    flex: 1 1 calc(50% - 4px);
    width: calc(50% - 4px);
    min-width: 120px;
  }

  .scholar-results-actions > .el-button,
  .scholar-results-actions > .el-dropdown {
    flex: 1 1 120px;
    min-width: 0;
    margin-left: 0;
  }

  .scholar-results-actions > .el-button {
    min-height: 40px;
  }

  .scholar-results-more-panel,
  .scholar-export-panel {
    grid-template-columns: minmax(0, 1fr);
    padding: 8px;
  }

  .scholar-work-card,
  .scholar-results.is-compact .scholar-work-card {
    padding: 12px;
    margin-bottom: 10px;
  }

  .scholar-work-top {
    flex-wrap: wrap;
    gap: 8px;
  }

  .scholar-work-title {
    font-size: 16px;
    line-height: 24px;
  }

  .scholar-work-actions {
    display: flex;
    flex: 1 1 100%;
    flex-wrap: wrap;
    min-width: 0;
    margin-left: 30px;
    gap: 6px;
  }

  .scholar-pill-btn {
    min-height: 36px;
    height: auto;
    line-height: 34px;
  }

  .scholar-work-footer { gap: 10px; }
  .scholar-tags {
    flex: 1 1 100%;
    max-width: 100%;
    gap: 6px;
  }

  .scholar-tags button {
    min-height: 32px;
    height: auto;
    line-height: 30px;
  }

  .scholar-card-actions {
    flex: 1 1 100%;
    justify-content: flex-start;
    width: 100%;
    max-width: none;
    margin-left: 0;
    gap: 8px 12px;
  }

  .scholar-action-link {
    min-height: 36px;
    height: auto;
    padding: 0 4px;
    line-height: 36px;
  }

  .scholar-empty-state {
    height: 240px;
    padding: 0 10px;
  }

  .scholar-pagination {
    max-width: 100%;
    overflow: hidden;
  }

  /deep/ .scholar-pagination .el-pagination__total,
  /deep/ .scholar-pagination .el-pagination__sizes,
  /deep/ .scholar-pagination .el-pagination__jump {
    display: none;
  }

  /deep/ .scholar-pagination .el-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
  }

  /deep/ .scholar-pagination .el-pager {
    display: flex;
    min-width: 0;
  }

  /deep/ .scholar-pagination .el-pager li {
    min-width: 28px;
    margin: 0 2px;
  }

  .scholar-footer { min-width: 0; }

  /deep/ .el-dialog {
    width: calc(100vw - 24px) !important;
    max-width: 720px;
    margin: 12px auto 0 !important;
  }

  /deep/ .el-dialog__body {
    max-height: calc(100dvh - 142px);
    padding: 16px;
    overflow-x: auto;
    overflow-y: auto;
  }
}

@media (max-width: 480px) {
  .scholar-main { padding-right: 8px; padding-left: 8px; }

  .scholar-advanced-grid,
  .scholar-filter-controls,
  .scholar-filter-entity-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .scholar-results-actions > .el-select {
    flex-basis: 100%;
    width: 100%;
  }

  .scholar-batch-panel > div,
  .scholar-card-action-grid,
  .scholar-card-action-grid.is-expand {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>

<style lang="less">
.scholar-suggest-popper li {
  height: auto;
  padding: 8px 14px;
  line-height: 20px;
}

.scholar-research-dropdown-menu {
  min-width: 180px;
  max-width: calc(100vw - 32px);
  max-height: min(560px, calc(100vh - 120px));
  overflow-y: auto;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(31, 45, 61, 0.14);
  box-sizing: border-box;
}

.scholar-results-more-menu {
  display: none !important;
}

.scholar-export-menu {
  display: none !important;
}

body.scholar-search-page-active > .el-dropdown-menu.scholar-results-more-menu,
body.scholar-search-page-active > .el-dropdown-menu.scholar-export-menu,
body.scholar-search-page-active > ul[id^="dropdown-menu-"].scholar-results-more-menu,
body.scholar-search-page-active > ul[id^="dropdown-menu-"].scholar-export-menu {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
}

.scholar-card-screening-menu,
.scholar-card-expand-menu,
.scholar-batch-more-menu,
.scholar-batch-screening-menu,
.scholar-compare-export-menu,
.scholar-saved-export-menu,
.scholar-saved-organizer-menu {
  display: none !important;
}

.scholar-research-dropdown-menu .el-dropdown-menu__item {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 32px !important;
  height: auto !important;
  margin: 1px 0;
  padding: 7px 10px !important;
  border-radius: 4px;
  color: var(--foreground);
  font-size: 13px !important;
  font-weight: 500 !important;
  line-height: 18px !important;
  white-space: normal !important;
  overflow-wrap: anywhere;
}

.scholar-research-dropdown-menu .el-dropdown-menu__item:hover,
.scholar-research-dropdown-menu .el-dropdown-menu__item:focus {
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-research-dropdown-menu .el-dropdown-menu__item.is-disabled {
  color: var(--muted-fg-light);
}

.scholar-research-dropdown-menu .el-dropdown-menu__item.is-disabled.scholar-menu-section {
  min-height: 24px;
  margin: 7px 0 3px;
  padding: 8px 10px 4px !important;
  border-top: 1px solid var(--border);
  border-radius: 0;
  background: #ffffff;
  color: var(--muted-fg-light);
  font-size: 12px !important;
  font-weight: 700 !important;
  line-height: 16px !important;
  cursor: default;
}

.scholar-research-dropdown-menu .el-dropdown-menu__item.is-disabled.scholar-menu-section:first-child {
  margin-top: 0;
  padding-top: 4px;
  border-top: 0;
}

.scholar-research-dropdown-menu .el-dropdown-menu__item > span {
  min-width: 0;
  max-width: 100%;
}

.scholar-research-dropdown-menu .scholar-expand-option,
.scholar-research-dropdown-menu .scholar-expand-option strong,
.scholar-research-dropdown-menu .scholar-expand-option em {
  display: block;
  min-width: 0;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
}

.scholar-research-dropdown-menu .scholar-expand-option strong {
  color: var(--foreground);
  font-size: 13px;
  font-weight: 600;
  line-height: 19px;
}

.scholar-research-dropdown-menu .scholar-expand-option em {
  margin-top: 3px;
  color: var(--muted-fg-light);
  font-size: 12px;
  font-style: normal;
  line-height: 18px;
}
</style>
